/**
 * Step Handler
 * 
 * Handles step rendering, auto-advance, validation, and UI interactions
 */

/**
 * Split a single "owner name" into Shopify first/last (first space only). Aligned with Introbox backend.
 * @param {string} raw
 * @returns {{ firstName: string, lastName: string }}
 */
function splitOwnerNameForShopify(raw) {
  if (typeof raw !== 'string') {
    return { firstName: '', lastName: '' };
  }
  const trimmed = raw.replace(/^[\s\uFEFF\u00A0]+|[\s\uFEFF\u00A0]+$/g, '');
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }
  const spaceIdx = trimmed.indexOf(' ');
  if (spaceIdx === -1) {
    return { firstName: trimmed, lastName: '' };
  }
  return {
    firstName: trimmed.slice(0, spaceIdx).trim(),
    lastName: trimmed.slice(spaceIdx + 1).replace(/\s+/g, ' ').trim()
  };
}

class StepHandler {
  constructor(flowController) {
    this.flow = flowController;
    this.stepTemplates = {};
    this.currentStepElement = null;
    this.originalFooterButtons = null; // Store original button elements
  }

  /**
   * Initialize step handler
   */
  async init() {
    // Store original footer buttons for later restoration
    const footerButtons = document.querySelector('.footer-buttons');
    if (footerButtons) {
      const nextButton = footerButtons.querySelector('[data-next-button]');
      if (nextButton) {
        // Clone the original button to preserve its server-rendered translation
        this.originalFooterButtons = {
          next: nextButton.cloneNode(true)
        };
      }
    }
    
    // Load all step templates
    await this.loadStepTemplates();
    
    // Set up event listeners
    this.setupEventListeners();
  }

  /**
   * Load step templates from <template> tags rendered in wizard-1-flow.liquid
   */
  async loadStepTemplates() {
    const steps = [
      'name', 'spayed', 'age', 'breed', 'food', 'eating', 'weight', 'composition',
      'activity', 'allergies', 'issues', 'snacks', 'email', 'calculation',
      'flavors', 'plan', 'bulk', 'profile'
    ];

    steps.forEach((step) => {
      const templateId = `wizard-1-step-${step}`;
      const template = document.getElementById(templateId);
      if (template) {
        this.stepTemplates[step] = template.innerHTML;
      }
    });
  }

  /**
   * Render a step
   * @param {string} step - Step identifier
   * @param {Object} params - Route parameters (optional)
   * @returns {Promise<void>}
   */
  async renderStep(step, params = {}) {
    const contentInner = document.querySelector('[data-quiz-content] .content-inner');
    if (!contentInner) {
      console.error('Quiz content container not found');
      return;
    }

    // Get current step from state (needed for cleanup and other logic)
    const currentStep = this.flow.state.getCurrentStep();

    // Ensure plan-overview section is in slot on every render (handles page refresh on any step)
    const planEl = document.querySelector('[data-plan-overview-section], .data-plan-overview-section');
    const planSectionWrapper = planEl?.closest('.shopify-section');
    const planSlot = document.querySelector('[data-plan-overview-slot]');
    if (planSectionWrapper && planSlot && !planSlot.contains(planSectionWrapper)) {
      planSlot.appendChild(planSectionWrapper);
    }

    // Hide plan-overview Instant slot unless wizard-1 plan or wizard-2 plan_overview
    const planOverviewShopifySlotStep = step === 'plan' || step === 'plan_overview';
    if (!planOverviewShopifySlotStep) {
      if (planSlot) planSlot.style.display = 'none';
      const quizContainer = document.querySelector('[data-quiz-container]');
      if (quizContainer) quizContainer.classList.remove('plan-step-active');
    }

    // Always reset footer to clean state before rendering any step
    // This ensures no leftover elements from previous steps (especially important for browser back/forward)
    this.resetFooter(step);

    // Restore footer buttons if leaving profile step
    // Check both state and actual footer class to handle timing issues
    const footer = document.querySelector('.quiz-footer');
    if (footer && footer.classList.contains('profile-footer') && step !== 'profile') {
      this.restoreFooterButtons();
    } else if (currentStep === 'profile' && step !== 'profile') {
      this.restoreFooterButtons();
    }

    // Hide/show quiz UI for calculation step
    if (step === 'calculation') {
      this.hideQuizUI();
    } else {
      // Always show UI for non-calculation steps
      // This ensures UI is visible even if we navigate directly to a step
      this.showQuizUI();
      if (currentStep === 'calculation') {
        this.cleanupCalculationStep();
      }
    }

    try {
      // Check if we have pre-rendered content for flavors step (from calculation step)
      if (step === 'flavors') {
        const flavorsContainer = contentInner.querySelector('[data-flavors-prerender]');
        if (flavorsContainer && flavorsContainer.dataset.ready === 'true') {
          // Move all child nodes from pre-rendered container to contentInner
          // This preserves event listeners attached to the nodes
          while (flavorsContainer.firstChild) {
            contentInner.appendChild(flavorsContainer.firstChild);
          }
          flavorsContainer.remove();
          
          // Update current step element
          this.currentStepElement = contentInner;
          
          // Ensure footer is restored for flavors step
          if (footer && footer.classList.contains('profile-footer')) {
            this.restoreFooterButtons();
          }
          
          // Event listeners are already set up from pre-rendering
          // Just update button states and progress bar
          this.updateButtonStates(step);
          this.updateProgressBar(step);

          // Toggle email-step class on footer
          const footerEl = document.querySelector('.quiz-footer');
          if (footerEl) {
            footerEl.classList.toggle('email-step', step === 'email');
            if (step === 'email') {
              this.appendEmailStepDog(footerEl.querySelector('.footer-buttons'));
            }
          }

          // Skip the rest of the setup since it's already done
          return;
        }
      }

      const templateHtml = this.stepTemplates[step];
      if (!templateHtml) {
        throw new Error(`Step template not found: ${step}`);
      }

      contentInner.innerHTML = templateHtml;

      // Update current step element
      this.currentStepElement = contentInner;

      // Update dynamic text (gender pronouns, etc.) immediately to prevent flicker
      // This must run before async operations to ensure title is updated right away
      this.updateDynamicText(step);

      // Set up step-specific event listeners
      await this.setupStepListeners(step, params);

      // Restore field values from state
      this.restoreFieldValues(step);

      // Update button states
      this.updateButtonStates(step);

      // Update progress bar
      this.updateProgressBar(step);

      // Show/hide inline messages
      this.updateInlineMessages(step);

      // Toggle email-step class on footer for dog illustration (email step only)
      const footer = document.querySelector('.quiz-footer');
      if (footer) {
        footer.classList.toggle('email-step', step === 'email');
        if (step === 'email') {
          this.appendEmailStepDog(footer.querySelector('.footer-buttons'));
        }
      }
    } catch (error) {
      console.error(`Error rendering step ${step}:`, error);
      // Fallback: show error message
      contentInner.innerHTML = `<p>Error loading step. Please refresh the page.</p>`;
    }
  }

  /**
   * Set up global event listeners
   */
  setupEventListeners() {
    // Back button
    const backButtons = document.querySelectorAll('[data-back-button]');
    backButtons.forEach(button => {
      button.addEventListener('click', () => {
        // Clean up bulk step if leaving it
        const currentStep = this.flow.state.getCurrentStep();
        if (currentStep === 'bulk') {
          this.cleanupBulkStep();
        }
        // Clean up plan / plan_overview (Instant slot + footer)
        if (currentStep === 'plan' || currentStep === 'plan_overview') {
          this.cleanupPlanStep();
        }
        this.flow.navigatePrevious();
      });
    });

    // Next button
    const nextButton = document.querySelector('[data-next-button]');
    if (nextButton) {
      nextButton.addEventListener('click', (e) => {
        const currentStep = this.flow.state.getCurrentStep();
        
        // NOTE: petMetaobjectId clearing is now handled in handleInputChange()
        // when the user types/changes the name field. This ensures it's cleared
        // immediately when the name changes, not just when clicking continue.
        
        // Save weight when continue button is clicked on weight step
        if (currentStep === 'weight') {
          this.saveAllDogWeights();
        }
        
        // Don't navigate on email step - let setupCustomerCreation handle it
        if (currentStep === 'email') {
          // Email step has its own handler that will call navigateNext() after customer creation
          return;
        }
        if (currentStep === 'extras' && window.WizardPlan) {
          if (window.BzWizard && window.BzWizard.id() === 'w3') {
            const picked = document.querySelector('input[name="w3-extra"]:checked');
            this.flow.state.extrasOn.w3 = picked ? picked.value : '';
          }
          this._runWizardCartAndCheckout({ mode: 'checkout' });
          return;
        }
        if (currentStep === 'profile' && window.BzWizard && window.BzWizard.id() === 'w3' && window.WizardPlan) {
          this._runWizardCartAndCheckout({ mode: 'checkout' });
          return;
        }
        this.flow.navigateNext();
      });
    }

    // Cart button
    const cartButton = document.querySelector('[data-cart-button]');
    if (cartButton) {
      cartButton.addEventListener('click', () => {
        window.location.href = '/cart';
      });
    }
  }

  /**
   * Set up step-specific event listeners
   * @param {string} step - Step identifier
   * @param {Object} params - Route parameters (optional)
   */
  async setupStepListeners(step, params = {}) {
    if (window.WizardHousehold && typeof window.WizardHousehold.mountCopies === 'function') {
      window.WizardHousehold.mountCopies(this, step);
    }

    if (step === 'eating' && this.shouldShowEatingHelp() && this.eatingHelpWasOpen()) {
      this.showEatingHelp();
    }

    // Input fields with data-step attribute
    const inputs = this.currentStepElement.querySelectorAll('[data-step="' + step + '"]');
    inputs.forEach(input => {
      const field = input.getAttribute('data-field');
      const autoAdvance = input.getAttribute('data-auto-advance') === 'true';

      if (input.type === 'radio' || input.type === 'checkbox') {
        input.addEventListener('change', (e) => {
          const dogIndex = Number(input.getAttribute('data-dog-index') || 0);
          const dogs = this.flow.state.ensureDogs ? this.flow.state.ensureDogs() : [];
          const dog = dogs[dogIndex];
          const currentStateValue = dog ? dog[field] : this.flow.state.petData[field];
          const isRestoration = String(currentStateValue) === String(e.target.value) &&
                               (input.type === 'radio' ? e.target.checked : true);
          const wasComplete = this.flow.state.validateStep(step);

          this.handleInputChange(step, field, e.target.value, e.target.checked, input.type, e.target, e);

          if (autoAdvance && input.type === 'radio' && e.target.checked && !isRestoration) {
            this.maybeAutoAdvance(step, wasComplete);
          }
        });
      } else {
        // Skip auto-save for weight input - it will be saved on continue button click
        if (step === 'weight' && field === 'weight') {
          // Only validate, don't auto-save
          input.addEventListener('blur', (e) => {
            this.validateField(step, field, e.target.value);
          });
        } else {
          input.addEventListener('input', (e) => {
            this.handleInputChange(step, field, e.target.value, null, null, e.target);
          });

          input.addEventListener('blur', (e) => {
            this.validateField(step, field, e.target.value);
          });
        }
      }
    });

    // Special handlers
    if (step === 'name') {
      this.setupResumeLogic();
      this.setupNameStepLogic();
    }

    if (step === 'spayed' && window.WizardHousehold) {
      window.WizardHousehold.mountGender(this);
    }

    if (step === 'weight') {
      this.setupWeightControls();
    }

    if (step === 'breed') {
      this.setupBreedInput();
    }

    if (step === 'activity') {
    }

    if (step === 'allergies') {
      this.setupAllergiesValidation();
    }

    if (step === 'email') {
      this.setupEmailValidation();
      this.setupCustomerCreation();
    }

    if (step === 'calculation') {
      this.setupCalculationStep();
    }

    if (step === 'flavors') {
      this.setupFlavorsStep();
    }

    if (step === 'plan') {
      await this.setupPlanStep();
    }

    if (step === 'bulk') {
      await this.setupBulkStep();
    }

    if (step === 'profile') {
      this.setupProfileStep(params);
    }
  }

  /**
   * Handle input change
   * @param {string} step - Step identifier
   * @param {string} field - Field name
   * @param {*} value - Field value
   * @param {boolean} checked - For checkboxes/radio buttons
   * @param {string} inputType - Input type ('radio', 'checkbox', etc.)
   */
  handleInputChange(step, field, value, checked = null, inputType = null, el = null, event = null) {
    const dogIndexAttr = el && el.getAttribute ? el.getAttribute('data-dog-index') : null;
    const dogIndex = dogIndexAttr === null || dogIndexAttr === '' ? 0 : Number(dogIndexAttr);
    if ((field === 'currentFood' || field === 'healthIssues') && this.flow.state.toggleDogList) {
      if (value) this.flow.state.toggleDogList(dogIndex, field, value, checked);
      if (field === 'healthIssues' && value && event && event.isTrusted && window.bzToast) {
        const toastKey = 'issue:' + dogIndex + ':' + value;
        if (checked && el && window.bzToast.showStacked) {
          const message = this.decodeIssueToast(el.getAttribute('data-toast'));
          const tone = el.getAttribute('data-toast-tone') === 'warning' ? 'warning' : 'freshwater';
          if (message) window.bzToast.showStacked({ type: tone, message: message, key: toastKey });
        } else if (!checked && window.bzToast.dismissStacked) {
          window.bzToast.dismissStacked(toastKey);
        }
      }
      if (field === 'healthIssues' && el) this.syncIssueNotes(el.closest('[data-wizard-dog-fields]'));
      this.flow.storage.autoSave(this.flow.state.getState(), this.flow.state.customerId);
      this.updateButtonStates(step);
      this.updateInlineMessages(step);
      return;
    }
    if ((field === 'eating' || field === 'health') && this.flow.state.setDogField) {
      this.flow.state.setDogField(dogIndex, field, value);
      if (field === 'health' && el) {
        const fields = el.closest('[data-wizard-dog-fields]');
        const panel = fields ? fields.querySelector('[data-issues-panel]') : null;
        if (panel) panel.hidden = value !== 'some';
        if (value === 'none' && window.bzToast && window.bzToast.dismissStackedPrefix) {
          window.bzToast.dismissStackedPrefix('issue:' + dogIndex + ':');
        }
        this.syncIssueNotes(fields);
      }
      if (field === 'eating' && event && event.isTrusted) {
        const stepNow = step;
        setTimeout(() => {
          if (this.flow.state.getCurrentStep() !== stepNow) return;
          if (!this.flow.state.validateStep(stepNow)) return;
          this.flow.navigateNext();
        }, 300);
      }
      if (field === 'health' && value === 'none' && event && event.isTrusted) {
        const stepNow = step;
        setTimeout(() => {
          if (this.flow.state.getCurrentStep() !== stepNow) return;
          const dogs = this.flow.state.ensureDogs ? this.flow.state.ensureDogs() : [];
          if (!dogs.length || dogs.some((dog) => dog.health !== 'none')) return;
          this.flow.navigateNext();
        }, 300);
      }
      this.flow.storage.autoSave(this.flow.state.getState(), this.flow.state.customerId);
      this.updateButtonStates(step);
      this.updateInlineMessages(step);
      return;
    }
    if (this.flow.state.setDogField && dogIndex > 0) {
      if (inputType === 'checkbox' && field === 'allergies' && this.flow.state.toggleDogList) {
        this.flow.state.toggleDogList(dogIndex, field, value, checked);
      } else if (inputType === 'radio' || inputType !== 'checkbox') {
        const stored = field === 'spayed' ? (value === 'true' || value === true) : value;
        this.flow.state.setDogField(dogIndex, field, stored);
      }
      this.flow.storage.autoSave(this.flow.state.getState(), this.flow.state.customerId);
      this.updateButtonStates(step);
      this.updateInlineMessages(step);
      return;
    }

    if (inputType === 'radio') {
      // Radio buttons: use the value parameter (e.g., "male", "female", "true", "false")
      // For boolean fields like spayed, parse the string value
      if (field === 'spayed') {
        // Parse "true"/"false" strings to boolean
        const boolValue = value === 'true' || value === true;
        this.flow.updatePetData(field, boolValue);
      } else if (field === 'activity') {
        // For activity radio buttons: if workingDog is selected, set activity to 'veryActive'
        // Otherwise, update normally with the selected value
        if (value === 'workingDog') {
          this.flow.updatePetData('activity', 'veryActive');
        } else {
          this.flow.updatePetData(field, value);
        }
        // Clear any stored previous activity since user is making a new selection
        this.flow.state.previousActivity = null;
      } else {
        // For other radio fields (gender, ageGroup, composition, snacks), use the value as-is
        this.flow.updatePetData(field, value);
      }
    } else if (inputType === 'checkbox') {
      // For checkboxes
      if (field === 'allergies') {
        // Handle array of allergies
        const currentAllergies = this.flow.state.petData.allergies || [];
        if (checked) {
          if (!currentAllergies.includes(value)) {
            currentAllergies.push(value);
          }
        } else {
          const index = currentAllergies.indexOf(value);
          if (index > -1) {
            currentAllergies.splice(index, 1);
          }
        }
        const normalizedAllergies = this.normalizePetAllergies(currentAllergies);
        this.flow.updatePetData(field, normalizedAllergies);
        
        // Remove conflicting flavors from selectedFlavors when allergies are updated
        const allergicFlavorIds = this.getAllergicFlavorIds(normalizedAllergies);
        const selectedFlavors = this.flow.state.selectedFlavors || [];
        const conflictingFlavors = selectedFlavors.filter(flavorId => allergicFlavorIds.includes(flavorId));
        
        if (conflictingFlavors.length > 0) {
          // Remove conflicting flavors from selectedFlavors
          const updatedSelectedFlavors = selectedFlavors.filter(flavorId => !allergicFlavorIds.includes(flavorId));
          this.flow.state.selectedFlavors = updatedSelectedFlavors;
          
          // Uncheck conflicting flavor checkboxes if we're on the flavors step
          if (this.currentStepElement) {
            conflictingFlavors.forEach(flavorId => {
              const checkbox = this.currentStepElement.querySelector(`#flavor-${flavorId}`);
              if (checkbox) {
                checkbox.checked = false;
              }
            });
          }
        }
      } else {
        // Boolean checkboxes (breedUnknown, breedMixed, emailConsent, isWorkingDog)
        // Handle breed checkboxes specially - set breed value when checked, clear when unchecked
        if (field === 'breedUnknown') {
          this.flow.updatePetData('breedUnknown', checked);
          if (checked) {
            // Set breed to "dont_know" when checkbox is checked
            this.flow.updatePetData('breed', 'dont_know');
          } else {
            // Clear breed when checkbox is unchecked
            this.flow.updatePetData('breed', '');
          }
        } else if (field === 'breedMixed') {
          this.flow.updatePetData('breedMixed', checked);
          if (checked) {
            // Set breed to "mixed" when checkbox is checked
            this.flow.updatePetData('breed', 'mixed');
          } else {
            // Clear breed when checkbox is unchecked
            this.flow.updatePetData('breed', '');
          }
        } else {
          this.flow.updatePetData(field, checked);
        }
      }
    } else {
      // Text, number, email, select inputs
      
      // CRITICAL FIX: Clear petMetaobjectId when user changes pet name on name step
      // This ensures we create a new pet instead of trying to update an old one
      if (step === 'name' && field === 'name') {
        const dogIndex = Number(el?.getAttribute?.('data-dog-index') || 0);
        const dog = this.flow.state.ensureDogs?.()[dogIndex];
        if (!(dog && dog.petMetaobjectId)) {
          const previousName = this.flow.state.petData?.name || '';
          const newName =
            typeof window.normalizePetNameEdges === 'function'
              ? window.normalizePetNameEdges(value || '')
              : (value || '').trim();
          const isNewPet = !previousName || previousName.trim() === '';
          const isNameChanged = newName && previousName && newName.toLowerCase() !== previousName.toLowerCase();
          if (isNewPet || isNameChanged) {
            this.flow.state.petMetaobjectId = null;
          }
        }
      }
      
      // CRITICAL FIX: Clear customerId when user changes email on email step (for new pets)
      // This ensures we create/find the correct customer for the new email, not reuse an old customerId
      if (step === 'email' && field === 'ownerEmail') {
        const previousEmail = this.flow.state.petData?.ownerEmail || '';
        const newEmail = (value || '').trim().toLowerCase();
        const isNewEmail = !previousEmail || previousEmail.trim() === '';
        const isEmailChanged = newEmail && previousEmail && newEmail !== previousEmail.toLowerCase();
        
        // Clear customerId if:
        // 1. We're creating a new pet (petMetaobjectId is null), AND
        // 2. User is entering a new email OR changed the email
        if (!this.flow.state.petMetaobjectId && (isNewEmail || isEmailChanged)) {
          console.log('Email changed on email step for new pet - clearing old customerId');
          this.flow.state.customerId = null;
        }
      }
      
      this.flow.updatePetData(field, value);
    }

    // Auto-save to localStorage (and Shopify if customer exists)
    this.flow.storage.autoSave(this.flow.state.getState(), this.flow.state.customerId);

    // Update button states
    this.updateButtonStates(step);

    // Update inline messages
    this.updateInlineMessages(step);
    
    // Update missing data warning for email step
    if (step === 'email') {
      this.setupMissingDataWarning();
    }
  }

  /**
   * Restore field values from state after rendering step
   * @param {string} step - Step identifier
   */
  restoreFieldValues(step) {
    if (!this.currentStepElement) return;

    const petData = this.flow.state.getPetData();

    // Flag to skip breed auto-advance when restoring values (e.g. after navigating back)
    if (step === 'breed') this._restoringBreedStep = true;
    
    // Special handling for composition step: preselect "normal" if empty/null
    if (step === 'composition' && (!petData.composition || petData.composition === '')) {
      petData.composition = 'normal';
      this.flow.updatePetData('composition', 'normal');
    }
    
    const inputs = this.currentStepElement.querySelectorAll('[data-step="' + step + '"]');

    inputs.forEach(input => {
      const field = input.getAttribute('data-field');
      if (!field) return;

      const dogIndex = Number(input.getAttribute('data-dog-index') || 0);
      const dogs = this.flow.state.ensureDogs ? this.flow.state.ensureDogs() : [];
      const dog = dogs[dogIndex];
      const fieldValue = dog && field in dog ? dog[field] : petData[field];

      // Handle different input types
      if (input.type === 'radio') {
        // Radio buttons: check if value matches
        if (String(input.value) === String(fieldValue)) {
          input.checked = true;
          // Trigger change event to update UI (radio icons, etc.)
          // The change listener will detect this is a restoration by comparing
          // the value with current state before the event fires
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (input.type === 'checkbox') {
        // Checkboxes: handle arrays or booleans
        if (field === 'currentFood' || field === 'healthIssues') {
          const list = Array.isArray(fieldValue) ? fieldValue : [];
          input.checked = list.includes(input.value);
        } else if (field === 'allergies') {
          const allergies = this.normalizePetAllergies(fieldValue || []);
          const isNoneCheckbox = input.hasAttribute('data-allergy-none');
          
          if (isNoneCheckbox) {
            // "None" checkbox: checked if allergies array is empty
            input.checked = allergies.length === 0;
          } else {
            // Other allergy checkboxes: checked if value is in array
            input.checked = allergies.includes(input.value);
          }
        } else if (field === 'breedUnknown' || field === 'breedMixed') {
          // Boolean checkboxes
          input.checked = fieldValue === true;
        } else if (field === 'emailConsent') {
          // Email consent checkbox
          input.checked = fieldValue !== false; // Default to true if not set
          
        } else {
          // Generic checkbox
          input.checked = fieldValue === true || fieldValue === input.value;
        }
        // Trigger change event to update UI (checkbox icons, etc.)
        if (input.checked) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (input.type === 'text' || input.type === 'email') {
        if (field === 'breed' && fieldValue) {
          input.value = (window.WizardBreedEntry && window.WizardBreedEntry.displayBreed)
            ? window.WizardBreedEntry.displayBreed(fieldValue)
            : String(fieldValue);
        } else if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
          input.value = String(fieldValue);
        }
      } else if (input.type === 'number') {
        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
          input.value = String(fieldValue);
        }
        if (field === 'weight') input.dispatchEvent(new Event('input', { bubbles: true }));
      } else if (input.tagName === 'SELECT') {
        // Select dropdowns
        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
          input.value = String(fieldValue);
        }
      }
    });

    if (step === 'breed') this._restoringBreedStep = false;

    if (step === 'issues') {
      this.currentStepElement.querySelectorAll('[data-wizard-dog-fields]').forEach((fields) => {
        const selected = fields.querySelector('input[data-field="health"]:checked');
        const panel = fields.querySelector('[data-issues-panel]');
        if (panel) panel.hidden = !selected || selected.value !== 'some';
        this.syncIssueNotes(fields);
      });
    }

    // Special handling for email step (has ownerName field)
    if (step === 'email') {
      const ownerNameInput = this.currentStepElement.querySelector('[name="ownerName"]');
      if (ownerNameInput && petData.ownerName) {
        ownerNameInput.value = petData.ownerName;
      }
    }
  }

  /**
   * Validate field
   * @param {string} step - Step identifier
   * @param {string} field - Field name
   * @param {*} value - Field value
   */
  validateField(step, field, value) {
    // Email format validation only shown on Continue click, not on blur - see setupEmailValidation/setupCustomerCreation
    if (step === 'email' && field === 'ownerEmail') {
      return;
    }
  }

  /**
   * Set up resume logic (Step 1)
   * Shows toast notification if existing pets are found
   */
  async setupResumeLogic() {
    // Check for existing pets (from localStorage or Shopify)
    const savedPets = await this.getExistingPets();

    // Only show resume toast if:
    // 1. A Pet Metaobject exists in Shopify (plan was created), OR
    // 2. The saved state indicates meaningful progress (beyond name step)
    const shouldShowToast = savedPets.some(pet => {
      // Check if pet has a Shopify Metaobject (plan was created)
      if (pet.source === 'shopify' && pet.metaobjectId) {
        return true;
      }
      
      // Check if saved state indicates progress worth resuming
      if (pet.source === 'localStorage') {
        const savedState = this.flow.storage.loadFromLocalStorage();
        if (savedState) {
          // Show if user reached any step beyond 'name' (e.g. email, plan, bulk)
          const stepsWithProgress = ['spayed', 'age', 'breed', 'food', 'eating', 'weight', 'composition',
            'activity', 'allergies', 'issues', 'snacks', 'email', 'calculation',
            'flavors', 'plan', 'bulk', 'profile'];
          return stepsWithProgress.includes(savedState.currentStep);
        }
      }
      
      return false;
    });

    if (shouldShowToast && savedPets.length > 0) {
      this.showResumeToast(savedPets);
    }
  }

  /**
   * Get existing pets from localStorage or Shopify
   * @returns {Promise<Array<Object>>} Array of saved pets
   */
  async getExistingPets() {
    const pets = [];

    // Always check for logged-in customer (not just if customerId in state)
    // This ensures we check even if customer was just detected
    let customerId = this.flow.state.customerId;
    
    // If no customerId in state, try to detect logged-in customer
    if (!customerId) {
      try {
        await this.flow.detectLoggedInCustomer();
        customerId = this.flow.state.customerId;
      } catch (error) {
        // Customer not logged in - continue with localStorage check
      }
    }

    // Check Shopify if customer exists
    if (customerId) {
      try {
        const shopifyPets = await this.flow.shopifyAPI.getAllPetMetaobjects(customerId);
        
        shopifyPets.forEach(pet => {
          if (pet.petName) {
            // Use resume_step from metaobject (single source of truth), fallback to derived step
            let step = 'name';
            if (pet.fields) {
              const resumeStepField = pet.fields.find(f => f.key === 'resume_step' && f.value);
              if (resumeStepField && resumeStepField.value) {
                step = resumeStepField.value;
              } else {
                const hasCalculations = pet.fields.some(f => f.key === 'dailyfoodg' && f.value);
                step = hasCalculations ? 'plan' : 'email';
              }
            }

            pets.push({
              name: pet.petName,
              step: step,
              source: 'shopify',
              metaobjectId: pet.id
            });
          }
        });
      } catch (error) {
        console.error('Error fetching Shopify pets:', error);
      }
    }

    // Check localStorage (only if no Shopify pets found, to avoid duplicates)
    if (pets.length === 0) {
      const savedState = this.flow.storage.loadFromLocalStorage();
      if (savedState && savedState.petData && savedState.petData.name) {
        pets.push({
          name: savedState.petData.name,
          step: savedState.currentStep || 'name',
          source: 'localStorage'
        });
      }
    }

    return pets;
  }

  /**
   * Show resume toast notification
   * @param {Array<Object>} pets - Array of saved pets
   */
  showResumeToast(pets) {
    if (!window.bzToast || !pets || !pets.length) return;

    const escapeHtml = (str) =>
      String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const L = window.bzToast.locale();
    const pack = (window.bzToastLocales && (window.bzToastLocales[L] || window.bzToastLocales.nl)) || {};
    let html;

    if (pets.length === 1) {
      const pet = pets[0];
      html =
        '<p>' +
        escapeHtml(pack.resume_one_prefix) +
        '<strong>' +
        escapeHtml(pet.name) +
        '</strong>. ' +
        '<button type="button" class="bz-toast__link" data-resume-pet="' +
        escapeHtml(pet.name) +
        '">' +
        escapeHtml(pack.resume_one_link) +
        '</button></p>';
    } else {
      const petNames = pets.map((p) => p.name).join(', ');
      html =
        '<p>' +
        escapeHtml(pack.resume_multi_prefix) +
        '<strong>' +
        escapeHtml(petNames) +
        '</strong>. ' +
        '<button type="button" class="bz-toast__link" data-resume-pets>' +
        escapeHtml(pack.resume_multi_link) +
        '</button></p>';
    }

    const self = this;
    window.bzToast.show({
      type: 'info',
      html,
      onMount: function (toast, dismiss) {
        const resumeButton = toast.querySelector('[data-resume-pet]');
        if (resumeButton) {
          resumeButton.addEventListener('click', function () {
            const petName = resumeButton.getAttribute('data-resume-pet');
            const petInfo = pets.find((p) => p.name === petName);
            if (self.flow.analytics && typeof self.flow.analytics.trackResume === 'function') {
              self.flow.analytics.trackResume(petName, petInfo?.source || 'unknown');
            }
            dismiss();
            self.resumePet(petName, petInfo);
          });
        }
        const resumePetsButton = toast.querySelector('[data-resume-pets]');
        if (resumePetsButton) {
          resumePetsButton.addEventListener('click', function () {
            dismiss();
            self.showPetSelection(pets);
          });
        }
      },
    });
  }

  /**
   * Resume pet flow
   * @param {string} petName - Pet name
   * @param {Object} petInfo - Pet information
   */
  async resumePet(petName, petInfo) {
    try {
      let targetStep = petInfo.step;

      // Load pet data - use metaobject.resumeStep as source of truth when loading from Shopify
      if (petInfo.source === 'shopify' && petInfo.metaobjectId) {
        const metaobject = await this.flow.shopifyAPI.getPetMetaobjectById(petInfo.metaobjectId);
        if (metaobject) {
          this.loadPetDataFromMetaobject(metaobject);
          if (metaobject.resumeStep) {
            targetStep = metaobject.resumeStep;
          }
        }
      } else {
        // Load from localStorage - use savedState.currentStep
        const savedState = this.flow.storage.loadFromLocalStorage();
        if (savedState) {
          this.flow.state.loadState(savedState);
          if (savedState.currentStep) {
            targetStep = savedState.currentStep;
          }
        }
      }

      // Normalize plan-related steps (flavors, plan, bulk -> plan for initial navigation)
      if (targetStep === 'plan' || ['flavors', 'plan', 'bulk'].includes(targetStep)) {
        targetStep = 'plan';
      }

      // Remove toast before navigating (prevents duplicate toast on re-render)
      const existingToast = document.querySelector('.resume-toast');
      if (existingToast) {
        existingToast.remove();
      }

      this.flow.router.navigate(targetStep);
    } catch (error) {
      console.error('Error resuming pet:', error);
      alert('Er is een fout opgetreden bij het laden van het profiel.');
    }
  }

  /**
   * Load pet data from Metaobject
   * @param {Object} metaobject - Pet Metaobject
   * @param {boolean} updateCurrentStep - Whether to update currentStep (default: true)
   */
  loadPetDataFromMetaobject(metaobject, updateCurrentStep = true) {
    const fields = metaobject.fields || [];
    const petData = {};
    const calculations = {};

    // Map fields to pet data (according to metafield mapping table)
    fields.forEach(field => {
      const key = field.key;
      const value = field.value;

      // Input metafields
      if (key === 'pet_name') petData.name = value;
      else if (key === 'gender') petData.gender = value;
      else if (key === 'breed') petData.breed = value;
      else if (key === 'weight') {
        // Weight is stored as JSON string: {"value":18,"unit":"kg"}
        try {
          const weightObj = typeof value === 'string' ? JSON.parse(value) : value;
          petData.weight = weightObj?.value ? parseFloat(weightObj.value) : parseFloat(value);
        } catch (e) {
          // Fallback: try to parse as number directly
          petData.weight = parseFloat(value);
        }
      }
      else if (key === 'composition') petData.composition = value;
      else if (key === 'age_group') petData.ageGroup = value;
      else if (key === 'spayed') petData.spayed = value === 'true' || value === true;
      else if (key === 'activity') petData.activity = value;
      else if (key === 'snacks') petData.snacks = value;
      else if (key === 'allergies') {
        let raw = [];
        if (value && typeof value === 'string') {
          if (value.trim().toLowerCase() === 'none') {
            raw = [];
          } else {
            raw = value.split(',').map(a => a.trim()).filter(a => a.length > 0);
          }
        } else if (Array.isArray(value)) {
          raw = value;
        }
        petData.allergies = this.normalizePetAllergies(raw);
      }
      else if (key === 'profile_photo') {
        petData.profilePhotoUrl = this.parseProfilePhotoFromField(field);
      }
      // Calculation metafields
      else if (key === 'dailykcal') calculations.dailyKcal = parseFloat(value);
      else if (key === 'dailyfoodg') calculations.dailyFoodG = parseFloat(value);
      else if (key === 'daysperbag') calculations.daysPerBag = parseFloat(value);
      else if (key === 'bagcount') calculations.bagCount = parseInt(value);
      else if (key === 'matchdays') calculations.matchDays = parseInt(value);
      else if (key === 'introbagcount') calculations.introBagCount = parseInt(value);
      else if (key === 'intromatchdays') calculations.introMatchDays = parseInt(value);
    });

    // Update state
    this.flow.state.updatePetDataBatch(petData);
    this.flow.state.updateCalculations(calculations);
    this.flow.state.petMetaobjectId = metaobject.id;
    if (metaobject.selectedFlavors && Array.isArray(metaobject.selectedFlavors)) {
      this.flow.state.selectedFlavors = metaobject.selectedFlavors;
    }
    
    // Extract customer ID from customer field if present
    const customerField = fields.find(f => f.key === 'customer');
    if (customerField && customerField.value) {
      this.flow.state.customerId = customerField.value;
      this.flow.state.sessionCustomerId = customerField.value;
    }

    // Use resume_step if available (cross-device resume), otherwise derive from data
    let targetStep = metaobject.resumeStep || 'name';
    if (!metaobject.resumeStep && petData.name && petData.gender) {
      targetStep = 'spayed';
      if (petData.spayed !== null) {
        targetStep = 'age';
        if (petData.ageGroup) {
          targetStep = 'breed';
          if (petData.breed || petData.breedUnknown || petData.breedMixed) {
            targetStep = 'weight';
            if (petData.weight) {
              targetStep = 'composition';
              if (petData.composition) {
                targetStep = 'activity';
                if (petData.activity) {
                  targetStep = 'allergies';
                  if (petData.allergies !== undefined) {
                    targetStep = 'snacks';
                    if (petData.snacks) {
                      targetStep = 'email';
                      if (calculations.dailyFoodG) {
                        targetStep = 'calculation';
                        if (this.flow.state.selectedFlavors && this.flow.state.selectedFlavors.length > 0) {
                          targetStep = 'flavors';
                          if (this.flow.state.selectedPlan) {
                            targetStep = 'plan';
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    if (updateCurrentStep) {
      this.flow.state.setCurrentStep(targetStep);
    }

    // Restore field values in UI after loading data
    // This will be called when the step is rendered
    // But we need to ensure it happens after renderStep completes
    // So we'll trigger it after a short delay to ensure DOM is ready
    if (updateCurrentStep) {
      setTimeout(() => {
        const currentStep = this.flow.state.getCurrentStep();
        if (this.currentStepElement) {
          this.restoreFieldValues(currentStep);
        }
      }, 100);
    }
  }

  /**
   * Show pet selection modal
   * @param {Array<Object>} pets - Array of pets
   */
  showPetSelection(pets) {
    // TODO: Implement pet selection modal
    // For now, just resume the first pet
    if (pets.length > 0) {
      this.resumePet(pets[0].name, pets[0]);
    }
  }

  /**
   * Calculate default weight based on breed and gender
   * @returns {number} Default weight in kg
   */
  calculateDefaultWeight() {
    const petData = this.flow.state.getPetData();
    const breed = petData.breed;
    const gender = petData.gender;

    // If weight already exists in state, don't override it
    if (petData.weight !== null && petData.weight !== undefined && petData.weight > 0) {
      return petData.weight;
    }

    // If no breed or gender, default to 18kg
    if (!breed || !gender || (gender !== 'male' && gender !== 'female')) {
      return 18;
    }

    // Look up breed in BREEDS_DATA
    const breedsData = window.Wizard1Breeds || [];
    const breedData = breedsData.find(b => b.value === breed);

    if (!breedData) {
      return 18;
    }

    // Get weight based on gender
    const weight = gender === 'male' 
      ? breedData.avg_weight_male 
      : breedData.avg_weight_female;

    // Fallback to 18 if weight is missing or invalid
    return (weight && weight > 0) ? weight : 18;
  }

  /**
   * Set up weight controls (+/- buttons)
   */
  setupWeightControls() {
    const groups = this.currentStepElement.querySelectorAll('.weight-controls');
    groups.forEach((group) => this.bindWeightGroup(group));
    const nextButton = document.querySelector('[data-next-button]');
    if (nextButton && this.flow.state.getCurrentStep() === 'weight') {
      nextButton.disabled = false;
    }
  }

  defaultWeightForDog(dog) {
    const breed = dog?.breed;
    const gender = dog?.gender;
    if (dog?.weight !== null && dog?.weight !== undefined && Number(dog.weight) > 0) {
      return Number(dog.weight);
    }
    if (!breed || (gender !== 'male' && gender !== 'female')) return 18;
    const breedsData = window.Wizard1Breeds || [];
    const breedData = breedsData.find((item) => item.value === breed);
    if (!breedData) return 18;
    const weight = gender === 'male' ? breedData.avg_weight_male : breedData.avg_weight_female;
    return weight && weight > 0 ? weight : 18;
  }

  saveAllDogWeights() {
    document.querySelectorAll('[data-weight-input]').forEach((weightInput) => {
      const value = parseInt(weightInput.value, 10);
      if (!value || value <= 0) return;
      const index = Number(weightInput.getAttribute('data-dog-index') || 0);
      const clamped = Math.max(1, Math.min(100, value));
      if (index > 0 && this.flow.state.setDogField) this.flow.state.setDogField(index, 'weight', clamped);
      else this.flow.updatePetData('weight', clamped);
      weightInput.value = clamped;
    });
    this.flow.storage.autoSave(this.flow.state.getState(), this.flow.state.customerId);
  }

  bindWeightGroup(group) {
    const decreaseButton = group.querySelector('[data-weight-decrease]');
    const increaseButton = group.querySelector('[data-weight-increase]');
    const weightInput = group.querySelector('[data-weight-input]');
    const unitElement = group.querySelector('.weight-unit');
    if (!weightInput) return;

    const index = Number(weightInput.getAttribute('data-dog-index') || 0);
    const dogs = this.flow.state.ensureDogs ? this.flow.state.ensureDogs() : [];
    const dog = dogs[index] || this.flow.state.getPetData();
    const existingWeight = dog?.weight;
    if (existingWeight === null || existingWeight === undefined || existingWeight === 0 || existingWeight === '') {
      weightInput.value = Math.round(this.defaultWeightForDog(dog));
    } else {
      weightInput.value = Math.round(Number(existingWeight));
    }

    const preventButtonFocus = (e) => e.preventDefault();
    if (decreaseButton) decreaseButton.addEventListener('mousedown', preventButtonFocus);
    if (increaseButton) increaseButton.addEventListener('mousedown', preventButtonFocus);

    const updateUnitPosition = () => {
      if (!unitElement || !weightInput) return;
      const tempSpan = document.createElement('span');
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.fontSize = window.getComputedStyle(weightInput).fontSize;
      tempSpan.style.fontFamily = window.getComputedStyle(weightInput).fontFamily;
      tempSpan.style.fontWeight = window.getComputedStyle(weightInput).fontWeight;
      tempSpan.textContent = weightInput.value || '18';
      document.body.appendChild(tempSpan);
      const numberWidth = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);
      const inputWidth = weightInput.offsetWidth;
      if (!inputWidth) return;
      const centerX = inputWidth / 2;
      unitElement.style.left = `${centerX + (numberWidth / 2) + 4}px`;
    };

    if (decreaseButton) {
      decreaseButton.addEventListener('click', () => {
        const currentValue = parseInt(weightInput.value, 10) || 18;
        weightInput.value = Math.max(1, currentValue - 1);
        if (document.activeElement !== weightInput) weightInput.focus();
        updateUnitPosition();
        this.updateButtonStates('weight');
      });
    }

    if (increaseButton) {
      increaseButton.addEventListener('click', () => {
        const currentValue = parseInt(weightInput.value, 10) || 18;
        weightInput.value = Math.min(100, currentValue + 1);
        if (document.activeElement !== weightInput) weightInput.focus();
        updateUnitPosition();
        this.updateButtonStates('weight');
      });
    }

    weightInput.addEventListener('change', () => {
      const value = parseInt(weightInput.value, 10) || 18;
      weightInput.value = Math.max(1, Math.min(100, value));
      updateUnitPosition();
      this.updateButtonStates('weight');
    });
    weightInput.addEventListener('input', updateUnitPosition);
    requestAnimationFrame(updateUnitPosition);
    setTimeout(updateUnitPosition, 100);
  }

  /**
   * Set up breed input with dropdown
   */
  async setupBreedInput() {
    const inputs = this.currentStepElement.querySelectorAll('[data-breed-input]');
    inputs.forEach((input) => {
      if (input.dataset.breedBound === 'true') return;
      input.dataset.breedBound = 'true';
      this.attachBreedAutocomplete(input);
    });
    this.setupBreedCheckboxToggle();
  }

  attachBreedAutocomplete(breedInput) {
    const breedDropdown = breedInput.parentElement?.querySelector('[data-breed-dropdown]');

    if (!breedInput || !breedDropdown) return;

    // Get breeds from window.Wizard1Breeds (loaded from wizard-1-breeds.js)
    const breedsData = window.Wizard1Breeds || [];
    if (breedsData.length === 0) {
      console.warn('Breeds data not loaded. Make sure wizard-1-breeds.js is loaded before wizard-1-steps.js');
      return;
    }

    // Get current locale (wizard-2 sets __wizard2CurrentLocale; __wizard1* mirrored in wizard-2-flow.liquid)
    const locale = window.__wizard1CurrentLocale || window.__wizard2CurrentLocale || 'nl';
    const labelKey = locale === 'nl' ? 'label_nl' : 'label_en';

    let selectedIndex = -1;
    let filteredBreeds = [];

    // Filter breeds based on input
    const filterBreeds = (query) => {
      // First, filter out "mixed" and "dont_know" as they are checkboxes
      const availableBreeds = breedsData.filter(breed => 
        breed.value !== 'mixed' && breed.value !== 'dont_know'
      );
      
      if (!query || query.trim() === '') {
        filteredBreeds = availableBreeds; // Show all breeds if no query
      } else {
        const lowerQuery = query.toLowerCase();
        filteredBreeds = availableBreeds.filter(breed => {
          // Search in both label and value
          const label = breed[labelKey] || breed.label_en || '';
          const value = breed.value || '';
          return label.toLowerCase().includes(lowerQuery) || 
                 value.toLowerCase().includes(lowerQuery);
        }); // Show all matching results
      }
      
      // Sort breeds alphabetically by locale label
      filteredBreeds.sort((a, b) => {
        const labelA = (a[labelKey] || a.label_en || a.value || '').toLowerCase();
        const labelB = (b[labelKey] || b.label_en || b.value || '').toLowerCase();
        return labelA.localeCompare(labelB);
      });
      
      if (filteredBreeds.length === 0 && query && String(query).trim()) {
        const otherText = (window.WizardBreedEntry && window.WizardBreedEntry.otherLabel)
          ? window.WizardBreedEntry.otherLabel()
          : (this.getLocaleString?.('quiz.steps.breed.option_other') || 'Other');
        filteredBreeds = [{ value: 'other', label_en: otherText, label_nl: otherText }];
      }

      selectedIndex = -1;
      this.renderBreedDropdown(filteredBreeds, breedDropdown, labelKey);
    };

    // Handle input
    breedInput.addEventListener('input', (e) => {
      const query = e.target.value;
      filterBreeds(query);
    });

    // Handle keyboard navigation
    breedInput.addEventListener('keydown', (e) => {
      if (filteredBreeds.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectedIndex = Math.min(selectedIndex + 1, filteredBreeds.length - 1);
        this.highlightBreedOption(breedDropdown, selectedIndex);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectedIndex = Math.max(selectedIndex - 1, -1);
        this.highlightBreedOption(breedDropdown, selectedIndex);
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        this.selectBreed(filteredBreeds[selectedIndex], breedInput, breedDropdown);
      } else if (e.key === 'Escape') {
        this.hideBreedDropdown(breedDropdown);
      }
    });

    // Handle click outside to close dropdown
    document.addEventListener('click', (e) => {
      if (!breedInput.contains(e.target) && !breedDropdown.contains(e.target)) {
        this.hideBreedDropdown(breedDropdown);
      }
    });

    // Handle focus to show breeds
    breedInput.addEventListener('focus', () => {
      filterBreeds(breedInput.value);
    });
  }

  /**
   * Set up breed checkbox toggle logic (hide/show input and other checkbox)
   */
  setupBreedCheckboxToggle() {
    const roots = this.currentStepElement.querySelectorAll('[data-wizard-dog-fields], [data-wizard-dog-copy]');
    const list = roots.length ? roots : [this.currentStepElement];
    list.forEach((root) => this.bindBreedCheckboxes(root));
  }

  bindBreedCheckboxes(root) {
    const breedInputWrapper = root.querySelector('.breed-input-wrapper');
    const breedInput = root.querySelector('[data-breed-input]');
    const breedUnknownCheckbox = root.querySelector('[data-field="breedUnknown"]');
    const breedMixedCheckbox = root.querySelector('[data-field="breedMixed"]');
    const breedUnknownField = breedUnknownCheckbox?.closest('.form-field');
    const breedMixedField = breedMixedCheckbox?.closest('.form-field');

    if (!breedInputWrapper || !breedUnknownCheckbox || !breedMixedCheckbox) return;

    const dogIndex = Number(breedInput?.getAttribute('data-dog-index') || breedUnknownCheckbox.getAttribute('data-dog-index') || 0);
    const writeBreed = (value) => {
      if (dogIndex > 0 && this.flow.state.setDogField) this.flow.state.setDogField(dogIndex, 'breed', value);
      else this.flow.updatePetData('breed', value);
    };

    const toggleBreedFields = () => {
      const isUnknownChecked = breedUnknownCheckbox.checked;
      const isMixedChecked = breedMixedCheckbox.checked;

      if (isUnknownChecked) {
        breedInputWrapper.style.display = 'none';
        if (breedMixedField) breedMixedField.style.display = 'none';
        if (breedInput) breedInput.value = '';
        breedMixedCheckbox.checked = false;
        writeBreed('dont_know');
      } else if (isMixedChecked) {
        breedInputWrapper.style.display = 'none';
        if (breedUnknownField) breedUnknownField.style.display = 'none';
        if (breedInput) breedInput.value = '';
        breedUnknownCheckbox.checked = false;
        writeBreed('mixed');
      } else {
        breedInputWrapper.style.display = '';
        if (breedUnknownField) breedUnknownField.style.display = '';
        if (breedMixedField) breedMixedField.style.display = '';
        writeBreed('');
      }
      this.updateButtonStates('breed');
    };

    const onBreedChoice = () => {
      toggleBreedFields();
      if (this._restoringBreedStep) return;
      if (breedUnknownCheckbox.checked || breedMixedCheckbox.checked) {
        this.maybeAutoAdvance('breed', false);
      }
    };
    breedUnknownCheckbox.addEventListener('change', onBreedChoice);
    breedMixedCheckbox.addEventListener('change', onBreedChoice);
  }

  /**
   * Hide breed dropdown and update wrapper class
   * @param {HTMLElement} dropdown - Dropdown container element
   */
  hideBreedDropdown(dropdown) {
    dropdown.style.display = 'none';
    const wrapper = dropdown.closest('.breed-input-wrapper');
    if (wrapper) {
      wrapper.classList.remove('has-dropdown');
    }
  }

  /**
   * Show breed dropdown and update wrapper class
   * @param {HTMLElement} dropdown - Dropdown container element
   */
  showBreedDropdown(dropdown) {
    dropdown.style.display = 'block';
    const wrapper = dropdown.closest('.breed-input-wrapper');
    if (wrapper) {
      wrapper.classList.add('has-dropdown');
    }
  }

  /**
   * Render breed dropdown options
   * @param {Array<Object>} breeds - Array of breed objects with value, label_en, label_nl
   * @param {HTMLElement} dropdown - Dropdown container element
   * @param {string} labelKey - Key to use for label ('label_en' or 'label_nl')
   */
  renderBreedDropdown(breeds, dropdown, labelKey = 'label_en') {
    if (breeds.length === 0) {
      this.hideBreedDropdown(dropdown);
      return;
    }

    dropdown.innerHTML = '';
    this.showBreedDropdown(dropdown);

    breeds.forEach((breed, index) => {
      const option = document.createElement('div');
      option.className = 'breed-option';
      // Display the localized label
      option.textContent = breed[labelKey] || breed.label_en || breed.value;
      // Store the breed object for selection
      option.setAttribute('data-breed-value', breed.value);
      option.setAttribute('data-breed-label', breed[labelKey] || breed.label_en || breed.value);
      option.setAttribute('data-index', index);
      
      option.addEventListener('click', () => {
        const field = dropdown.parentElement?.querySelector('[data-breed-input]');
        this.selectBreed(breed, field, dropdown);
      });

      option.addEventListener('mouseenter', () => {
        this.highlightBreedOption(dropdown, index);
      });

      dropdown.appendChild(option);
    });
  }

  /**
   * Highlight a breed option
   * @param {HTMLElement} dropdown - Dropdown container
   * @param {number} index - Index of option to highlight
   */
  highlightBreedOption(dropdown, index) {
    const options = dropdown.querySelectorAll('.breed-option');
    options.forEach((opt, i) => {
      if (i === index) {
        opt.classList.add('breed-option--highlighted');
      } else {
        opt.classList.remove('breed-option--highlighted');
      }
    });
  }

  /**
   * Select a breed
   * @param {Object|string} breed - Selected breed object (with value, label_en, label_nl) or breed value string
   * @param {HTMLElement|null} input - Breed input element (if null, will find it)
   * @param {HTMLElement} dropdown - Dropdown container
   */
  selectBreed(breed, input, dropdown) {
    const breedInput = input || this.currentStepElement.querySelector('[data-breed-input]');
    if (!breedInput) return;

    // Handle both object and string formats
    let breedValue, breedLabel;
    if (typeof breed === 'object' && breed.value) {
      breedValue = breed.value;
      // Get localized label
      const locale = window.__wizard1CurrentLocale || window.__wizard2CurrentLocale || 'nl';
      const labelKey = locale === 'nl' ? 'label_nl' : 'label_en';
      breedLabel = breed[labelKey] || breed.label_en || breed.value;
    } else {
      // Legacy string format - try to find in breeds data
      breedValue = breed;
      breedLabel = breed;
      const breedsData = window.Wizard1Breeds || [];
      const breedObj = breedsData.find(b => b.value === breed);
      if (breedObj) {
        const locale = window.__wizard1CurrentLocale || window.__wizard2CurrentLocale || 'nl';
        const labelKey = locale === 'nl' ? 'label_nl' : 'label_en';
        breedLabel = breedObj[labelKey] || breedObj.label_en || breedObj.value;
      }
    }

    // Display the label, but save the value
    breedInput.value = breedLabel;
    const wasComplete = this.flow.state.validateStep('breed');
    const dogIndex = Number(breedInput.getAttribute('data-dog-index') || 0);
    if (dogIndex > 0 && this.flow.state.setDogField) {
      this.flow.state.setDogField(dogIndex, 'breed', breedValue);
    } else {
      this.flow.updatePetData('breed', breedValue);
    }
    this.hideBreedDropdown(dropdown);
    this.updateButtonStates('breed');
    this.maybeAutoAdvance('breed', wasComplete);
  }

  maybeAutoAdvance(step, wasComplete) {
    if (wasComplete) return;
    if (!this.flow.state.validateStep(step)) return;
    setTimeout(() => {
      if (this.flow.state.getCurrentStep() !== step) return;
      if (!this.flow.state.validateStep(step)) return;
      this.flow.navigateNext();
    }, 300);
  }

  /**
   * Set up allergies validation
   */
  setupAllergiesValidation() {
    const roots = this.currentStepElement.querySelectorAll('[data-wizard-dog-fields], [data-wizard-dog-copy]');
    const groups = roots.length ? roots : [this.currentStepElement];
    groups.forEach((root) => this.bindAllergyGroup(root));
    this.refreshAllergyMessage();
  }

  allergyDogIndex(root) {
    const marked = root.querySelector('[data-dog-index]');
    return Number((marked && marked.getAttribute('data-dog-index')) || root.getAttribute('data-dog-index') || 0);
  }

  writeDogAllergies(dogIndex, list) {
    if (dogIndex > 0 && this.flow.state.setDogField) this.flow.state.setDogField(dogIndex, 'allergies', list);
    else this.flow.updatePetData('allergies', list);
  }

  refreshAllergyMessage() {
    const activeThree = this.constructor.ACTIVE_PRODUCT_ALLERGIES || [];
    const dogs = this.flow.state.ensureDogs ? this.flow.state.ensureDogs() : [this.flow.state.getPetData()];
    const blocked = dogs.some((dog) => {
      const list = this.normalizePetAllergies(dog.allergies || []);
      return activeThree.every((token) => list.includes(token));
    });
    const message = this.currentStepElement.querySelector('[data-conditional="all-allergies-selected"]');
    if (message) message.style.display = blocked ? 'flex' : 'none';
    const nextButton = document.querySelector('[data-next-button]');
    if (nextButton) nextButton.disabled = blocked;
  }

  bindAllergyGroup(root) {
    const checkboxes = root.querySelectorAll('[data-allergy-checkbox]');
    const noneCheckbox = root.querySelector('[data-allergy-none]');
    const otherCheckboxes = Array.from(checkboxes).filter((cb) => !cb.hasAttribute('data-allergy-none'));
    const dogIndex = this.allergyDogIndex(root);
    const dogs = this.flow.state.ensureDogs ? this.flow.state.ensureDogs() : [];
    const dog = dogs[dogIndex] || this.flow.state.getPetData();
    const normalized = this.normalizePetAllergies(dog.allergies || []);
    if (JSON.stringify(dog.allergies || []) !== JSON.stringify(normalized)) {
      this.writeDogAllergies(dogIndex, normalized);
    }
    const allergies = normalized;
    if (noneCheckbox) noneCheckbox.checked = allergies.length === 0;
    otherCheckboxes.forEach((cb) => {
      cb.checked = allergies.includes(cb.value);
    });

    let isUpdating = false;
    const selectedNow = () => this.normalizePetAllergies(
      otherCheckboxes.filter((cb) => cb.checked).map((cb) => cb.value).filter((val) => val !== '')
    );

    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        if (isUpdating) return;
        isUpdating = true;
        try {
          if (checkbox.hasAttribute('data-allergy-none') && checkbox.checked) {
            otherCheckboxes.forEach((cb) => { cb.checked = false; });
            this.writeDogAllergies(dogIndex, []);
          } else if (!checkbox.hasAttribute('data-allergy-none')) {
            if (checkbox.checked && noneCheckbox) noneCheckbox.checked = false;
            const selected = selectedNow();
            this.writeDogAllergies(dogIndex, selected);
            if (selected.length === 0 && noneCheckbox) noneCheckbox.checked = true;
          }
          this.refreshAllergyMessage();
        } finally {
          isUpdating = false;
        }
      });
    });
  }

  /**
   * Check for missing required steps before email step
   * @returns {string|null} First missing step identifier or null if all required
   */
  checkMissingRequiredSteps() {
    const order = (window.BzWizard && typeof window.BzWizard.domSteps === 'function')
      ? window.BzWizard.domSteps()
      : ['name', 'spayed', 'age', 'breed', 'food', 'eating', 'weight', 'composition', 'activity', 'issues', 'snacks'];
    const emailIndex = order.indexOf('email');
    const requiredSteps = emailIndex === -1 ? order : order.slice(0, emailIndex);

    for (const step of requiredSteps) {
      if (!this.flow.state.validateStep(step)) {
        return step;
      }
    }

    return null;
  }

  /**
   * Get URL for a step
   * @param {string} step - Step identifier
   * @returns {string} URL path for the step
   */
  getStepUrl(step) {
    const config = this.flow.getConfig();
    const prefix = config?.urlPrefix || '/wizard-1';
    
    const stepToUrlMap = {
      'name': `${prefix}/quiz/name`,
      'spayed': `${prefix}/quiz/spayed`,
      'age': `${prefix}/quiz/age`,
      'breed': `${prefix}/quiz/breed`,
      'food': `${prefix}/quiz/food`,
      'eating': `${prefix}/quiz/eating`,
      'weight': `${prefix}/quiz/weight`,
      'composition': `${prefix}/quiz/composition`,
      'activity': `${prefix}/quiz/activity`,
      'allergies': `${prefix}/quiz/allergies`,
      'issues': `${prefix}/quiz/issues`,
      'snacks': `${prefix}/quiz/snacks`
    };
    
    return stepToUrlMap[step] || `${prefix}/quiz/name`;
  }

  /**
   * Set up missing data warning for email step
   */
  setupMissingDataWarning() {
    const missingStep = this.checkMissingRequiredSteps();
    const warningElement = this.currentStepElement?.querySelector('[data-conditional="missing-data-warning"]');
    
    if (missingStep && warningElement) {
      const missingStepUrl = this.getStepUrl(missingStep);
      const linkElement = warningElement.querySelector('[data-missing-step-link]');
      
      if (linkElement) {
        // Update the link href with the missing step URL
        linkElement.href = missingStepUrl;
        warningElement.style.display = 'flex';
      } else {
        // Link element doesn't exist yet (template not loaded or rendered)
        // This shouldn't happen, but handle gracefully
        warningElement.style.display = 'flex';
      }
    } else if (warningElement) {
      // No missing steps - hide warning
      warningElement.style.display = 'none';
    }
  }

  /**
   * Set up email validation
   */
  setupEmailValidation() {
    const emailInput = this.currentStepElement.querySelector('[name="ownerEmail"]');
    const nextButton = document.querySelector('[data-next-button]');

    if (emailInput && nextButton) {
      // Check for missing required steps and set up warning
      this.setupMissingDataWarning();
      const missingStep = this.checkMissingRequiredSteps();
      
      // Button disabled when empty or missing required steps (valid format checked on Continue click)
      const hasValue = !!emailInput.value.trim();
      nextButton.disabled = !hasValue || !!missingStep;

      // Update button state when email input changes
      emailInput.addEventListener('input', () => {
        const hasValue = !!emailInput.value.trim();
        const hasMissingData = !!this.checkMissingRequiredSteps();
        nextButton.disabled = !hasValue || hasMissingData;

        // Only hide error when email becomes valid (never show on input/blur - only on Continue click)
        const isValid = this.flow.state.isValidEmail(emailInput.value);
        const message = this.currentStepElement.querySelector('[data-conditional="email-validation"]');
        if (message && isValid) {
          message.style.display = 'none';
        }
        
        // Check if email matches previous session
        this.checkEmailMatch(emailInput.value);
        
        // Update missing data warning visibility
        this.setupMissingDataWarning();
      });

      // No blur validation - error only shows when Continue is clicked with invalid email
      
      // Listen for changes to petData fields (when user navigates back and fills data)
      // Use MutationObserver or periodic check - periodic check is simpler for now
      // This will be handled via handleInputChange which calls updateButtonStates
    }
  }

  /**
   * Check if email matches previous session and show info message
   * @param {string} email - Email address to check
   */
  checkEmailMatch(email) {
    if (!email || !this.flow.state.isValidEmail(email)) {
      // Hide info message if email is invalid or empty
      this.hideEmailMatchMessage();
      return;
    }

    const normalizedEmail = email.toLowerCase().trim();
    
    // Check if email exists in localStorage
    const emailExists = this.flow.storage.emailExistsInStorage(normalizedEmail);
    
    if (emailExists) {
      // Get customerId from storage for this email
      const customerId = this.flow.storage.getCustomerIdForEmail(normalizedEmail);
      
      if (customerId) {
        // Set sessionCustomerId for multi-pet flow
        this.flow.state.sessionCustomerId = customerId;
        this.showEmailMatchMessage();
      } else {
        // Email exists but no customerId - might be from incomplete session
        this.hideEmailMatchMessage();
      }
    } else {
      // Different email - clear sessionCustomerId
      this.flow.state.clearSessionCustomerId();
      this.hideEmailMatchMessage();
    }
  }

  /**
   * Show email match info message
   */
  showEmailMatchMessage() {
    const messageElement = this.currentStepElement.querySelector('[data-conditional="email-match"]');
    if (!messageElement) return;
    
    const messageContent = messageElement.querySelector('[data-dynamic-text]');
    if (!messageContent) return;
    
    // Get locale
    const lang = document.documentElement.lang || 'en';
    const isDutch = lang.startsWith('nl');
    
    // Set message content
    const messageText = isDutch 
      ? 'Dit e-mailadres is al geregistreerd. We voegen deze hond toe aan je account.'
      : 'This email is already registered. We\'ll add this pet to your account.';
    
    messageContent.textContent = messageText;
    
    // Show message
    messageElement.style.display = 'flex';
    
    // Hide email exists error if it was shown
    const emailExistsMessage = this.currentStepElement.querySelector('[data-conditional="email-exists"]');
    if (emailExistsMessage) {
      emailExistsMessage.style.display = 'none';
    }
  }

  /**
   * Hide email match info message
   */
  hideEmailMatchMessage() {
    const messageElement = this.currentStepElement.querySelector('[data-conditional="email-match"]');
    if (messageElement) {
      messageElement.style.display = 'none';
    }
  }

  /**
   * Show duplicate pet name message at email step
   * @param {string} petName - Pet name that already exists
   * @param {Object} existingPet - Existing pet object with metaobjectId and step
   */
  showDuplicatePetNameMessage(petName, existingPet) {
    const messageElement = this.currentStepElement.querySelector('[data-conditional="duplicate-pet-name-email"]');
    if (!messageElement) return;
    
    const messageContent = messageElement.querySelector('[data-dynamic-text]');
    if (!messageContent) return;
    
    // Get locale
    const lang = document.documentElement.lang || 'en';
    const isDutch = lang.startsWith('nl');
    
    // Get furthest step for the existing pet
    const furthestStep = existingPet.step || 'name';
    
    // Set message content with HTML link
    const messageText = isDutch 
      ? `Je hebt al een hond genaamd <strong>${petName}</strong>. <a href="#" class="inline-link" data-navigate="quiz" data-step="${furthestStep}" data-pet-id="${existingPet.metaobjectId}">Bewerk deze hond</a> of ga door om een nieuwe hond met dezelfde naam aan te maken.`
      : `You already have a pet named <strong>${petName}</strong>. <a href="#" class="inline-link" data-navigate="quiz" data-step="${furthestStep}" data-pet-id="${existingPet.metaobjectId}">Edit this pet</a> or continue to create a new pet with the same name.`;
    
    messageContent.innerHTML = messageText;
    
    // Set up click handler for edit link
    const link = messageContent.querySelector('.inline-link');
    if (link) {
      link.addEventListener('click', async (e) => {
        e.preventDefault();
        // Load existing pet data
        if (existingPet.metaobjectId) {
          try {
            const metaobject = await this.flow.shopifyAPI.getPetMetaobjectById(existingPet.metaobjectId);
            if (metaobject) {
              this.loadPetDataFromMetaobject(metaobject, true);
              // Navigate to the furthest step
              this.flow.router.navigate(furthestStep, {});
            }
          } catch (error) {
            console.error('Error loading existing pet:', error);
            alert(isDutch ? 'Fout bij het laden van de bestaande hond.' : 'Error loading existing pet.');
          }
        }
      });
    }
    
    // Show message
    messageElement.style.display = 'flex';
  }

  /**
   * Show email exists error message with login link
   */
  showEmailExistsError() {
    const messageElement = this.currentStepElement.querySelector('[data-conditional="email-exists"]');
    if (!messageElement) return;
    
    const messageContent = messageElement.querySelector('[data-dynamic-text]');
    if (!messageContent) return;
    
    // Get locale
    const lang = document.documentElement.lang || 'en';
    const isDutch = lang.startsWith('nl');
    
    // Construct login URL (standard Shopify route)
    const loginUrl = '/account/login';
    
    // Set message content with HTML
    const messageText = isDutch 
      ? `Dit e-mailadres bestaat al. <a href="${loginUrl}" class="inline-link">Log hier in</a> om een hond aan je account toe te voegen.`
      : `This email address already exists. <a href="${loginUrl}" class="inline-link">Log in here</a> to add a dog to your account.`;
    
    messageContent.innerHTML = messageText;
    
    // Show message
    messageElement.style.display = 'flex';
    
    // Hide other email validation messages
    const emailValidationMessage = this.currentStepElement.querySelector('[data-conditional="email-validation"]');
    if (emailValidationMessage) {
      emailValidationMessage.style.display = 'none';
    }
  }

  /**
   * Set up customer creation (Step 10)
   */
  setupCustomerCreation() {
    // Only set up customer creation on email step
    if (this.flow.state.getCurrentStep() !== 'email') {
      return;
    }
    
    const nextButton = document.querySelector('[data-next-button]');
    
    if (nextButton) {
      // Check if we've already set up this handler (prevent duplicates)
      if (nextButton.dataset.customerCreationHandler === 'true') {
        return;
      }
      nextButton.dataset.customerCreationHandler = 'true';
      
      // Add event listener for email step customer creation
      // Note: This will only handle email step - global listener is prevented from navigating on email step
      // Use capture phase to run before the global handler
      nextButton.addEventListener('click', async (e) => {
        
        // Only handle if this is the email step
        if (this.flow.state.getCurrentStep() !== 'email') {
          return;
        }

        const petData = this.flow.state.getPetData();
        const emailInput = this.currentStepElement?.querySelector('[name="ownerEmail"]');
        const emailValue = emailInput ? emailInput.value.trim() : (petData.ownerEmail || '');
        const isValidEmail = this.flow.state.isValidEmail(emailValue);

        if (!isValidEmail) {
          const message = this.currentStepElement?.querySelector('[data-conditional="email-validation"]');
          if (message) {
            message.style.display = 'flex';
          }
          return;
        }

        const labelEl = nextButton.querySelector('.next-button-label') || nextButton;
        const originalLabel = labelEl.textContent;

        const resetEmailLoadingState = () => {
          nextButton.disabled = false;
          nextButton.classList.remove('is-loading');
          nextButton.removeAttribute('aria-busy');
          labelEl.textContent = originalLabel;
        };

        const applyEmailLoadingState = () => {
          nextButton.disabled = true;
          nextButton.classList.add('is-loading');
          nextButton.setAttribute('aria-busy', 'true');
        };

        // CRITICAL FIX: Only skip if BOTH customerId AND petMetaobjectId exist
        // If customerId exists but petMetaobjectId is null, we're creating a NEW pet
        // and need to create it even though the customer already exists
        // Track whether we need to create customer/pet or just navigate
        const shouldCreateCustomer = !(this.flow.state.customerId && this.flow.state.petMetaobjectId);

        if (!shouldCreateCustomer) {
          // Both customer and pet already created - skip API but same loading UI as create path
          e.stopPropagation();
          e.stopImmediatePropagation();

          if (nextButton.disabled) {
            return;
          }

          applyEmailLoadingState();

          try {
            if (this.flow.analytics && typeof this.flow.analytics.trackFacebookLead === 'function') {
              try {
                this.flow.analytics.trackFacebookLead({
                  pet_name: petData.name
                });
                await new Promise(resolve => setTimeout(resolve, 200));
              } catch (e) {
                console.warn('Facebook Lead event tracking failed:', e);
              }
            }

            if (this.flow.klaviyoManager && petData.ownerEmail) {
              if (!this.flow.klaviyoManager.hasPendingEvent(petData.ownerEmail)) {
                try {
                  await this.flow.klaviyoManager.sendPetUpdatedEvent(
                    petData,
                    {
                      ownerEmail: petData.ownerEmail,
                      ownerName: petData.ownerName || ''
                    },
                    this.flow.state.petMetaobjectId
                  );
                } catch (e) {
                  console.warn('Failed to send Klaviyo "Pet updated" event (non-blocking):', e);
                }
              }
            }

            this.flow.state.sessionOwnerName = petData.ownerName;
            this.flow.state.sessionOwnerEmail = petData.ownerEmail;
            this.flow.state.sessionEmailConsent = petData.emailConsent !== undefined ? petData.emailConsent : true;

            nextButton.disabled = false;
            nextButton.classList.remove('is-loading');
            nextButton.removeAttribute('aria-busy');

            this.flow.navigateNext();
          } catch (error) {
            console.error('Error in email step (skip-create path):', error);
            resetEmailLoadingState();
          }
          return;
        }

        e.stopPropagation();
        e.stopImmediatePropagation();

        if (nextButton.disabled) {
          return;
        }

        applyEmailLoadingState();

        try {
          // Validate petData before sending
          if (!petData || typeof petData !== 'object' || Object.keys(petData).length === 0 || 
              !petData.name || typeof petData.name !== 'string' || petData.name.trim().length === 0) {
            console.error('petData is invalid:', petData);
            alert('Er is een fout opgetreden. Probeer de pagina te vernieuwen.');
            resetEmailLoadingState();
            return;
          }
          
          // Prepare customer data
          const customerData = {
            ownerName: petData.ownerName,
            ownerEmail: petData.ownerEmail,
            emailConsent: petData.emailConsent
          };
          

          // Get current calculations (may be empty on email step)
          const calculations = this.flow.state.getCalculations() || {};

          // CRITICAL: Check if customer is logged in before calling backend
          // If logged in, we'll pass their customerId so the pet gets attached to their account
          // even if the email already exists
          let loggedInCustomerId = null;
          try {
            await this.flow.detectLoggedInCustomer();
            loggedInCustomerId = this.flow.state.customerId || null;
          } catch (error) {
            // Customer not logged in - this is fine, continue as anonymous
          }

          // Check for duplicate pet name at email step (now that we know the customer)
          // Use sessionCustomerId if available, otherwise loggedInCustomerId
          const customerIdForCheck = this.flow.state.sessionCustomerId || loggedInCustomerId;
          if (customerIdForCheck) {
            const existingPet = await this.flow.storage.petNameExists(petData.name, { 
              flow: this.flow,
              customerId: customerIdForCheck 
            });
            
            if (existingPet && existingPet.metaobjectId) {
              // Pet with same name exists - show message and let user decide
              this.showDuplicatePetNameMessage(petData.name, existingPet);
              // Don't block - allow them to continue if they want to create new pet with same name
            }
          }

          // Check if this is a new pet BEFORE creation (for Klaviyo event type)
          const isNewPet = !this.flow.state.petMetaobjectId;

          // Use sessionCustomerId if available (for multi-pet flow), otherwise loggedInCustomerId
          const customerIdToUse = this.flow.state.sessionCustomerId || loggedInCustomerId;

          // Call backend API to create customer and Pet Metaobject
          const result = await this.flow.shopifyAPI.createCustomerViaBackend(
            customerData,
            petData,
            calculations,
            this.flow.state.petMetaobjectId, // Pass existing ID if updating
            customerIdToUse // Pass sessionCustomerId or logged-in customerId
          );

          // Store customer ID and Pet Metaobject ID in state
          if (result.customerId) {
            this.flow.state.customerId = result.customerId;
            // Also store as sessionCustomerId for multi-pet flow
            this.flow.state.sessionCustomerId = result.customerId;
          }

          if (result.petMetaobjectId) {
            this.flow.state.petMetaobjectId = result.petMetaobjectId;
            const lead = this.flow.state.ensureDogs?.()[0];
            if (lead && !lead.petMetaobjectId) lead.petMetaobjectId = result.petMetaobjectId;
          }
          if (typeof this.flow.syncHouseholdPets === 'function') {
            await this.flow.syncHouseholdPets({
              createMissing: false,
              customerId: this.flow.state.customerId
            });
          }

          // Update preserved name/email/consent for future dogs (multi-pet flow)
          this.flow.state.sessionOwnerName = petData.ownerName;
          this.flow.state.sessionOwnerEmail = petData.ownerEmail;
          this.flow.state.sessionEmailConsent = petData.emailConsent !== undefined ? petData.emailConsent : true;

          // Save state to localStorage only (no Shopify save needed - we just created/updated)
          this.flow.storage.saveToLocalStorage(this.flow.state.getState());

          // Set flag to prevent duplicate save in navigateNext()
          this.flow.state._justCreated = true;

          // Schedule Klaviyo event (15-minute consolidation timer via backend)
          // Wizard-1 pages set __wizard1BackendApiUrl; wizard-2 sets __wizard2BackendApiUrl
          const backendApiUrl =
            window.__wizard1BackendApiUrl ||
            window.__wizard2BackendApiUrl ||
            this.flow?.klaviyoManager?.backendApiUrl ||
            null;

          if (window.KlaviyoEventManager && backendApiUrl) {
            try {
              
              // Initialize manager if not already initialized
              if (!this.flow.klaviyoManager) {
                const isWizard2 = typeof window !== 'undefined' && window.wizard2Flow === this.flow;
                if (isWizard2 && typeof KlaviyoEventManagerWizard2 !== 'undefined') {
                  this.flow.klaviyoManager = new KlaviyoEventManagerWizard2(backendApiUrl, this.flow.config);
                } else {
                  this.flow.klaviyoManager = new KlaviyoEventManager(backendApiUrl, this.flow.config);
                }
              }
              
              
              this.flow.klaviyoManager.scheduleKlaviyoEvent(
                petData,
                customerData,
                result.petMetaobjectId,
                isNewPet
              );
            } catch (e) {
              
              console.warn('Failed to schedule Klaviyo event (non-blocking):', e);
            }
          } else {
            
          }

          // Track Lead event in Facebook Pixel (after successful customer creation)
          // Fire before navigation to ensure event is sent
          if (this.flow.analytics && typeof this.flow.analytics.trackFacebookLead === 'function') {
            try {
              this.flow.analytics.trackFacebookLead({
                pet_name: petData.name
              });
              // Small delay to ensure event is sent before navigation
              // (Facebook Pixel events are async, but this helps ensure delivery)
              await new Promise(resolve => setTimeout(resolve, 200));
            } catch (e) {
              console.warn('Facebook Lead event tracking failed:', e);
            }
          }

          // Clear loading state before navigation (next step replaces footer)
          nextButton.disabled = false;
          nextButton.classList.remove('is-loading');
          nextButton.removeAttribute('aria-busy');

          // Proceed to next step
          this.flow.navigateNext();

        } catch (error) {
          console.error('Error creating customer:', error.message);
          
          // Check if error is about email already existing
          const isEmailExistsError = error.message && (
            error.message.includes('already exists') || 
            error.message.includes('already registered') ||
            error.message.includes('email address is already') ||
            error.message.includes('customer_email_exists')
          );
          
          if (isEmailExistsError) {
            // Show inline error message below email field with login link
            this.showEmailExistsError();
          } else {
            // Show generic error alert for other errors
            alert('Er is een fout opgetreden bij het aanmaken van je account. Probeer het opnieuw.');
          }
          
          resetEmailLoadingState();
        }
      }, { once: false });
    }
  }

  /**
   * Update button states based on step validation
   * @param {string} step - Step identifier
   */
  updateButtonStates(step) {
    // Bulk step uses custom button, skip standard button updates
    if (step === 'bulk') {
      return;
    }

    const nextButton = document.querySelector('[data-next-button]');
    if (nextButton) {
      const labelEl = nextButton.querySelector('.next-button-label') || nextButton;
      const isLoading = nextButton.classList.contains('is-loading');
      // Email step: use "Plan bekijken" / "View plan"; other steps: use "Doorgaan" / "Continue"
      if (step === 'email' && !isLoading) {
        labelEl.textContent = this.getLocaleString('quiz.steps.email.button_next');
      } else if (step !== 'email' && !isLoading) {
        labelEl.textContent = this.getLocaleString('quiz.buttons.next');
      }

      // Calculation step doesn't need validation (auto-advances)
      if (step === 'calculation') {
        nextButton.disabled = true;
        return;
      }

      // Weight step: always enabled (has default value)
      if (step === 'weight') {
        nextButton.disabled = !this.flow.state.validateStep('weight');
        return;
      }
      
      // Flavors step: at least one flavor must be selected
      if (step === 'flavors') {
        const selectedFlavors = this.flow.state.selectedFlavors || [];
        nextButton.disabled = selectedFlavors.length === 0;
        return;
      }
      
      // Email step: button enabled when non-empty (format validated on Continue click)
      if (step === 'email') {
        const emailInput = this.currentStepElement?.querySelector('[name="ownerEmail"]');
        const hasValue = emailInput ? !!emailInput.value.trim() : false;
        const hasMissingData = !!this.checkMissingRequiredSteps();
        if (!isLoading) {
          nextButton.disabled = !hasValue || hasMissingData;
        }
        // Update missing data warning when button state changes
        this.setupMissingDataWarning();
        return;
      }
      
      const isValid = this.flow.state.validateStep(step);
      nextButton.disabled = !isValid;
    }
  }

  decodeIssueToast(value) {
    return String(value || '')
      .replace(/&amp;/g, '&')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  syncIssueNotes() {
    const root = this.currentStepElement;
    if (!root) return;
    const notes = root.querySelector('[data-issues-notes]');
    if (!notes) return;
    const dogs = this.flow.state.ensureDogs ? this.flow.state.ensureDogs() : [];
    const selected = [];
    dogs.forEach((dog) => {
      if (!dog || dog.health !== 'some' || !Array.isArray(dog.healthIssues)) return;
      dog.healthIssues.forEach((id) => {
        if (selected.indexOf(id) === -1) selected.push(id);
      });
    });
    const source = root.querySelector('[data-wizard-dog-fields]') || root;
    notes.replaceChildren();
    source.querySelectorAll('input[data-field="healthIssues"]').forEach((input) => {
      if (selected.indexOf(input.value) === -1) return;
      const tone = input.getAttribute('data-toast-tone') === 'warning' ? 'warning' : 'success';
      const note = document.createElement('p');
      note.className = 'wizard-issue-note wizard-issue-note--' + tone;
      note.textContent = this.decodeIssueToast(input.getAttribute('data-toast'));
      notes.appendChild(note);
    });
  }

  shouldShowEatingHelp() {
    const dogs = this.flow.state.ensureDogs ? this.flow.state.ensureDogs() : [];
    return dogs.some((dog) => dog.eating === 'picky' || dog.eating === 'very_picky');
  }

  eatingHelpWasOpen() {
    try {
      return sessionStorage.getItem('wizard_eating_help') === '1';
    } catch (e) {
      return false;
    }
  }

  rememberEatingHelp(open) {
    try {
      if (open) sessionStorage.setItem('wizard_eating_help', '1');
      else sessionStorage.removeItem('wizard_eating_help');
    } catch (e) {}
  }

  eatingHelpOpen() {
    const panel = this.currentStepElement?.querySelector('[data-eating-help]');
    return !!(panel && !panel.hidden);
  }

  showEatingHelp() {
    const root = this.currentStepElement;
    if (!root) return;
    const stack = root.querySelector('.wizard-dog-stack');
    const help = root.querySelector('[data-eating-help]');
    if (stack) stack.hidden = true;
    if (help) help.hidden = false;
    this.rememberEatingHelp(true);
    window.scrollTo(0, 0);
  }

  hideEatingHelp() {
    const root = this.currentStepElement;
    if (!root) return;
    const stack = root.querySelector('.wizard-dog-stack');
    const help = root.querySelector('[data-eating-help]');
    if (stack) stack.hidden = false;
    if (help) help.hidden = true;
    this.rememberEatingHelp(false);
    window.scrollTo(0, 0);
  }

  /**
   * Update progress bar
   * @param {string} step - Step identifier
   */
  updateProgressBar(step) {
    const progressBar = document.querySelector('[data-progress-bar]');
    if (!progressBar) return;

    const fill = progressBar.querySelector('.wizard-1-progress-fill[data-progress-fill]');
    const dogIcon = progressBar.querySelector('[data-progress-icon="dog"]');
    const checkIcon = progressBar.querySelector('[data-progress-icon="check"]');
    const cartIcon = progressBar.querySelector('[data-progress-icon="cart"]');

    // Progress spans from 5% to 95% of the bar
    const START = 5;
    const END = 95;
    const RANGE = END - START;

    const journeySteps = window.BzWizard && window.BzWizard.domSteps ? window.BzWizard.domSteps() : [];
    const QUIZ_IDS = [
      'name',
      'spayed',
      'age',
      'breed',
      'food',
      'eating',
      'weight',
      'composition',
      'activity',
      'allergies',
      'issues',
      'snacks',
      'email',
      'calculation'
    ];
    const PLAN_IDS = ['box_size', 'flavors', 'plan_overview', 'extras'];
    let QUIZ_STEPS = journeySteps.filter((id) => QUIZ_IDS.includes(id));
    let PLAN_STEPS = journeySteps.filter((id) => PLAN_IDS.includes(id));
    if (!QUIZ_STEPS.length) QUIZ_STEPS = QUIZ_IDS.slice();
    if (!PLAN_STEPS.length) PLAN_STEPS = ['flavors', 'plan_overview', 'extras'];
    const TOTAL_STEPS = QUIZ_STEPS.length + PLAN_STEPS.length;

    const quizSpan = (QUIZ_STEPS.length / TOTAL_STEPS) * RANGE;
    const planSpan = RANGE - quizSpan;

    const QUIZ_START = START;
    const QUIZ_END = START + quizSpan;
    const PLAN_START = QUIZ_END;
    const PLAN_END = END;

    let currentPercent = START;

    const isQuizStep = QUIZ_STEPS.includes(step);
    const isPlanStep = PLAN_STEPS.includes(step);

    if (isQuizStep) {
      // Use a fixed percentage spacing that works well on all screen sizes
      // 2% provides good spacing without being too large on small screens
      const ICON_SPACING_PERCENT = 2;
      
      const index = QUIZ_STEPS.indexOf(step);
      const lastIndex = QUIZ_STEPS.length - 1 || 1;
      const t = index / lastIndex;
      
      // Dog icon position - fill should stop exactly at icon center
      const dogIconPos = QUIZ_START + (QUIZ_END - QUIZ_START - ICON_SPACING_PERCENT) * t;
      currentPercent = dogIconPos; // Fill stops exactly at icon position

      if (dogIcon) {
        dogIcon.style.left = `${dogIconPos}%`;
      }

      if (checkIcon) {
        // Position check icon with spacing from dog icon
        checkIcon.style.left = `${QUIZ_END + ICON_SPACING_PERCENT}%`;
        checkIcon.setAttribute('data-state', 'inactive');
      }

      if (cartIcon) {
        // Position cart icon with spacing from check icon
        cartIcon.style.left = `${PLAN_END + ICON_SPACING_PERCENT}%`;
        cartIcon.setAttribute('data-state', 'inactive');
      }
    } else if (isPlanStep) {
      // Use a fixed percentage spacing that works well on all screen sizes
      const ICON_SPACING_PERCENT = 2;
      
      // Dog stays at end of quiz segment with spacing
      const dogPos = QUIZ_END - ICON_SPACING_PERCENT;
      if (dogIcon) {
        dogIcon.style.left = `${dogPos}%`;
      }

      const index = PLAN_STEPS.indexOf(step);
      const lastIndex = PLAN_STEPS.length - 1 || 1;
      const t = index / lastIndex;
      
      // Check icon position - fill should stop exactly at icon center
      const checkIconPos = Math.max(
        PLAN_START + ICON_SPACING_PERCENT + (planSpan - ICON_SPACING_PERCENT * 2) * t,
        PLAN_START + ICON_SPACING_PERCENT
      );
      currentPercent = checkIconPos; // Fill stops exactly at icon position

      if (checkIcon) {
        checkIcon.style.left = `${checkIconPos}%`;
        checkIcon.setAttribute('data-state', 'active');
      }

      if (cartIcon) {
        // Position cart icon with spacing from check icon
        cartIcon.style.left = `${PLAN_END + ICON_SPACING_PERCENT}%`;
        cartIcon.setAttribute(
          'data-state',
          step === 'extras' || step === 'bulk' ? 'active' : 'inactive'
        );
      }
    } else if (step === 'plan_overview') {
      // Wizard 2: one conceptual step between “quiz done” (dog) and checkout (cart) — place checklist midway
      const ICON_SPACING_PERCENT = 2;
      const dogPos = QUIZ_END - ICON_SPACING_PERCENT;
      if (dogIcon) {
        dogIcon.style.left = `${dogPos}%`;
      }
      const cartPos = PLAN_END + ICON_SPACING_PERCENT;
      const checkIconPos = (dogPos + cartPos) / 2;
      currentPercent = checkIconPos;
      if (checkIcon) {
        checkIcon.style.left = `${checkIconPos}%`;
        checkIcon.setAttribute('data-state', 'active');
      }
      if (cartIcon) {
        cartIcon.style.left = `${cartPos}%`;
        cartIcon.setAttribute('data-state', 'inactive');
      }
    } else if (step === 'profile') {
      // Use a fixed percentage spacing that works well on all screen sizes
      const ICON_SPACING_PERCENT = 2;
      
      // Post-flow: everything at the end with proper spacing
      const dogPos = QUIZ_END - ICON_SPACING_PERCENT;
      if (dogIcon) {
        dogIcon.style.left = `${dogPos}%`;
      }
      if (checkIcon) {
        checkIcon.style.left = `${PLAN_END - ICON_SPACING_PERCENT}%`;
        checkIcon.setAttribute('data-state', 'active');
      }
      if (cartIcon) {
        cartIcon.style.left = `${PLAN_END + ICON_SPACING_PERCENT}%`;
        cartIcon.setAttribute('data-state', 'active');
      }
      currentPercent = PLAN_END;
    } else {
      // Use a fixed percentage spacing that works well on all screen sizes
      const ICON_SPACING_PERCENT = 2;
      
      // Fallback: initial state with proper spacing
      if (dogIcon) {
        dogIcon.style.left = `${QUIZ_START}%`;
      }
      if (checkIcon) {
        checkIcon.style.left = `${QUIZ_END + ICON_SPACING_PERCENT}%`;
        checkIcon.setAttribute('data-state', 'inactive');
      }
      if (cartIcon) {
        cartIcon.style.left = `${PLAN_END + ICON_SPACING_PERCENT}%`;
        cartIcon.setAttribute('data-state', 'inactive');
      }
      currentPercent = QUIZ_START;
    }

    if (fill) {
      fill.style.width = `${currentPercent}%`;
    }
  }

  /**
   * Update dynamic text (gender pronouns, pet name, etc.)
   * @param {string} step - Step identifier
   */
  updateDynamicText(step) {
    const dynamicTitles = this.currentStepElement.querySelectorAll('[data-dynamic-title]');
    const dynamicLabels = this.currentStepElement.querySelectorAll('[data-dynamic-label]');
    const dynamicTexts = this.currentStepElement.querySelectorAll('[data-dynamic-text]');
    const petName = this.flow.state.petData.name;
    const gender = this.flow.state.petData.gender;
    
    // Get locale-specific fallback
    const locale = document.documentElement.lang || 'nl';
    const fallbackName = locale === 'nl' ? 'je hond' : 'your dog';
    const displayName = petName || fallbackName;
    
    // Get pronoun based on gender and locale
    let pronoun;
    if (locale === 'nl') {
      pronoun = gender === 'female' ? 'haar' : 'zijn';
    } else {
      pronoun = gender === 'female' ? 'her' : 'his';
    }
    
    // Update dynamic titles
    dynamicTitles.forEach(element => {
      // Always store the current text as original if not set, or reset it if step changed
      // This ensures we always have the raw template text from Liquid
      const currentText = element.textContent;
      if (!element.dataset.originalText || element.dataset.lastStep !== step) {
        element.dataset.originalText = currentText;
        element.dataset.lastStep = step;
      }
      
      let text = element.dataset.originalText;

      if (step === 'calculation' && typeof this.flow.state.ensureDogs === 'function' && this.flow.state.ensureDogs().length > 1) {
        const many = this.getLocaleString?.('quiz.steps.calculation.header_title_many');
        element.textContent = many && many !== 'quiz.steps.calculation.header_title_many'
          ? many
          : (locale === 'nl' ? 'We maken je plan' : 'We are creating your plan');
        return;
      }
      
      // Check if text contains pet_name or dogname placeholders
      const hasPetName = text.includes('%{pet_name}') || text.includes('[dogname]');
      
      // Define accent span for dog name (same styling as plan step)
      const accentName = `<span class="accent accent--freshwater shape_1 nowrap">${displayName}</span>`;
      const spacedAccent = `  ${accentName}  `;

      if (step === 'email' && typeof this.flow.state.ensureDogs === 'function' && this.flow.state.ensureDogs().length > 1) {
        const dogs = this.flow.state.ensureDogs();
        const andWord = locale === 'nl' ? 'en' : 'and';
        const piece = (dog, index) => {
          const shape = (index % 5) + 1;
          const safe = String(dog.name || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
          return `  <span class="accent accent--freshwater shape_${shape} nowrap">${safe}</span>  `;
        };
        const parts = dogs.map(piece);
        const joined = parts.length === 2
          ? parts[0] + andWord + parts[1]
          : parts.slice(0, -1).join(',') + andWord + parts[parts.length - 1];
        text = text.replace(/\s*%\{pet_name\}\s*/g, joined);
        element.innerHTML = text;
        return;
      }
      
      // Special handling for name step - accent "jouw" (Dutch) or "your" (English)
      if (step === 'name') {
        const locale = document.documentElement.lang || 'nl';
        if (locale === 'nl') {
          const accentWord = `<span class="accent accent--neutral-dark shape_1 nowrap">jouw</span>`;
          text = text.replace(/\bjouw\b/g, accentWord);
        } else {
          const accentWord = `<span class="accent accent--neutral-dark shape_1 nowrap">your</span>`;
          text = text.replace(/\byour\b/g, accentWord);
        }
        // Use innerHTML to render the accent span
        element.innerHTML = text;
        return; // Skip the textContent assignment below
      }
      
      // Special handling for spayed step - use gender-specific title
      // Check explicitly for 'male' or 'female' to avoid falsy issues with empty strings
      if (step === 'spayed' && (gender === 'male' || gender === 'female')) {
        // Use gender-specific title based on current gender with accent styling on dog name
        const locale = document.documentElement.lang || 'nl';
        if (locale === 'nl') {
          text = gender === 'male' 
            ? `Is ${spacedAccent} gecastreerd?`
            : `Is ${spacedAccent} gesteriliseerd?`;
        } else {
          text = gender === 'male'
            ? `Is ${spacedAccent} neutered?`
            : `Is ${spacedAccent} spayed?`;
        }
        // Use innerHTML to render the accent span
        element.innerHTML = text;
        return; // Skip the textContent assignment below
      } else if ((step === 'plan' || step === 'plan_overview') && (gender === 'male' || gender === 'female')) {
        // Use gender-specific title for plan step with accent styling on dog name
        const locale = document.documentElement.lang || 'nl';
        if (locale === 'nl') {
          text = gender === 'male' 
            ? `${spacedAccent} zijn plan`
            : `${spacedAccent} haar plan`;
        } else {
          text = gender === 'male'
            ? `${spacedAccent} his plan`
            : `${spacedAccent} her plan`;
        }
        // Use innerHTML for plan step to render the accent span
        element.innerHTML = text;
        return; // Skip the textContent assignment below
      } else {
        // Replace %{pet_name} placeholder (used in locale strings) with accent styling
        text = text.replace(/\s*%\{pet_name\}\s*/g, spacedAccent);
        // Replace %{pronoun} placeholder (used in calculation step)
        text = text.replace(/%\{pronoun\}/g, pronoun);
      }
      
      // Replace legacy placeholders for backwards compatibility with accent styling
      if (hasPetName) {
        text = text.replace(/\s*\[dogname\]\s*/g, spacedAccent);
      } else {
        text = text.replace(/\[dogname\]/g, displayName);
      }
      text = text.replace(/\[haar\/zijn\]/g, pronoun);
      text = text.replace(/\[zijn\/haar\]/g, pronoun);
      text = text.replace(/\[his\/her\]/g, pronoun);
      // Use innerHTML if we added accent spans, otherwise use textContent
      if (hasPetName || text.includes('<span class="accent')) {
        element.innerHTML = text;
      } else {
        element.textContent = text;
      }
    });
    
    // Update dynamic labels (for checkboxes with pet name placeholders)
    dynamicLabels.forEach(element => {
      // Find the checkbox-text span inside the label
      const textSpan = element.querySelector('.checkbox-text');
      if (!textSpan) return;
      
      // Store the original text if not set, or reset if step changed
      const currentText = textSpan.textContent;
      if (!textSpan.dataset.originalText || element.dataset.lastStep !== step) {
        textSpan.dataset.originalText = currentText;
        element.dataset.lastStep = step;
      }
      
      let text = textSpan.dataset.originalText;
      const pronoun = this.flow.state.getPronoun();
      
      // Replace %{pet_name} placeholder (used in locale strings)
      text = text.replace(/%\{pet_name\}/g, displayName);
      
      // Replace {petname} placeholder (used in allergies checkbox)
      text = text.replace(/\{petname\}/g, displayName);
      
      // Replace legacy placeholders for backwards compatibility
      text = text.replace(/\[dogname\]/g, displayName);
      text = text.replace(/\[haar\/zijn\]/g, pronoun);
      text = text.replace(/\[zijn\/haar\]/g, pronoun);
      text = text.replace(/\[his\/her\]/g, pronoun);
      
      textSpan.textContent = text;
    });
    
    // Update dynamic text elements (for inline messages, branding sections, etc.)
    dynamicTexts.forEach(element => {
      // Store the original text if not set, or reset if step changed
      const currentText = element.textContent;
      if (!element.dataset.originalText || element.dataset.lastStep !== step) {
        element.dataset.originalText = currentText;
        element.dataset.lastStep = step;
      }
      
      let text = element.dataset.originalText;
      const pronoun = this.flow.state.getPronoun();
      const locale = document.documentElement.lang || 'nl';
      
      // Replace %{pet_name} placeholder (used in locale strings)
      text = text.replace(/%\{pet_name\}/g, displayName);
      
      // Replace legacy placeholders for backwards compatibility
      text = text.replace(/\[dogname\]/g, displayName);
      text = text.replace(/\[haar\/zijn\]/g, pronoun);
      text = text.replace(/\[zijn\/haar\]/g, pronoun);
      
      // Gender-specific replacements for spayed/neutered terminology
      if (step === 'spayed' && (gender === 'male' || gender === 'female')) {
        if (locale === 'nl') {
          // Replace "Gecastreerde/Gesteriliseerde" with gender-specific term
          text = text.replace(/Gecastreerde\/Gesteriliseerde/g, gender === 'male' ? 'Gecastreerde' : 'Gesteriliseerde');
          text = text.replace(/gecastreerd\/gesteriliseerd/gi, gender === 'male' ? 'gecastreerd' : 'gesteriliseerd');
        } else {
          // English: "Neutered/Spayed"
          text = text.replace(/Neutered\/Spayed/g, gender === 'male' ? 'Neutered' : 'Spayed');
          text = text.replace(/neutered\/spayed/gi, gender === 'male' ? 'neutered' : 'spayed');
        }
      }
      
      element.textContent = text;
    });
  }


  /**
   * Set up calculation step
   */
  async setupCalculationStep() {
    const calculationStep = this.currentStepElement.querySelector('[data-calculation-step]');
    if (!calculationStep) {
      return;
    }

    // Prevent multiple setups - check if already running
    if (calculationStep.dataset.setupComplete === 'true') {
      return;
    }
    calculationStep.dataset.setupComplete = 'true';

    // Clear any existing timeouts
    this.cleanupCalculationStep();

    // Initialize timeout array if not exists
    if (!this.calculationTimeouts) {
      this.calculationTimeouts = [];
    }

    // Run calculations immediately
    try {
      const calculationResults = this.flow.runCalculations();

      // Update Pet Metaobject with calculation results via backend
      if (this.flow.state.petMetaobjectId && this.flow.state.customerId) {
        try {
          const petData = this.flow.state.getPetData();
          const calculations = this.flow.state.getCalculations();
          const customerData = {
            ownerName: petData.ownerName || '',
            ownerEmail: petData.ownerEmail || '',
            emailConsent: petData.emailConsent !== false
          };
          
          // Update via backend API (will update existing Pet Metaobject)
          const result = await this.flow.shopifyAPI.createCustomerViaBackend(
            customerData,
            petData,
            calculations,
            this.flow.state.petMetaobjectId // Pass existing ID to update
          );
          
          
          // Save updated state to localStorage
          await this.flow.storage.autoSave(this.flow.state.getState(), this.flow.state.customerId);
        } catch (error) {
          console.error('Error updating Pet Metaobject with calculations:', error);
          // Don't block flow - calculations are saved in state
        }
      }

      // Pre-render flavors step in background (fully initialized)
      await this.preRenderFlavorsStep();

      // Preload all calculation step images for smooth transitions
      const imageUrls = [
        'https://cdn.shopify.com/s/files/1/0919/3534/2856/files/negative_e8acc299-ba17-4085-8d42-158adb186911.webp?v=1771332973',
        'https://cdn.shopify.com/s/files/1/0919/3534/2856/files/negative_no_text.webp?v=1770713048',
        'https://cdn.shopify.com/s/files/1/0919/3534/2856/files/positive.webp?v=1770712376',
        'https://cdn.shopify.com/s/files/1/0919/3534/2856/files/positive_no_text.webp?v=1770713048'
      ];
      
      await Promise.all(
        imageUrls.map(url => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = resolve; // Resolve even on error to not block the flow
            img.src = url;
          });
        })
      );

      // Phase 1: Show negative (kibble) image with text, positive without text (0-4s)
      calculationStep.classList.add('image-negative-active');
      calculationStep.classList.remove('image-positive-active', 'fade-out');

      // Animated dots in title: . → .. → … → (empty) → . → .. → … → (empty) → ...
      const dotsSpan = calculationStep.querySelector('[data-calculation-dots]');
      if (dotsSpan) {
        const dotStates = ['.', '..', '…', ''];
        let dotIndex = 0;
        dotsSpan.textContent = dotStates[0];
        this.calculationDotsInterval = setInterval(() => {
          dotIndex = (dotIndex + 1) % dotStates.length;
          dotsSpan.textContent = dotStates[dotIndex];
        }, 450);
      }

      // Phase 2: Switch to positive (salmon) image with blue labels (4-8s)
      const timeout1 = setTimeout(() => {
        calculationStep.classList.remove('image-negative-active');
        calculationStep.classList.add('image-positive-active');
      }, 4000);
      this.calculationTimeouts.push(timeout1);

      // Phase 3: Fade out and reveal flavors (8-9s)
      const timeout2 = setTimeout(() => {
        calculationStep.classList.add('fade-out');
        
        // Show flavors step behind calculation step (only if fully ready)
        const flavorsContainer = document.querySelector('[data-flavors-prerender]');
        if (flavorsContainer && flavorsContainer.dataset.ready === 'true') {
          flavorsContainer.style.opacity = '1';
          flavorsContainer.style.zIndex = '1';
          flavorsContainer.style.pointerEvents = 'auto';
        } else {
          // If not ready yet, wait a bit more
          const checkReady = setInterval(() => {
            if (flavorsContainer && flavorsContainer.dataset.ready === 'true') {
              flavorsContainer.style.opacity = '1';
              flavorsContainer.style.zIndex = '1';
              flavorsContainer.style.pointerEvents = 'auto';
              clearInterval(checkReady);
            }
          }, 100);
          // Stop checking after 2 seconds (fallback)
          setTimeout(() => clearInterval(checkReady), 2000);
        }
      }, 8000);
      this.calculationTimeouts.push(timeout2);

      // Phase 4: Navigate to flavors step (9s) - use pre-rendered content
      const timeout3 = setTimeout(() => {
        // Use normal navigation - renderStep will check for pre-rendered content
        this.flow.navigateNext();
      }, 9000);
      this.calculationTimeouts.push(timeout3);

    } catch (error) {
      console.error('Error running calculations:', error);
      // Show error and allow user to continue
      calculationStep.innerHTML = `<p>Error calculating recommendations. Please try again.</p>`;
    }
  }

  /**
   * Hide quiz UI elements (header, footer, progress bar, offer bar)
   */
  hideQuizUI() {
    const quizContainer = document.querySelector('[data-quiz-container]');
    if (quizContainer) {
      quizContainer.classList.add('calculation-step-active');
    }
  }

  /**
   * Show quiz UI elements (header, footer, progress bar, offer bar)
   */
  showQuizUI() {
    const quizContainer = document.querySelector('[data-quiz-container]');
    if (quizContainer) {
      quizContainer.classList.remove('calculation-step-active');
    }
  }

  /**
   * Pre-render flavors step in background (behind calculation step)
   * Fully initializes the step including async operations
   */
  async preRenderFlavorsStep() {
    // Get flavors step template
    const flavorsTemplate = this.stepTemplates['flavors'];
    if (!flavorsTemplate) {
      return;
    }

    const contentInner = document.querySelector('[data-quiz-content] .content-inner');
    if (!contentInner) {
      return;
    }

    // Create flavors step container behind calculation step
    let flavorsContainer = contentInner.querySelector('[data-flavors-prerender]');
    if (!flavorsContainer) {
      flavorsContainer = document.createElement('div');
      flavorsContainer.setAttribute('data-flavors-prerender', '');
      contentInner.appendChild(flavorsContainer);
    }

    // Render flavors step template
    flavorsContainer.innerHTML = flavorsTemplate;

    // Wait for DOM to be ready
    await new Promise(resolve => setTimeout(resolve, 50));

    const flavorsStepElement = flavorsContainer.querySelector('[data-flavors-step]');
    if (!flavorsStepElement) {
      console.warn('Flavors step element not found in pre-render container');
      return;
    }

    // Temporarily set currentStepElement to flavors container for full setup
    const originalStepElement = this.currentStepElement;
    this.currentStepElement = flavorsContainer;

    try {
      // Set up basic step listeners and state first
      await this.setupStepListeners('flavors', {});
      this.restoreFieldValues('flavors');
      this.updateButtonStates('flavors');
      this.updateProgressBar('flavors');
      this.updateDynamicText('flavors');
      this.updateInlineMessages('flavors');

      // Fully initialize flavors step (includes async product loading)
      // This will load product images, update profile card, check allergies, etc.
      await this.setupFlavorsStep();

      // Mark as ready
      flavorsContainer.dataset.ready = 'true';
    } catch (error) {
      console.error('Error pre-rendering flavors step:', error);
    } finally {
      // Always restore original step element
      this.currentStepElement = originalStepElement;
    }
  }

  /**
   * Clean up calculation step timeouts
   */
  cleanupCalculationStep() {
    if (this.calculationTimeouts && this.calculationTimeouts.length > 0) {
      this.calculationTimeouts.forEach(timeout => {
        clearTimeout(timeout);
      });
      this.calculationTimeouts = [];
    }
    
    if (this.calculationDotsInterval) {
      clearInterval(this.calculationDotsInterval);
      this.calculationDotsInterval = null;
    }
    
    // Reset setup flag if calculation step exists
    const calculationStep = document.querySelector('[data-calculation-step]');
    if (calculationStep) {
      calculationStep.dataset.setupComplete = 'false';
    }
  }

  /**
   * Show comparison screens sequentially
   * @param {HTMLElement} container - Comparison screens container
   */
  showComparisonScreens(container) {
    const screen1 = container.querySelector('[data-comparison-screen="1"]');
    const screen2 = container.querySelector('[data-comparison-screen="2"]');

    // Show screen 1 immediately
    if (screen1) {
      screen1.style.display = 'block';
    }

    // Show screen 2 after 2 seconds
    setTimeout(() => {
      if (screen1) {
        screen1.style.display = 'none';
      }
      if (screen2) {
        screen2.style.display = 'block';
      }
    }, 2000);
  }

  /**
   * Display calculation results
   * @param {HTMLElement} container - Results container
   * @param {Object} results - Calculation results
   */
  displayCalculationResults(container, results) {
    const dailyFoodElement = container.querySelector('[data-result-daily-food]');
    const dailyCaloriesElement = container.querySelector('[data-result-daily-calories]');

    if (dailyFoodElement) {
      dailyFoodElement.textContent = Math.round(results.dailyFoodG);
    }

    if (dailyCaloriesElement) {
      dailyCaloriesElement.textContent = Math.round(results.dailyKcal);
    }
  }

  /**
   * Set up flavors step
   */
  async setupFlavorsStep() {
    // Load product data for all flavors
    await this.loadFlavorProducts();

    // Update dog profile card
    this.updateDogProfileCard();

    // Update dynamic text (for allergy warnings with pet name)
    this.updateDynamicText('flavors');

    // Check for allergy conflicts
    this.checkAllergyConflicts();

    // Set up flavor selection
    this.setupFlavorSelection();

    // Set up Details links to prevent card click interference
    this.setupDetailsLinks();
  }

  /**
   * Load product data for flavors
   */
  async loadFlavorProducts() {
    const config = this.flow.getConfig();
    const flavorCards = this.currentStepElement.querySelectorAll('[data-flavor-card]');

    for (const card of flavorCards) {
      const variantId = card.getAttribute('data-variant-id');
      const flavorId = card.getAttribute('data-flavor-id');

      try {
        const variant = await this.flow.shopifyAPI.getVariant(variantId);
        
        // Update image
        const imageElement = card.querySelector('[data-flavor-image] img');
        if (imageElement && variant.image) {
          imageElement.src = variant.image;
          imageElement.alt = variant.imageAlt || variant.title;
        }

        // Update tagline from metafield
        const taglineElement = card.querySelector('[data-flavor-tagline]');
        if (taglineElement) {
          if (variant.tagline) {
            taglineElement.textContent = variant.tagline;
            taglineElement.style.display = '';
          } else {
            // Hide tagline section if empty/null
            taglineElement.style.display = 'none';
          }
        }

        // Store variant pricing data in state for plan overview calculations
        // Convert from euros (string) to cents (number) for accurate calculations
        if (flavorId && variant.price && variant.currency) {
          const priceInCents = Math.round(Number(variant.price) * 100);
          const compareAtPriceInCents = variant.compareAtPrice 
            ? Math.round(Number(variant.compareAtPrice) * 100)
            : null;

          this.flow.state.variantPricing[flavorId] = {
            price: priceInCents, // Amount in cents (converted from euros)
            compareAtPrice: compareAtPriceInCents, // Original price in cents (if available)
            currency: variant.currency,
            title: variant.title // For flavor name display
          };
        }
      } catch (error) {
        console.error(`Error loading flavor ${flavorId}:`, error);
      }
    }
  }

  /**
   * Update dog profile card
   */
  updateDogProfileCard() {
    const profileCard = this.currentStepElement.querySelector('[data-dog-profile-card]');
    if (!profileCard) return;

    const petData = this.flow.state.getPetData();
    const calculations = this.flow.state.getCalculations();

    const nameElement = profileCard.querySelector('[data-profile-name]');
    const dailyFoodElement = profileCard.querySelector('[data-profile-daily-food]');
    const bagQuantityElement = profileCard.querySelector('[data-profile-bag-quantity]');

    if (nameElement) {
      nameElement.textContent = petData.name || 'Jouw hond';
    }

    if (dailyFoodElement && calculations.dailyFoodG) {
      dailyFoodElement.textContent = this.getLocaleString('quiz.steps.flavors.daily_food_text', {
        grams: Math.round(calculations.dailyFoodG)
      });
    }

    // Update bag quantity text if both bagCount and matchDays are available
    if (bagQuantityElement) {
      const bagCount = calculations.bagCount;
      const matchDays = calculations.matchDays;
      
      if (bagCount && matchDays) {
        const bagQuantityText = this.getLocaleString('quiz.steps.flavors.bag_quantity_text', {
          bag_count: bagCount,
          match_days: matchDays
        });
        bagQuantityElement.textContent = bagQuantityText;
        bagQuantityElement.style.display = 'block';
      } else {
        bagQuantityElement.style.display = 'none';
      }
    }

    // Set up edit button
    const editButton = profileCard.querySelector('[data-profile-edit]');
    const editTextElement = profileCard.querySelector('[data-profile-edit-text]');
    if (editButton && editTextElement) {
      const displayName = petData.name || (document.documentElement.lang?.startsWith('nl') ? 'Jouw hond' : 'your dog');
      editTextElement.textContent = this.getLocaleString('quiz.steps.flavors.edit_profile', { pet_name: displayName });
      editButton.setAttribute('aria-label', editTextElement.textContent);
      editButton.addEventListener('click', () => {
        const metaobjectId = this.flow.state.petMetaobjectId;
        if (metaobjectId) {
          this.flow.router.navigate('profile', { pet: metaobjectId });
        }
      });
    }
  }

  /**
   * Canonical allergy tokens: rund, kip, zalm, wit_kleine_vis (lowercase).
   * Maps legacy quiz/API values (Gevogelte, Witvis, Rund, etc.) for backwards compatibility.
   * @param {Array<string>} allergies
   * @returns {string[]}
   */
  normalizePetAllergies(allergies) {
    if (!Array.isArray(allergies)) return [];
    const out = [];
    const seen = new Set();
    allergies.forEach(raw => {
      const s = String(raw ?? '').trim();
      if (!s) return;
      const k = s.toLowerCase();
      let c = null;
      if (k === 'gevogelte' || k === 'poultry' || k === 'chicken' || k === 'kip') c = 'kip';
      else if (k === 'rund' || k === 'beef') c = 'rund';
      else if (k === 'witvis' || k === 'whitefish' || k === 'vis' || k === 'wit_kleine_vis') c = 'wit_kleine_vis';
      else if (k === 'zalm' || k === 'salmon') c = 'zalm';
      else if (k === 'lam' || k === 'lamb') c = 'lam';
      else if (k === 'eend' || k === 'duck') c = 'eend';
      if (c && !seen.has(c)) {
        seen.add(c);
        out.push(c);
      }
    });
    return out;
  }

  /**
   * Flavor IDs (rund/kip/vis) excluded when this canonical allergy is present. Salmon excludes none.
   */
  canonicalAllergyToFlavorId(token) {
    if (token === 'rund') return 'rund';
    if (token === 'kip') return 'kip';
    if (token === 'wit_kleine_vis') return 'vis';
    return null;
  }

  /**
   * The three allergies that map to product flavors; all three → no plan possible.
   */
  static get ACTIVE_PRODUCT_ALLERGIES() {
    return ['rund', 'kip', 'wit_kleine_vis'];
  }

  /**
   * Get allergic flavor IDs from allergies array
   * @param {Array<string>} allergies - Array of allergy strings
   * @returns {Array<string>} Array of flavor IDs that match allergies
   */
  getAllergicFlavorIds(allergies) {
    const canon = this.normalizePetAllergies(allergies || []);
    const allergicFlavorIds = [];
    canon.forEach(token => {
      const flavorId = this.canonicalAllergyToFlavorId(token);
      if (flavorId && !allergicFlavorIds.includes(flavorId)) {
        allergicFlavorIds.push(flavorId);
      }
    });
    return allergicFlavorIds;
  }

  /**
   * Check for allergy conflicts
   */
  checkAllergyConflicts() {
    const petData = this.flow.state.getPetData();
    const allergies = this.normalizePetAllergies(petData.allergies || []);

    allergies.forEach(token => {
      const flavorId = this.canonicalAllergyToFlavorId(token);
      if (flavorId) {
        const message = this.currentStepElement.querySelector(`[data-conditional="allergy-${flavorId}"]`);
        if (message) {
          message.style.display = 'flex';
        }
      }
    });
  }

  /**
   * Set up flavor selection
   */
  setupFlavorSelection() {
    const checkboxes = this.currentStepElement.querySelectorAll('[data-flavor-checkbox]');
    const selectedFlavors = this.flow.state.selectedFlavors || [];

    // Pre-select flavors based on priority and bag count
    if (selectedFlavors.length === 0) {
      const calculations = this.flow.state.getCalculations();
      const petData = this.flow.state.getPetData();
      const allergies = petData.allergies || [];
      
      // Get allergic flavor IDs to exclude from preselection
      const allergicFlavorIds = this.getAllergicFlavorIds(allergies);
      
      const priorityFlavors = FlavorDistribution.getFlavorDistributionOrder();

      // Filter out allergic flavors from priority list
      const availableFlavors = priorityFlavors.filter(flavorId => !allergicFlavorIds.includes(flavorId));
      
      // Use bagCount (main plan) - align preselection with distribution mechanism
      const bagCount = calculations.bagCount || 1;
      const maxFlavors = Math.min(bagCount, 3, availableFlavors.length);
      
      // Pre-select from available (non-allergic) flavors
      // If all flavors are allergic, leave it empty (user must manually select)
      for (let i = 0; i < maxFlavors && i < availableFlavors.length; i++) {
        selectedFlavors.push(availableFlavors[i]);
        const checkbox = this.currentStepElement.querySelector(`#flavor-${availableFlavors[i]}`);
        if (checkbox) {
          checkbox.checked = true;
        }
      }
      
      this.flow.state.selectedFlavors = selectedFlavors;
    } else {
      // Restore selected flavors
      selectedFlavors.forEach(flavorId => {
        const checkbox = this.currentStepElement.querySelector(`#flavor-${flavorId}`);
        if (checkbox) {
          checkbox.checked = true;
        }
      });
    }

    // Update badge visibility for all flavors
    this.updateFlavorBadges();

    // Handle flavor selection changes
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const flavorId = checkbox.value;
        const selectedFlavors = this.flow.state.selectedFlavors || [];

        if (checkbox.checked) {
          if (!selectedFlavors.includes(flavorId)) {
            selectedFlavors.push(flavorId);
          }
        } else {
          const index = selectedFlavors.indexOf(flavorId);
          if (index > -1) {
            selectedFlavors.splice(index, 1);
          }
        }

        this.flow.state.selectedFlavors = selectedFlavors;
        this.updateButtonStates('flavors');
        this.updateFlavorBadges();
      });
    });

    this.updateButtonStates('flavors');
  }

  /**
   * Update flavor badge visibility based on selection state
   */
  updateFlavorBadges() {
    const checkboxes = this.currentStepElement.querySelectorAll('[data-flavor-checkbox]');
    
    checkboxes.forEach(checkbox => {
      const flavorId = checkbox.value;
      const flavorCard = this.currentStepElement.querySelector(`[data-flavor-id="${flavorId}"]`);
      
      if (flavorCard) {
        const badge = flavorCard.querySelector('.flavor-card-badge');
        if (badge) {
          badge.style.display = checkbox.checked ? 'flex' : 'none';
        }
      }
    });
  }

  /**
   * Set up Details links to prevent card click interference
   */
  setupDetailsLinks() {
    const detailsLinks = this.currentStepElement.querySelectorAll('.flavor-card-details-link');
    detailsLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        const value = link.getAttribute('data-open-flavor-modal');
        if (value) {
          const modal = document.querySelector('.flavor-modal-' + value);
          if (modal) modal.dataset.state = 'open';
        }
      });
    });
  }

  /**
   * Format future date for delivery display
   * @param {number} matchDays - Number of days to add
   * @param {string} locale - Locale code (e.g., 'nl-NL')
   * @returns {string} Formatted date like "6 jan. 2026"
   */
  formatFutureDate(matchDays, locale = 'nl-NL') {
    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + matchDays);

    // Format using locale
    const formatted = futureDate.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

    // Add period after month abbreviation if not already present
    // toLocaleDateString might return "6 jan 2026" or "6 jan. 2026" depending on locale
    // We want "6 jan. 2026" format
    const parts = formatted.split(' ');
    if (parts.length >= 3) {
      const month = parts[1];
      if (!month.endsWith('.')) {
        parts[1] = month + '.';
      }
      return parts.join(' ');
    }

    return formatted;
  }

  /**
   * Format money amount using shared formatMoney helper
   * @param {number} amountInCents - Amount in cents
   * @param {string} currencyCode - Currency code (e.g., 'EUR', 'PLN')
   * @param {string} locale - Locale code (e.g., 'nl-NL', 'pl-PL')
   * @returns {string} Formatted price string
   */
  formatMoney(amountInCents, currencyCode = 'EUR', locale = null) {
    const amountInCurrency = amountInCents / 100;
    const currentLocale = locale || window.__wizard1CurrentLocaleFull || window.__wizard1CurrentLocale || 'nl-NL';
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(amountInCurrency, currencyCode, currentLocale);
    }
    return new Intl.NumberFormat(currentLocale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amountInCurrency);
  }

  /**
   * Get locale string with variable substitution
   * @param {string} key - Locale key (e.g., 'quiz.steps.bulk.annual_savings')
   * @param {Object} variables - Variables to substitute (e.g., { amount: '€60,77' })
   * @returns {string} Localized string
   */
  getLocaleString(key, variables = {}) {
    try {
      // First try to get from a data attribute on a hidden element (Shopify's translation system)
      let translationElement = document.querySelector(`[data-translate="${key}"]`);
      if (!translationElement) {
        // Try to find in the current step element
        translationElement = this.currentStepElement?.querySelector(`[data-translate="${key}"]`);
      }
      
      if (translationElement) {
        let value = translationElement.textContent || translationElement.getAttribute('data-translate-value') || key;
        // Substitute variables (e.g., %{amount} -> variables.amount)
        if (typeof value === 'string' && variables) {
          Object.keys(variables).forEach(varKey => {
            value = value.replace(new RegExp(`%\\{${varKey}\\}`, 'g'), variables[varKey]);
          });
        }
        return value;
      }
      
      // Fallback: try to get from window.Shopify.locales if available
      const locale = window.__wizard1CurrentLocale || window.__wizard2CurrentLocale || 'nl';
      const localeCode = locale.split('-')[0];
      const translations = window.Shopify?.locales?.[localeCode] || window.Shopify?.locales?.['en'] || {};
      
      if (Object.keys(translations).length > 0) {
        const keys = key.split('.');
        let value = translations;
        for (const k of keys) {
          value = value?.[k];
          if (value === undefined) break;
        }
        
        if (typeof value === 'string') {
          // Substitute variables
          if (variables) {
            Object.keys(variables).forEach(varKey => {
              value = value.replace(new RegExp(`%\\{${varKey}\\}`, 'g'), variables[varKey]);
            });
          }
          return value;
        }
      }
      
      // Final fallback: return key
      return key;
    } catch (error) {
      console.warn('Error getting locale string:', error);
      return key;
    }
  }

  /**
   * Get translated flavor name for display (e.g. "Chicken" / "Kip")
   * Uses quiz.steps.flavors.flavor_{flavorId} to always show correct locale.
   * @param {string} flavorId - Flavor id (rund, kip, vis)
   * @returns {string} Translated flavor name
   */
  getTranslatedFlavorName(flavorId) {
    return this.getLocaleString('quiz.steps.flavors.flavor_' + flavorId);
  }

  /**
   * Format cart note with owner name and pet names
   * @param {string} ownerName - Owner's first name
   * @param {Array<string>} petNames - Array of unique pet names
   * @returns {string} Formatted cart note string
   */
  formatCartNote(ownerName, petNames) {
    if (!ownerName || !ownerName.trim()) {
      return '';
    }

    // Get unique pet names (remove duplicates and empty values)
    const uniquePetNames = [...new Set(petNames)].filter(name => name && name.trim());

    // If no pets, return just the owner name
    if (uniquePetNames.length === 0) {
      return ownerName.trim();
    }

    // Fulfillment center prints in English only
    const connector = 'and';

    // Format based on number of pets
    if (uniquePetNames.length === 1) {
      return `${ownerName.trim()} ${connector} ${uniquePetNames[0].trim()}`;
    } else {
      const allButLast = uniquePetNames.slice(0, -1).map(name => name.trim()).join(', ');
      const lastPet = uniquePetNames[uniquePetNames.length - 1].trim();
      return `${ownerName.trim()}, ${allButLast} ${connector} ${lastPet}`;
    }
  }

  /**
   * Build line item properties for pet name: hidden `_pet_name` + optional customer-visible label key.
   * @param {string} petName
   * @param {string} [visiblePropertyLabel] - localized key text shown in cart (e.g. Meal plan for / Trial for)
   * @returns {Object<string, string>}
   */
  buildPetLineItemProperties(petName, visiblePropertyLabel) {
    const name = String(petName || '').trim();
    if (!name) return {};
    const out = { _pet_name: name };
    const label = visiblePropertyLabel != null ? String(visiblePropertyLabel).trim() : '';
    if (label) out[label] = name;
    return out;
  }

  /**
   * Visible cart line property label for Wizard 1 (full subscription from bulk step).
   */
  getWizard1MealPlanLinePropertyLabel() {
    const fromTheme =
      typeof window !== 'undefined' && window.__wizard1CartLinePropertyLabels?.meal_plan_for != null
        ? String(window.__wizard1CartLinePropertyLabels.meal_plan_for).trim()
        : '';
    if (fromTheme) return fromTheme;
    const raw =
      (typeof window !== 'undefined' && window.__wizard1CurrentLocale) ||
      (typeof window !== 'undefined' && window.__wizard2CurrentLocale) ||
      (typeof document !== 'undefined' && document.documentElement?.getAttribute?.('lang')) ||
      'en';
    const primary = String(raw).split('-')[0].toLowerCase();
    return primary === 'nl' ? 'Voedingsplan voor' : 'Meal plan for';
  }

  /**
   * Read pet name from cart/order line properties (canonical + legacy).
   * @param {Object} [properties]
   * @returns {string}
   */
  getPetNameFromLineItemProperties(properties) {
    if (typeof window !== 'undefined' && typeof window.getPetNameFromLineItemProperties === 'function') {
      return window.getPetNameFromLineItemProperties(properties);
    }
    if (!properties || typeof properties !== 'object') return '';
    const raw = properties._pet_name ?? properties['🐶'] ?? properties.pet_name;
    if (raw == null) return '';
    return String(raw).trim();
  }

  /**
   * Calculate plan totals with all discounts applied
   * @returns {Object} Pricing data with totals
   */
  calculatePlanTotals() {
    const selectedFlavors = this.flow.state.selectedFlavors || [];
    const calculations = this.flow.state.getCalculations();
    const bagCount = calculations.bagCount || 0;
    const variantPricing = this.flow.state.variantPricing || {};

    if (selectedFlavors.length === 0 || bagCount === 0) {
      return {
        currency: 'EUR',
        totalItems: 0,
        rawTotal: 0,
        volumeDiscountPercent: 0,
        totalFirstOrder: 0,
        totalRecurring: 0
      };
    }

    // Get flavor distribution
    const distribution = this.flow.calculationEngine.calculateFlavorDistribution(
      bagCount,
      selectedFlavors
    );

    // Calculate raw total: sum of (variant price × quantity) for each flavor
    // Use compareAtPrice if available (for strikethrough display), otherwise use regular price
    let rawTotal = 0;
    let compareAtTotal = 0; // For strikethrough price display
    let totalItems = 0;
    let currency = 'EUR';

    FlavorDistribution.forEachFlavorInDistributionOrder(distribution, (flavorId, quantity) => {
      const variant = variantPricing[flavorId];

      if (variant && variant.price) {
        // Use regular price for discount calculations
        rawTotal += variant.price * quantity;
        
        // Use compareAtPrice for strikethrough if available, otherwise use regular price
        const priceForCompare = variant.compareAtPrice || variant.price;
        compareAtTotal += priceForCompare * quantity;
        
        totalItems += quantity;
        currency = variant.currency || currency;
      }
    });

    const volumeDiscountPercent = Math.min(20, Math.max(0, totalItems - 1));

    // Per-unit: subscription 10% rounded, then volume discount, then sum (matches Shopify)
    let totalFirstOrder = 0;
    FlavorDistribution.forEachFlavorInDistributionOrder(distribution, (flavorId, quantity) => {
      const variant = variantPricing[flavorId];

      if (variant && variant.price) {
        const subscriptionPricePerUnit = Math.round(variant.price * 0.9);
        const volumeDiscountedPricePerUnit = Math.round(
          subscriptionPricePerUnit * (1 - volumeDiscountPercent / 100)
        );
        totalFirstOrder += quantity * volumeDiscountedPricePerUnit;
      }
    });

    const totalRecurring = totalFirstOrder;

    return {
      currency,
      totalItems,
      rawTotal: Math.round(rawTotal),
      compareAtTotal: Math.round(compareAtTotal),
      volumeDiscountPercent,
      totalFirstOrder: Math.round(totalFirstOrder),
      totalRecurring: Math.round(totalRecurring),
      distribution // Include for flavor text formatting
    };
  }

  /**
   * Set up plan step
   */
  async setupPlanStep() {
    // Ensure variantPricing is loaded for all selected flavors
    const selectedFlavors = this.flow.state.selectedFlavors || [];
    const variantPricing = this.flow.state.variantPricing || {};
    
    // Check if all selected flavors have pricing data
    const missingPricing = selectedFlavors.some(flavorId => !variantPricing[flavorId]);
    
    if (missingPricing && selectedFlavors.length > 0) {
      try {
        await this.loadFlavorProducts();
      } catch (error) {
        console.error('Error loading flavor products:', error);
        // Redirect to flavors step if loading fails
        this.flow.router.navigate('flavors', {}, true);
        return;
      }
    }

    // Populate selling plan names from Liquid-injected data
    this.populateSellingPlanNamesFromLiquid();

    // Ensure footer is clean before setting up plan step
    // Remove any bulk elements that might be leftover
    this.resetFooter();

    this.updatePlanOverview();
    this.updatePlanPricing();

    // Show plan-price-footer
    const planPriceFooter = document.querySelector('[data-plan-price-footer]');
    if (planPriceFooter) {
      planPriceFooter.style.display = 'flex';
    }

    // Move plan-overview Instant section into slot (visible only on plan step)
    this.ensurePlanOverviewShopifySectionInSlotVisible();

  }

  /**
   * Update plan overview
   */
  updatePlanOverview() {
    const petData = this.flow.state.getPetData();
    const calculations = this.flow.state.getCalculations();
    const selectedFlavors = this.flow.state.selectedFlavors || [];
    const variantPricing = this.flow.state.variantPricing || {};
    const matchDays = calculations.matchDays || 0;
    const bagCount = calculations.bagCount || 0;
    const locale = window.__wizard1CurrentLocale || 'nl-NL';

    // Title is already updated by updateDynamicText() with pet name and pronoun
    // No additional highlighting needed - pet name should match the rest of the title

    // Get flavor distribution
    const distribution = this.flow.calculationEngine.calculateFlavorDistribution(
      bagCount,
      selectedFlavors
    );

    // Format flavor distribution text
    const formatFlavorDistribution = (dist) => {
      const parts = [];
      FlavorDistribution.forEachFlavorInDistributionOrder(dist, (flavorId, quantity) => {
        const variant = variantPricing[flavorId];
        if (variant && quantity > 0) {
          parts.push(`${quantity}x ${this.getTranslatedFlavorName(flavorId)}`);
        }
      });
      
      if (parts.length > 0 && matchDays > 0) {
        const flavorText = parts.join(', ');
        // Format: "3x Rund, 2x Kip (genoeg voor 26 dagen)"
        const daysText = locale === 'nl' || locale === 'nl-NL' 
          ? `(genoeg voor ${matchDays} dagen)`
          : `(enough for ${matchDays} days)`;
        return `${flavorText} ${daysText}`;
      }
      return '';
    };

    const flavorDistributionText = formatFlavorDistribution(distribution);

    // Calculate effective discount (subscription 10% + volume) for both delivery cards
    const totals = this.calculatePlanTotals();
    const effectiveDiscountPercent = totals.rawTotal > 0
      ? Math.round((1 - totals.totalFirstOrder / totals.rawTotal) * 100)
      : 0;
    const discountText = `-${effectiveDiscountPercent}%`;

    // Update Levering 1 card
    const delivery1FlavorElement = this.currentStepElement.querySelector('[data-delivery-1-flavor-distribution]');
    if (delivery1FlavorElement) {
      delivery1FlavorElement.textContent = flavorDistributionText;
    }
    const delivery1DiscountElement = this.currentStepElement.querySelector('[data-delivery-1-discount]');
    if (delivery1DiscountElement) {
      delivery1DiscountElement.textContent = discountText;
    }

    // Update Levering 2 card
    const delivery2FlavorElement = this.currentStepElement.querySelector('[data-delivery-2-flavor-distribution]');
    if (delivery2FlavorElement) {
      delivery2FlavorElement.textContent = flavorDistributionText;
    }
    const delivery2DiscountElement = this.currentStepElement.querySelector('[data-delivery-2-discount]');
    if (delivery2DiscountElement) {
      delivery2DiscountElement.textContent = discountText;
    }

    // Update Levering 2 date
    const delivery2DateElement = this.currentStepElement.querySelector('[data-delivery-2-date]');
    if (delivery2DateElement && matchDays > 0) {
      const futureDate = this.formatFutureDate(matchDays, locale);
      delivery2DateElement.textContent = futureDate;
    }

    // Update reminder sections
    const delivery1InDaysElement = this.currentStepElement.querySelector('[data-delivery-1-in-days]');
    if (delivery1InDaysElement && matchDays > 0) {
      const inDaysText = locale === 'nl' || locale === 'nl-NL'
        ? `Over ${matchDays} dagen`
        : `In ${matchDays} days`;
      delivery1InDaysElement.textContent = inDaysText;
    }

    const recurringEveryElement = this.currentStepElement.querySelector('[data-recurring-every]');
    if (recurringEveryElement && matchDays > 0) {
      const repeatsText = locale === 'nl' || locale === 'nl-NL'
        ? `Herhaalt elke ${matchDays} dagen`
        : `Repeats every ${matchDays} days`;
      recurringEveryElement.textContent = repeatsText;
    }
  }

  /**
   * Update plan pricing
   */
  async updatePlanPricing() {
    const calculations = this.flow.state.getCalculations();
    const matchDays = calculations.matchDays || 0;

    if (matchDays === 0) {
      return;
    }

    // Calculate plan totals
    const totals = this.calculatePlanTotals();

    if (totals.totalItems === 0 || totals.totalFirstOrder === 0) {
      return;
    }

    const locale = window.__wizard1CurrentLocale || 'nl-NL';
    const currency = totals.currency || 'EUR';

    // Get price display elements from quiz-footer (plan-price-footer is now in quiz-footer)
    const planPriceFooter = document.querySelector('[data-plan-price-footer]');
    const priceCurrent = planPriceFooter ? planPriceFooter.querySelector('[data-price-current]') : null;
    const priceCompare = planPriceFooter ? planPriceFooter.querySelector('[data-price-compare]') : null;
    const periodSelector = planPriceFooter ? planPriceFooter.querySelector('[data-price-period-selector]') : null;

    // Calculate per-period prices for first order (Levering 1)
    const pricePerDay = totals.totalFirstOrder / matchDays;
    const pricePerWeek = pricePerDay * 7;
    const pricePerShipment = totals.totalFirstOrder;

    // Function to update displayed price based on selected period
    const updatePriceDisplay = (period = 'day') => {
      let displayPrice = 0;
      let priceInCents = 0;

      switch (period) {
        case 'week':
          priceInCents = Math.round(pricePerWeek);
          displayPrice = this.formatMoney(priceInCents, currency, locale);
          break;
        case 'shipment':
          priceInCents = totals.totalFirstOrder;
          displayPrice = this.formatMoney(priceInCents, currency, locale);
          break;
        case 'day':
        default:
          priceInCents = Math.round(pricePerDay);
          displayPrice = this.formatMoney(priceInCents, currency, locale);
          break;
      }

      // Update price display
      if (priceCurrent) {
        priceCurrent.textContent = displayPrice;
      }

      // Update compare price (original price - use compareAtTotal if available)
      if (priceCompare) {
        // Use compareAtTotal (original price) if available, otherwise use rawTotal
        const originalTotal = totals.compareAtTotal || totals.rawTotal;
        
        // Calculate original price for the same period
        const originalPerDay = originalTotal / matchDays;
        let originalPriceInCents = 0;

        switch (period) {
          case 'week':
            originalPriceInCents = Math.round(originalPerDay * 7);
            break;
          case 'shipment':
            originalPriceInCents = originalTotal;
            break;
          case 'day':
          default:
            originalPriceInCents = Math.round(originalPerDay);
            break;
        }

        const originalPriceFormatted = this.formatMoney(originalPriceInCents, currency, locale);
        priceCompare.textContent = originalPriceFormatted;
      }
    };

    // Set initial price display (sync with state)
    const initialPeriod = this.flow.state.selectedPricePeriod || periodSelector?.value || 'day';
    this.flow.state.selectedPricePeriod = initialPeriod;
    if (periodSelector) {
      periodSelector.value = initialPeriod;
    }
    updatePriceDisplay(initialPeriod);

    // Add change handler for period selector (sync with state and bulk step)
    if (periodSelector) {
      periodSelector.addEventListener('change', (e) => {
        this.flow.state.selectedPricePeriod = e.target.value;
        updatePriceDisplay(e.target.value);
      });
    }
  }

  /**
   * Get bulk flavor distribution (ratio-preserving: base × multiplier)
   * Preserves the 1x distribution ratio when applying 2x/3x multipliers.
   * @param {number} multiplier - Multiplier (1, 2, or 3)
   * @returns {Object} Distribution of bags per flavor
   */
  getBulkDistribution(multiplier = 1) {
    const selectedFlavors = this.flow.state.selectedFlavors || [];
    const calculations = this.flow.state.getCalculations();
    const baseBagCount = calculations.bagCount || 0;

    if (selectedFlavors.length === 0 || baseBagCount === 0) {
      return {};
    }

    const baseDistribution = this.flow.calculationEngine.calculateFlavorDistribution(
      baseBagCount,
      selectedFlavors
    );

    if (multiplier <= 1) {
      return baseDistribution;
    }

    const result = {};
    Object.keys(baseDistribution).forEach(flavorId => {
      result[flavorId] = baseDistribution[flavorId] * multiplier;
    });
    return result;
  }

  /**
   * Calculate bulk totals with multiplier
   * @param {number} multiplier - Multiplier (1, 2, or 3)
   * @returns {Object} Pricing data with totals
   */
  calculateBulkTotals(multiplier = 1) {
    const selectedFlavors = this.flow.state.selectedFlavors || [];
    const calculations = this.flow.state.getCalculations();
    const baseBagCount = calculations.bagCount || 0;
    const variantPricing = this.flow.state.variantPricing || {};
    const distribution = this.getBulkDistribution(multiplier);
    const totalItems = Object.values(distribution).reduce((sum, qty) => sum + qty, 0);

    if (selectedFlavors.length === 0 || totalItems === 0) {
      return {
        currency: 'EUR',
        totalItems: 0,
        rawTotal: 0,
        compareAtTotal: 0,
        volumeDiscountPercent: 0,
        totalFirstOrder: 0,
        totalRecurring: 0,
        distribution: {}
      };
    }

    // Shopify/Firmhouse pricing: subscription 10% first (rounded per unit), then volume discount on that
    let rawTotal = 0;
    let compareAtTotal = 0;
    let currency = 'EUR';

    FlavorDistribution.forEachFlavorInDistributionOrder(distribution, (flavorId, quantity) => {
      const variant = variantPricing[flavorId];

      if (variant && variant.price) {
        rawTotal += variant.price * quantity;
        const priceForCompare = variant.compareAtPrice || variant.price;
        compareAtTotal += priceForCompare * quantity;
        currency = variant.currency || currency;
      }
    });

    const volumeDiscountPercent = Math.min(20, Math.max(0, totalItems - 1));

    // Per-unit: subscription 10% rounded, then volume discount, then sum (matches Shopify)
    let totalFirstOrder = 0;
    FlavorDistribution.forEachFlavorInDistributionOrder(distribution, (flavorId, quantity) => {
      const variant = variantPricing[flavorId];

      if (variant && variant.price) {
        const subscriptionPricePerUnit = Math.round(variant.price * 0.9);
        const volumeDiscountedPricePerUnit = Math.round(
          subscriptionPricePerUnit * (1 - volumeDiscountPercent / 100)
        );
        totalFirstOrder += quantity * volumeDiscountedPricePerUnit;
      }
    });

    const totalRecurring = totalFirstOrder;

    return {
      currency,
      totalItems,
      rawTotal: Math.round(rawTotal),
      compareAtTotal: Math.round(compareAtTotal),
      volumeDiscountPercent,
      totalFirstOrder: Math.round(totalFirstOrder),
      totalRecurring: Math.round(totalRecurring),
      distribution
    };
  }

  /**
   * Format flavor quantities text
   * @param {Object} distribution - Flavor distribution object
   * @param {Object} variantPricing - Variant pricing object
   * @returns {string} Formatted text like "3x Rund, 2x Kip"
   */
  formatFlavorQuantities(distribution, variantPricing) {
    const parts = [];
    FlavorDistribution.forEachFlavorInDistributionOrder(distribution, (flavorId, quantity) => {
      const variant = variantPricing[flavorId];
      if (variant && quantity > 0) {
        parts.push(`${quantity}x ${this.getTranslatedFlavorName(flavorId)}`);
      }
    });
    return parts.join(', ');
  }

  /**
   * Calculate annual savings
   * @param {number} basePrice - Base price (1x multiplier)
   * @param {number} bulkPrice - Bulk price (with multiplier)
   * @param {number} matchDays - Match days for base frequency
   * @returns {number} Annual savings in cents
   */
  calculateAnnualSavings(basePrice, bulkPrice, matchDays, multiplier = 1) {
    if (!matchDays || matchDays === 0) return 0;
    
    // Calculate daily prices
    const dailySubscriptionPrice = basePrice / matchDays; // 1x daily price
    const dailyBulkPrice = bulkPrice / (matchDays * multiplier); // Bulk daily price
    
    // Calculate annual savings: (daily subscription - daily bulk) * 365
    const dailySavings = dailySubscriptionPrice - dailyBulkPrice;
    const annualSavings = dailySavings * 365;
    
    return Math.round(annualSavings);
  }

  /**
   * Set up bulk step
   */
  async setupBulkStep() {
    // Ensure variantPricing is loaded for all selected flavors
    const selectedFlavors = this.flow.state.selectedFlavors || [];
    const variantPricing = this.flow.state.variantPricing || {};
    
    // Check if all selected flavors have pricing data
    const missingPricing = selectedFlavors.some(flavorId => !variantPricing[flavorId]);
    
    if (missingPricing && selectedFlavors.length > 0) {
      try {
        await this.loadFlavorProducts();
      } catch (error) {
        console.error('Error loading flavor products:', error);
        // Redirect to flavors step if loading fails
        this.flow.router.navigate('flavors', {}, true);
        return;
      }
    }

    // Populate selling plan names from Liquid-injected data
    this.populateSellingPlanNamesFromLiquid();

    // Ensure footer is clean before setting up bulk step
    this.resetFooter();

    // Hide plan-price-footer (only show bulk-price-footer on bulk step)
    const planPriceFooter = document.querySelector('[data-plan-price-footer]');
    if (planPriceFooter) {
      planPriceFooter.style.display = 'none';
    }

    // Move price footer and "add another dog" link into quiz-footer
    this.moveBulkFooterElements();

    // Replace Terug/Doorgaan buttons with full-width add to cart button
    this.setupBulkAddToCartButton();

    // Update all displays
    this.updateBulkOptions();
    await this.updateBulkPricing();

    // Set up bulk selection
    const radios = this.currentStepElement.querySelectorAll('[data-bulk-radio]');
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        const multiplier = parseInt(radio.value);
        this.flow.state.selectedPlan = multiplier.toString() + 'x';
        this.updateBulkPricing();
      });
    });

    // Set up price period selector sync
    this.setupBulkPricePeriodSync();
  }

  /**
   * Populate selling plan names from Liquid-injected window.__wizard1SellingPlanNames
   */
  populateSellingPlanNamesFromLiquid() {
    const liquidNames = window.__wizard1SellingPlanNames || {};
    if (Object.keys(liquidNames).length > 0) {
      this.flow.state.sellingPlanNames = { ...this.flow.state.sellingPlanNames, ...liquidNames };
    }
  }

  /**
   * Set up price period selector synchronization
   */
  setupBulkPricePeriodSync() {
    // Note: price footer is moved to quiz-footer, so search in document
    let periodSelector = document.querySelector('[data-bulk-price-footer] [data-price-period-selector]');
    const cardSelectors = this.currentStepElement.querySelectorAll('[data-bulk-price-period-selector-1], [data-bulk-price-period-selector-2], [data-bulk-price-period-selector-3]');

    // Sync from state
    const currentPeriod = this.flow.state.selectedPricePeriod || 'day';
    if (periodSelector) {
      periodSelector.value = currentPeriod;
    }
    cardSelectors.forEach(selector => {
      selector.value = currentPeriod;
    });

    // Update function - gets fresh selector reference each time
    const updatePeriod = (period) => {
      this.flow.state.selectedPricePeriod = period;
      
      // Get fresh reference to footer selector (in case it was recreated)
      const footerSelector = document.querySelector('[data-bulk-price-footer] [data-price-period-selector]');
      if (footerSelector) {
        footerSelector.value = period;
      }
      
      // Update all card selectors
      const allCardSelectors = this.currentStepElement.querySelectorAll('[data-bulk-price-period-selector-1], [data-bulk-price-period-selector-2], [data-bulk-price-period-selector-3]');
      allCardSelectors.forEach(selector => {
        selector.value = period;
      });
      
      this.updateBulkPricing();
    };

    // Footer selector change
    if (periodSelector) {
      // Remove old listeners by cloning, but preserve the current value
      const currentValue = periodSelector.value;
      const newSelector = periodSelector.cloneNode(true);
      newSelector.value = currentValue; // Preserve the value after cloning
      periodSelector.parentNode.replaceChild(newSelector, periodSelector);
      periodSelector = newSelector; // Update reference
      newSelector.addEventListener('change', (e) => {
        updatePeriod(e.target.value);
      });
    }

    // Card selectors change - get fresh references each time to ensure sync
    cardSelectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        updatePeriod(e.target.value);
      });
    });
    
    // Also listen for changes from the flavors/plan step selector
    // This ensures sync when user changes the selector in the flavors step
    const planStepSelector = document.querySelector('[data-plan-price-footer] [data-price-period-selector]');
    if (planStepSelector) {
      planStepSelector.addEventListener('change', (e) => {
        updatePeriod(e.target.value);
      });
    }
  }

  /**
   * Update bulk options
   */
  updateBulkOptions() {
    const selectedPlan = this.flow.state.selectedPlan || '1x';
    const multiplier = parseInt(selectedPlan.replace('x', '')) || 1;
    const radio = this.currentStepElement.querySelector(`#bulk-${multiplier}x`);
    if (radio) {
      radio.checked = true;
    }

    // Update frequency labels
    const calculations = this.flow.state.getCalculations();
    const matchDays = calculations.matchDays;
    if (matchDays) {
      const config = this.flow.getConfig();
      [1, 2, 3].forEach(mult => {
        const frequencyDays = matchDays * mult;
        const sellingPlanId = config.sellingPlanMapping[frequencyDays.toString()];
        const frequencyElement = this.currentStepElement.querySelector(`[data-bulk-frequency-${mult}]`);
        
        if (frequencyElement) {
          if (sellingPlanId && this.flow.state.sellingPlanNames[sellingPlanId]) {
            frequencyElement.textContent = this.flow.state.sellingPlanNames[sellingPlanId];
          } else {
            // Fallback to "Elke X dagen"
            frequencyElement.textContent = `Elke ${frequencyDays} dagen`;
          }
        }
      });
    }

    // Update flavor quantities
    // Calculate 1x distribution first, then multiply for 2x and 3x
    const variantPricing = this.flow.state.variantPricing || {};
    const baseTotals = this.calculateBulkTotals(1);
    const baseDistribution = baseTotals.distribution;
    
    [1, 2, 3].forEach(mult => {
      const quantitiesElement = this.currentStepElement.querySelector(`[data-bulk-quantities-${mult}]`);
      if (quantitiesElement) {
        if (mult === 1) {
          // Use base distribution for 1x
          quantitiesElement.textContent = this.formatFlavorQuantities(baseDistribution, variantPricing);
        } else {
          // Multiply base distribution for 2x and 3x
          const multipliedDistribution = {};
          Object.keys(baseDistribution).forEach(flavorId => {
            multipliedDistribution[flavorId] = baseDistribution[flavorId] * mult;
          });
          quantitiesElement.textContent = this.formatFlavorQuantities(multipliedDistribution, variantPricing);
        }
      }
    });
  }

  /**
   * Update bulk step add to cart button with shipment totals
   */
  updateBulkAddToCartButton(selectedMultiplier, baseTotals, selectedTotals, currency, locale) {
    const addToCartButton = document.querySelector('.quiz-footer .bulk-add-to-cart-button');
    if (!addToCartButton) return;

    // Always use shipment totals (not affected by period selector)
    const comparePrice = selectedTotals.compareAtTotal;
    let currentPrice = 0;
    
    if (selectedMultiplier === 1) {
      // 1x: compare + current
      currentPrice = baseTotals.totalFirstOrder;
    } else {
      // 2x/3x: compare + bulk
      currentPrice = selectedTotals.totalFirstOrder;
    }

    const buttonText = addToCartButton.querySelector('.button-text');
    const buttonPrice = addToCartButton.querySelector('.button-price');

    if (buttonText) {
      buttonText.textContent = this.getLocaleString('quiz.steps.bulk.add_to_cart');
    }

    if (buttonPrice) {
      if (selectedMultiplier === 1) {
        // 1x: Compare (strikethrough, neutral-200) + Current (white)
        buttonPrice.innerHTML = `<span class="button-price-compare">${this.formatMoney(comparePrice, currency, locale)}</span><span class="button-price-current">${this.formatMoney(currentPrice, currency, locale)}</span>`;
      } else {
        // 2x/3x: Compare (strikethrough, neutral-200) + Bulk (white)
        buttonPrice.innerHTML = `<span class="button-price-compare">${this.formatMoney(comparePrice, currency, locale)}</span><span class="button-price-current">${this.formatMoney(currentPrice, currency, locale)}</span>`;
      }
    }
  }

  /**
   * Update bulk pricing
   */
  async updateBulkPricing() {
    const calculations = this.flow.state.getCalculations();
    const matchDays = calculations.matchDays || 0;
    const selectedPeriod = this.flow.state.selectedPricePeriod || 'day';
    const locale = window.__wizard1CurrentLocaleFull || window.__wizard1CurrentLocale || 'nl-NL';
    const baseTotals = this.calculateBulkTotals(1);
    const currency = baseTotals.currency || 'EUR';

    if (matchDays === 0) {
      return;
    }

    // baseTotals already computed above for currency; reuse for comparison
    
    // Calculate totals for each multiplier
    const multipliers = [1, 2, 3];
    const allTotals = {};
    multipliers.forEach(mult => {
      allTotals[mult] = this.calculateBulkTotals(mult);
    });

    // Update prices for each option
    multipliers.forEach(mult => {
      const totals = allTotals[mult];
      const frequencyDays = matchDays * mult;
      
      // Calculate per-period prices
      const pricePerDay = totals.totalFirstOrder / frequencyDays;
      const pricePerWeek = pricePerDay * 7;
      const pricePerShipment = totals.totalFirstOrder;

      // Calculate original price per period
      const originalPerDay = totals.compareAtTotal / frequencyDays;
      const originalPerWeek = originalPerDay * 7;
      const originalPerShipment = totals.compareAtTotal;

      // Update price display based on period
      let displayPrice = 0;
      let originalPrice = 0;

      switch (selectedPeriod) {
        case 'week':
          displayPrice = Math.round(pricePerWeek);
          originalPrice = Math.round(originalPerWeek);
          break;
        case 'shipment':
          displayPrice = totals.totalFirstOrder;
          originalPrice = totals.compareAtTotal;
          break;
        case 'day':
        default:
          displayPrice = Math.round(pricePerDay);
          originalPrice = Math.round(originalPerDay);
          break;
      }

      // Update card price displays with semantic classes
      const priceOriginal = this.currentStepElement.querySelector(`[data-bulk-price-original-${mult}]`);
      const priceBase = this.currentStepElement.querySelector(`[data-bulk-price-base-${mult}]`);
      const priceSubscription = this.currentStepElement.querySelector(`[data-bulk-price-subscription-${mult}]`);
      const priceFinal = this.currentStepElement.querySelector(`[data-bulk-price-final-${mult}]`);

      if (mult === 1) {
        // 1x: original (strikethrough, gray) + base (non-strikethrough, charcoal)
        if (priceOriginal) {
          priceOriginal.textContent = this.formatMoney(originalPrice, currency, locale);
          priceOriginal.classList.remove('bulk-price-hidden');
          priceOriginal.classList.add('bulk-price-visible');
        }
        if (priceBase) {
          priceBase.textContent = this.formatMoney(displayPrice, currency, locale);
          priceBase.classList.remove('bulk-price-hidden');
          priceBase.classList.add('bulk-price-visible');
        }
        if (priceSubscription) {
          priceSubscription.classList.add('bulk-price-hidden');
          priceSubscription.classList.remove('bulk-price-visible');
        }
        if (priceFinal) {
          priceFinal.classList.add('bulk-price-hidden');
          priceFinal.classList.remove('bulk-price-visible');
        }
      } else {
        // 2x/3x: compare-at (strikethrough, charcoal) + final (non-strikethrough, success green)
        // Use totals.compareAtTotal (actual compare-at for selected items) scaled by period
        const comparePriceForPeriod = originalPrice;

        // Hide original price on 2x/3x cards
        if (priceOriginal) {
          priceOriginal.classList.add('bulk-price-hidden');
          priceOriginal.classList.remove('bulk-price-visible');
        }
        if (priceBase) {
          priceBase.classList.add('bulk-price-hidden');
          priceBase.classList.remove('bulk-price-visible');
        }
        if (priceSubscription) {
          priceSubscription.textContent = this.formatMoney(comparePriceForPeriod, currency, locale);
          priceSubscription.classList.remove('bulk-price-hidden');
          priceSubscription.classList.add('bulk-price-visible');
        }
        if (priceFinal) {
          priceFinal.textContent = this.formatMoney(displayPrice, currency, locale);
          priceFinal.classList.remove('bulk-price-hidden');
          priceFinal.classList.add('bulk-price-visible');
        }

        // Update annual savings (always visible for 2x and 3x)
        const annualSavings = this.calculateAnnualSavings(
          baseTotals.totalFirstOrder,
          totals.totalFirstOrder,
          matchDays,
          mult
        );
        const savingsElement = this.currentStepElement.querySelector(`[data-bulk-annual-savings-${mult}]`);
        if (savingsElement) {
          const savingsFormatted = this.formatMoney(annualSavings, currency, locale);
          const savingsText = this.getLocaleString('quiz.steps.bulk.annual_savings', { amount: savingsFormatted });
          savingsElement.textContent = savingsText;
          savingsElement.style.display = 'block';
          savingsElement.style.visibility = 'visible';
        }
      }
    });

    // Update footer prices
    const selectedMultiplier = parseInt(this.flow.state.selectedPlan?.replace('x', '') || '1');
    const selectedTotals = allTotals[selectedMultiplier];
    const selectedFrequencyDays = matchDays * selectedMultiplier;

    // Calculate footer prices
    const footerPricePerDay = selectedTotals.totalFirstOrder / selectedFrequencyDays;
    const footerPricePerWeek = footerPricePerDay * 7;
    const footerPricePerShipment = selectedTotals.totalFirstOrder;

    const footerOriginalPerDay = selectedTotals.compareAtTotal / selectedFrequencyDays;
    const footerOriginalPerWeek = footerOriginalPerDay * 7;
    const footerOriginalPerShipment = selectedTotals.compareAtTotal;

    // Base totals for comparison (1x)
    const basePricePerDay = baseTotals.totalFirstOrder / matchDays;
    const basePricePerWeek = basePricePerDay * 7;
    const basePricePerShipment = baseTotals.totalFirstOrder;

    let footerDisplayPrice = 0;
    let footerOriginalPrice = 0;
    let footerBasePrice = 0;

    switch (selectedPeriod) {
      case 'week':
        footerDisplayPrice = Math.round(selectedMultiplier > 1 ? footerPricePerWeek : 0);
        footerOriginalPrice = Math.round(footerOriginalPerWeek);
        footerBasePrice = Math.round(basePricePerWeek);
        break;
      case 'shipment':
        footerDisplayPrice = selectedMultiplier > 1 ? footerPricePerShipment : 0;
        footerOriginalPrice = footerOriginalPerShipment;
        footerBasePrice = basePricePerShipment;
        break;
      case 'day':
      default:
        footerDisplayPrice = Math.round(selectedMultiplier > 1 ? footerPricePerDay : 0);
        footerOriginalPrice = Math.round(footerOriginalPerDay);
        footerBasePrice = Math.round(basePricePerDay);
        break;
    }

    // Update footer price element - use same classes as card prices
    // Note: price footer is moved to quiz-footer, so search in document
    const footerPrice = document.querySelector('[data-bulk-footer-price-base]');

    if (footerPrice) {
      // Remove all state classes first
      footerPrice.classList.remove('bulk-price-base', 'bulk-price-final', 'bulk-price-hidden', 'bulk-price-visible');
      
      if (selectedMultiplier === 1) {
        // 1x: show base price
        footerPrice.textContent = this.formatMoney(footerBasePrice, currency, locale);
        footerPrice.classList.add('bulk-price-base', 'bulk-price-visible');
      } else {
        // 2x/3x: show final price (success green)
        footerPrice.textContent = this.formatMoney(footerDisplayPrice, currency, locale);
        footerPrice.classList.add('bulk-price-final', 'bulk-price-visible');
      }
    }

    // Update bulk step add to cart button with shipment totals
    this.updateBulkAddToCartButton(selectedMultiplier, baseTotals, selectedTotals, currency, locale);
  }

  /**
   * Handle add to cart
   */
  async handleAddToCart() {
    const button = document.querySelector('.bulk-add-to-cart-button') || this.currentStepElement.querySelector('[data-add-to-cart]');
    if (button) {
      button.disabled = true;
      const buttonText = button.querySelector('.button-text');
      if (buttonText) {
        buttonText.textContent = this.getLocaleString('quiz.steps.bulk.add_to_cart_loading');
      }
    }

    try {
      const petData = this.flow.state.getPetData();
      const calculations = this.flow.state.getCalculations();
      const selectedFlavors = this.flow.state.selectedFlavors || [];
      const multiplier = parseInt(this.flow.state.selectedPlan?.replace('x', '') || '1');

      // Validate required data
      if (!petData.name || selectedFlavors.length === 0 || !calculations.matchDays) {
        throw new Error('Missing required data for cart');
      }

      // Get flavor distribution (ratio-preserving: base × multiplier)
      const distribution = this.getBulkDistribution(multiplier);
      const bagCount = Object.values(distribution).reduce((sum, qty) => sum + qty, 0);

      // Get selling plan ID - use frequencyDays (matchDays * multiplier) for 2x/3x options
      const config = this.flow.getConfig();
      const matchDays = calculations.matchDays;
      const frequencyDays = matchDays * multiplier;
      const sellingPlanId = config.sellingPlanMapping[frequencyDays.toString()];

      if (!sellingPlanId) {
        throw new Error(`No selling plan found for ${frequencyDays} days`);
      }

      // Build cart items with proper properties
      const cartItems = [];
      FlavorDistribution.getFlavorsWithPositiveQuantity(distribution).forEach(flavorId => {
        const variantId = config.variantIds[flavorId];
        const quantity = distribution[flavorId];

        if (!variantId) {
          console.warn(`No variant ID found for flavor: ${flavorId}`);
          return;
        }

        cartItems.push({
          variantId: variantId,
          quantity: quantity,
          sellingPlanId: sellingPlanId,
          properties: {
            ...this.buildPetLineItemProperties(petData.name, this.getWizard1MealPlanLinePropertyLabel()),
            _acquisition_flow: config.acquisitionFlowId || 'wizard-1'
          }
        });
      });

      if (cartItems.length === 0) {
        throw new Error('No cart items to add');
      }

      // Add items to cart (simple - no retry logic)
      await this.flow.shopifyAPI.addToCart(cartItems);

      // Sync Instant cart drawer (no drawer open - redirect follows immediately)
      window.Instant?.api?.updateCart?.();

      // Persist cart_items to pet metaobject for cross-device resume
      if (this.flow.state.petMetaobjectId && this.flow.shopifyAPI?.backendApiUrl) {
        try {
          const cartItemsForMetaobject = cartItems.map(item => ({
            variantId: item.variantId,
            quantity: item.quantity,
            sellingPlanId: item.sellingPlanId,
            properties: item.properties || {}
          }));
          await this.flow.shopifyAPI.createOrUpdatePetMetaobject({
            petData: this.flow.state.petData,
            calculations: this.flow.state.calculations || {},
            petMetaobjectId: this.flow.state.petMetaobjectId,
            cartItems: cartItemsForMetaobject
          });
        } catch (e) {
          console.warn('Failed to persist cart to metaobject (non-blocking):', e.message);
        }
      }

      // Extract unique pet names from cart and update cart note
      try {
        // Fetch current cart to get all items (including any existing items from previous pets)
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          
          // Extract unique pet names from all cart items
          const petNamesSet = new Set();
          if (cart.items && Array.isArray(cart.items)) {
            cart.items.forEach(item => {
              const petName = this.getPetNameFromLineItemProperties(item.properties);
              if (petName) petNamesSet.add(petName);
            });
          }
          
          // Convert Set to array
          const uniquePetNames = Array.from(petNamesSet);
          
          // Format cart note with owner name and pet names
          const formattedNote = this.formatCartNote(petData.ownerName, uniquePetNames);
          
          // Update cart note
          if (formattedNote) {
            await this.flow.shopifyAPI.updateCartNote(formattedNote);
          }
        }
      } catch (error) {
        // Non-blocking - log error but don't prevent checkout
        console.warn('Failed to update cart note (non-blocking):', error);
      }

      // Add cart items to pending Klaviyo event (if within 1-minute delay)
      if (this.flow.klaviyoManager && petData.ownerEmail) {
        try {
          // Build cart items with flavor names from variantPricing
          const variantPricing = this.flow.state.variantPricing || {};
          const cartItemsForKlaviyo = [];
          
          FlavorDistribution.getFlavorsWithPositiveQuantity(distribution).forEach(flavorId => {
            const variantInfo = variantPricing[flavorId];
            const flavorName = variantInfo?.title || flavorId;
            const quantity = distribution[flavorId];
            
            cartItemsForKlaviyo.push({
              variant_title: flavorName,
              quantity: quantity,
              properties: {
                ...this.buildPetLineItemProperties(petData.name, this.getWizard1MealPlanLinePropertyLabel())
              }
            });
          });
          
          this.flow.klaviyoManager.addCartItemsToEvent(cartItemsForKlaviyo, petData.ownerEmail);
        } catch (e) {
          console.warn('Failed to add cart items to Klaviyo event (non-blocking):', e);
        }
      }

      // Calculate totals for Facebook tracking
      const selectedTotals = this.calculateBulkTotals(multiplier);
      const cartValue = selectedTotals.totalFirstOrder / 100; // Convert cents to dollars
      const currency = selectedTotals.currency || 'EUR';

      // Build content_ids and contents arrays for Facebook
      const contentIds = cartItems.map(item => item.variantId);
      const contents = cartItems.map(item => ({
        id: item.variantId,
        quantity: item.quantity
      }));

      // Track add to cart / completion in analytics
      const analyticsPayload = {
        pet_name: petData.name,
        match_days: matchDays,
        bag_count: bagCount,
        intro_match_days: calculations.introMatchDays,
        intro_bag_count: calculations.introBagCount,
        flavors: selectedFlavors,
        multiplier: multiplier
      };
      if (this.flow.analytics) {
        try {
          if (typeof this.flow.analytics.trackCompletion === 'function') {
            this.flow.analytics.trackCompletion(analyticsPayload);
          }
          
          // Facebook Pixel AddToCart tracking
          // Note: Shopify's native Pixel will also track AddToCart per item automatically
          // This custom tracking tracks the entire cart as one event
          if (typeof this.flow.analytics.trackFacebookAddToCart === 'function') {
            this.flow.analytics.trackFacebookAddToCart({
              content_ids: contentIds,
              contents: contents,
              value: cartValue,
              currency: currency
            });
          }
          
          // GA4 tracking (includes AddToCart via trackAddToCart)
          if (typeof this.flow.analytics.trackAddToCart === 'function') {
            this.flow.analytics.trackAddToCart(analyticsPayload);
          }
        } catch (e) {
          console.warn('Introbox analytics add-to-cart tracking failed:', e);
        }
      }

      // Send pending Klaviyo event immediately before checkout navigation
      // This ensures the consolidated event (with all pets) is sent before user leaves the page
      if (this.flow.klaviyoManager && petData.ownerEmail) {
        try {
          
          if (this.flow.klaviyoManager.hasPendingEvent(petData.ownerEmail)) {
            // Cancel the timer since we're sending now
            this.flow.klaviyoManager.cancelPendingEvent();
            // Send the consolidated event immediately
            await this.flow.klaviyoManager.sendKlaviyoEvent(petData.ownerEmail);
            
          }
        } catch (e) {
          // Non-blocking - log error but don't prevent checkout
          
          console.warn('Failed to send Klaviyo event before checkout (non-blocking):', e);
        }
      }

      if (typeof window.bzStampVwoExperiments === 'function') {
        try {
          await window.bzStampVwoExperiments();
        } catch (e) {}
      }

      // Redirect to checkout based on locale
      // Dutch: iDeal/Bancontact (SEPA checkout via Firmhouse), English: Credit cards/PayPal (regular checkout)
      // Pre-fill checkout with email and firstname from quiz
      // Works for empty cart and pre-filled cart: addToCart adds to existing items, redirect sends full cart to checkout
      const locale = window.__wizard1CurrentLocale || 'nl';
      const isDutch = locale === 'nl' || locale.startsWith('nl-');
      
      const checkoutUrl = this.buildCheckoutUrlWithPrefill(petData.ownerEmail, petData.ownerName);
      
      if (isDutch && typeof window.firmhouse !== 'undefined' && typeof window.firmhouse.getCart === 'function' && typeof window.firmhouse.generateSEPACheckoutUrl === 'function') {
        try {
          const cart = await window.firmhouse.getCart();
          const [checkoutUrlMain, checkoutUrlParams] = checkoutUrl.split('?');
          const newParams = checkoutUrlParams ? `skip_shop_pay=true&${checkoutUrlParams}` : 'skip_shop_pay=true';
          const checkoutUrlWithSkipShopPay = `${checkoutUrlMain}?${newParams}`
          const sepaUrl = window.firmhouse.generateSEPACheckoutUrl(cart.items);
          // Merge pre-fill into SEPA URL via return_to parameter (Firmhouse-specific)
          const returnToPattern = /%26return_to=[^&]*/;
          const checkoutUrlWithSepaPlans = returnToPattern.test(sepaUrl)
            ? sepaUrl.replace(returnToPattern, `%26return_to=${encodeURIComponent(encodeURIComponent(checkoutUrlWithSkipShopPay))}`)
            : sepaUrl;
          window.location.href = checkoutUrlWithSepaPlans;
        } catch (e) {
          console.warn('Firmhouse SEPA checkout failed, falling back to regular checkout:', e);
          window.location.href = checkoutUrl;
        }
      } else {
        window.location.href = checkoutUrl;
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Er is een fout opgetreden bij het toevoegen aan de winkelwagen. Probeer het opnieuw.');
      
      if (button) {
        button.disabled = false;
        const buttonText = button.querySelector('.button-text');
        if (buttonText) {
          buttonText.textContent = this.getLocaleString('quiz.steps.bulk.add_to_cart');
        }
      }
    }
  }

  /**
   * Build checkout URL with pre-fill parameters for email, name, and country
   * @param {string} email - Customer email address
   * @param {string} ownerName - Quiz owner name (split on first space; matches Introbox customerCreate)
   * @returns {string} Checkout URL with pre-fill parameters
   */
  buildCheckoutUrlWithPrefill(email, ownerName) {
    const params = new URLSearchParams();
    if (email) {
      params.append('checkout[email]', email);
      const { firstName, lastName } = splitOwnerNameForShopify(ownerName || '');
      if (firstName) {
        params.append('checkout[shipping_address][first_name]', firstName);
      }
      if (lastName) {
        params.append('checkout[shipping_address][last_name]', lastName);
      }
    }

    const checkoutPath = params.toString() ? `/checkout?${params.toString()}` : '/checkout';
    return window.bzCheckoutPrefill?.addCountryToUrl?.(checkoutPath) || checkoutPath;
  }

  /**
   * Move price footer and "add another dog" link into quiz-footer div
   */
  moveBulkFooterElements() {
    const quizFooter = document.querySelector('.quiz-footer');
    if (!quizFooter) return;

    // Defensive: Remove any existing bulk price footer from quiz-footer first
    // This prevents duplicates when navigating with browser back/forward
    const existingPriceFooters = quizFooter.querySelectorAll('[data-bulk-price-footer]');
    existingPriceFooters.forEach(footer => footer.remove());

    // Find price footer in step content
    let priceFooter = this.currentStepElement.querySelector('[data-bulk-price-footer]');
    
    if (priceFooter) {
      // Sync period selector value from state before moving
      const periodSelector = priceFooter.querySelector('[data-price-period-selector]');
      if (periodSelector) {
        const currentPeriod = this.flow.state.selectedPricePeriod || 'day';
        periodSelector.value = currentPeriod;
      }
      
      // Remove from step content and insert at the beginning of quiz-footer
      priceFooter.remove();
      quizFooter.insertBefore(priceFooter, quizFooter.firstChild);
    }

    // Defensive: Remove any existing "add another dog" links from quiz-footer first
    const existingLinks = quizFooter.querySelectorAll('.add-another-dog-link');
    existingLinks.forEach(link => link.remove());

    // Find or create "add another dog" link
    let addAnotherDogLink = this.currentStepElement.querySelector('[data-add-another-dog]');
    if (!addAnotherDogLink) {
      // Create the link if it doesn't exist
      addAnotherDogLink = document.createElement('a');
      addAnotherDogLink.href = '/wizard-1/quiz/name';
      addAnotherDogLink.className = 'add-another-dog-link';
      addAnotherDogLink.setAttribute('data-add-another-dog', '');
      addAnotherDogLink.textContent = this.getLocaleString('quiz.steps.bulk.add_another_dog');
      
      addAnotherDogLink.addEventListener('click', async (e) => {
        e.preventDefault();
        await this.handleAddAnotherDog();
      });
    } else {
      // Remove from step content
      addAnotherDogLink.remove();
    }

    // Insert link at the end of quiz-footer (after buttons)
    if (addAnotherDogLink) {
      quizFooter.appendChild(addAnotherDogLink);
    }
  }

  /**
   * Set up bulk step add to cart button (replaces Terug/Doorgaan)
   */
  setupBulkAddToCartButton() {
    const quizFooter = document.querySelector('.quiz-footer');
    if (!quizFooter) return;

    const footerButtons = quizFooter.querySelector('.footer-buttons');
    if (!footerButtons) return;

    // Defensive: Remove any existing bulk add to cart buttons first
    // This prevents duplicates when navigating with browser back/forward
    const existingButtons = quizFooter.querySelectorAll('.bulk-add-to-cart-button');
    existingButtons.forEach(button => button.remove());

    // Hide existing button
    const nextButton = footerButtons.querySelector('.next-button');
    if (nextButton) nextButton.style.display = 'none';

    // Create add to cart button (always create new since we removed existing ones above)
    const addToCartButton = document.createElement('button');
    addToCartButton.className = 'bulk-add-to-cart-button add-to-cart-button';
    addToCartButton.setAttribute('data-bulk-add-to-cart', '');
    
    // Lock icon
    const lockIcon = document.createElement('img');
    lockIcon.src = 'https://cdn.shopify.com/s/files/1/0919/3534/2856/files/lock.svg?v=1769261176';
    lockIcon.className = 'button-lock-icon';
    lockIcon.width = 18;
    lockIcon.height = 26; // Maintain aspect ratio (18px width = ~26px height)
    lockIcon.alt = '';
    lockIcon.setAttribute('aria-hidden', 'true');
    addToCartButton.appendChild(lockIcon);
    
    const buttonText = document.createElement('span');
    buttonText.className = 'button-text';
    addToCartButton.appendChild(buttonText);
    
    const buttonPrice = document.createElement('span');
    buttonPrice.className = 'button-price';
    addToCartButton.appendChild(buttonPrice);

    // Insert before footer-buttons
    quizFooter.insertBefore(addToCartButton, footerButtons);

    // Set up click handler (remove old listeners first)
    const newButton = addToCartButton.cloneNode(true);
    addToCartButton.parentNode.replaceChild(newButton, addToCartButton);
    newButton.addEventListener('click', () => {
      this.handleAddToCart();
    });
  }

  /**
   * Reset footer to clean state - removes all step-specific elements
   * This should be called before setting up any step to ensure a clean slate
   */
  resetFooter() {
    try {
      const quizFooter = document.querySelector('.quiz-footer');
      if (!quizFooter) return;

      // Remove all bulk-specific elements (defensive - remove all instances)
      const bulkPriceFooters = quizFooter.querySelectorAll('[data-bulk-price-footer]');
      bulkPriceFooters.forEach(footer => footer.remove());

      const addToCartButtons = quizFooter.querySelectorAll('.bulk-add-to-cart-button');
      addToCartButtons.forEach(button => button.remove());

      const addAnotherDogLinks = quizFooter.querySelectorAll('.add-another-dog-link');
      addAnotherDogLinks.forEach(link => link.remove());

      // Hide plan footer (but keep it in DOM)
      const planPriceFooters = document.querySelectorAll('[data-plan-price-footer]');
      planPriceFooters.forEach(footer => {
        footer.style.display = 'none';
      });

      // Show normal button
      const footerButtons = quizFooter.querySelector('.footer-buttons');
      if (footerButtons) {
        const nextButton = footerButtons.querySelector('.next-button');
        if (nextButton) nextButton.style.display = '';
      }
    } catch (error) {
      // Silently fail - don't break the flow if footer reset fails
      console.warn('Error resetting footer:', error);
    }
  }

  /**
   * Clean up bulk step when leaving (restore normal footer)
   * Now calls resetFooter() for consistency
   */
  cleanupBulkStep() {
    this.resetFooter();
  }

  /**
   * Move the theme Instant plan-overview section into [data-plan-overview-slot] and show the slot.
   * Wizard-1: plan step. Wizard-2: plan_overview step.
   */
  ensurePlanOverviewShopifySectionInSlotVisible() {
    const planEl = document.querySelector('[data-plan-overview-section], .data-plan-overview-section');
    const sectionWrapper = planEl?.closest('.shopify-section');
    const slot = document.querySelector('[data-plan-overview-slot]');
    if (sectionWrapper && slot) {
      if (!slot.contains(sectionWrapper)) {
        slot.appendChild(sectionWrapper);
      }
      slot.style.display = '';
    }
    const quizContainer = document.querySelector('[data-quiz-container]');
    if (quizContainer) quizContainer.classList.add('plan-step-active');
  }

  /**
   * Clean up plan step when leaving (hide plan-price-footer)
   * Now calls resetFooter() for consistency.
   * Also used when leaving wizard-2 plan_overview via back.
   */
  cleanupPlanStep() {
    // Hide plan-overview-section slot when leaving plan / plan_overview step
    const slot = document.querySelector('[data-plan-overview-slot]');
    if (slot) {
      slot.style.display = 'none';
    }
    // Remove plan-step-specific class
    const quizContainer = document.querySelector('[data-quiz-container]');
    if (quizContainer) quizContainer.classList.remove('plan-step-active');
    this.resetFooter();
  }

  /**
   * Set up "Add another dog" link positioned after footer
   */
  setupAddAnotherDogLink() {
    // Remove any existing link from quiz content
    const existingLink = this.currentStepElement.querySelector('[data-add-another-dog]');
    if (existingLink) {
      existingLink.remove();
    }

    // Find or create the link container after footer
    let linkContainer = document.querySelector('.quiz-footer-add-another-dog');
    if (!linkContainer) {
      linkContainer = document.createElement('div');
      linkContainer.className = 'quiz-footer-add-another-dog';
      const footer = document.querySelector('.quiz-footer');
      if (footer && footer.parentNode) {
        footer.parentNode.insertBefore(linkContainer, footer.nextSibling);
      }
    }

    // Create the link
    const link = document.createElement('a');
    link.href = '/wizard-1/quiz/name';
    link.className = 'add-another-dog-link';
    link.setAttribute('data-add-another-dog', '');
    const locale = window.__wizard1CurrentLocale || 'nl';
    link.textContent = this.getLocaleString('quiz.steps.bulk.add_another_dog');

    link.addEventListener('click', async (e) => {
      e.preventDefault();
      
      // Prevent multiple clicks
      if (link.disabled) {
        
        return;
      }
      link.disabled = true;
      try {
        await this.handleAddAnotherDog();
      } finally {
        link.disabled = false;
      }
    });

    linkContainer.innerHTML = '';
    linkContainer.appendChild(link);
  }

  /**
   * Handle "Add another dog" - adds to cart and redirects to quiz start
   */
  async handleAddAnotherDog() {
    
    try {
      // First, add current products to cart (same logic as handleAddToCart)
      const petData = this.flow.state.getPetData();
      const calculations = this.flow.state.getCalculations();
      const selectedFlavors = this.flow.state.selectedFlavors || [];
      const multiplier = parseInt(this.flow.state.selectedPlan?.replace('x', '') || '1');

      // Validate required data
      if (!petData.name || selectedFlavors.length === 0 || !calculations.matchDays) {
        
        throw new Error('Missing required data for cart');
      }

      // Get flavor distribution (ratio-preserving: base × multiplier)
      const distribution = this.getBulkDistribution(multiplier);

      // Get selling plan ID - use frequencyDays (matchDays * multiplier) for 2x/3x options
      const config = this.flow.getConfig();
      const matchDays = calculations.matchDays;
      const frequencyDays = matchDays * multiplier;
      const sellingPlanId = config.sellingPlanMapping[frequencyDays.toString()];

      if (!sellingPlanId) {
        throw new Error(`No selling plan found for ${frequencyDays} days`);
      }

      // Build cart items
      const cartItems = [];
      FlavorDistribution.getFlavorsWithPositiveQuantity(distribution).forEach(flavorId => {
        const variantId = config.variantIds[flavorId];
        const quantity = distribution[flavorId];

        if (!variantId) {
          console.warn(`No variant ID found for flavor: ${flavorId}`);
          return;
        }

        cartItems.push({
          variantId: variantId,
          quantity: quantity,
          sellingPlanId: sellingPlanId,
          properties: {
            ...this.buildPetLineItemProperties(petData.name, this.getWizard1MealPlanLinePropertyLabel()),
            _acquisition_flow: config.acquisitionFlowId || 'wizard-1'
          }
        });
      });

      if (cartItems.length === 0) {
        throw new Error('No cart items to add');
      }

      // Add items to cart
      try {
        await this.flow.shopifyAPI.addToCart(cartItems);
      } catch (error) {
        const is429 = error.status === 429 || (error.message && error.message.includes('429')) || (error.message && error.message.includes('Too Many Requests'));
        if (is429) {
          await new Promise(resolve => setTimeout(resolve, 5000));
          try {
            await this.flow.shopifyAPI.addToCart(cartItems);
          } catch (retryError) {
            console.warn('Cart addition failed after retry, but continuing:', retryError);
          }
        } else {
          console.warn('Cart addition failed, but continuing:', error);
        }
      }

      // Refresh cart icon (show and update count) and sync Instant drawer
      if (typeof window.__wizard1RefreshCartIcon === 'function') {
        window.__wizard1RefreshCartIcon();
      }

      // Persist cart_items to pet metaobject for cross-device resume (before reset clears state)
      if (this.flow.state.petMetaobjectId && this.flow.shopifyAPI?.backendApiUrl) {
        try {
          const cartItemsForMetaobject = cartItems.map(item => ({
            variantId: item.variantId,
            quantity: item.quantity,
            sellingPlanId: item.sellingPlanId,
            properties: item.properties || {}
          }));
          await this.flow.shopifyAPI.createOrUpdatePetMetaobject({
            petData: this.flow.state.petData,
            calculations: this.flow.state.calculations || {},
            petMetaobjectId: this.flow.state.petMetaobjectId,
            cartItems: cartItemsForMetaobject
          });
        } catch (e) {
          console.warn('Failed to persist cart to metaobject (non-blocking):', e.message);
        }
      }

      // Preserve sessionCustomerId before reset (for multi-pet flow)
      const preservedSessionCustomerId = this.flow.state.sessionCustomerId || this.flow.state.customerId;
      

      // Clear quiz state (reset to initial state)
      this.flow.state.reset();

      // Restore sessionCustomerId after reset
      if (preservedSessionCustomerId) {
        this.flow.state.sessionCustomerId = preservedSessionCustomerId;
        
      }

      // Navigate to quiz start; cart updates in background (no drawer)
      this.flow.router.navigate('name', {});
    } catch (error) {
      
      console.error('Error adding another dog:', error);
      
      // Don't show alert for 429 errors (rate limiting) - these are temporary
      const is429 = error.status === 429 || 
                   (error.message && (error.message.includes('429') || error.message.includes('Too Many Requests')));
      
      if (!is429) {
        // Only show alert for non-rate-limit errors
        alert('Er is een fout opgetreden. Probeer het opnieuw.');
      } else {
        // For 429 errors, just log and continue - user can still proceed
        console.warn('Rate limited, but continuing with navigation:', error);
      }
      
      // Always navigate to name step even if there was an error (non-blocking)
      // The cart might still have been updated despite the error
      try {
        this.flow.router.navigate('name', {});
      } catch (navError) {
        console.error('Error navigating to name step:', navError);
      }
    }
  }

  /**
   * Set up profile step
   * @param {Object} params - Route parameters (pet from query string)
   */
  async setupProfileStep(params) {
    // Cache for all customer pets
    this.cachedPets = null;

    // Fetch all customer pets and cache them
    await this.fetchAndCacheCustomerPets();

    // Determine which pet to load
    let petIdToLoad = null;

    // Priority 1: Check URL query parameter
    if (params && params.pet) {
      petIdToLoad = params.pet;
    }
    // Priority 2: Check state petMetaobjectId (from pencil icon click)
    else if (this.flow?.state?.petMetaobjectId) {
      petIdToLoad = this.flow.state.petMetaobjectId;
    }
    // Priority 2b: Check localStorage for petMetaobjectId (fallback if state was cleared)
    else if (!petIdToLoad) {
      const savedState = this.flow.storage.loadFromLocalStorage();
      if (savedState && savedState.petMetaobjectId) {
        petIdToLoad = savedState.petMetaobjectId;
        // Also restore it to state for consistency
        this.flow.state.petMetaobjectId = savedState.petMetaobjectId;
      }
    }
    // Priority 3: Check localStorage for last selected pet
    if (!petIdToLoad && this.cachedPets && this.cachedPets.length > 0) {
      const lastSelectedId = localStorage.getItem('wizard-1-lastSelectedPetId');
      if (lastSelectedId && this.cachedPets.some(p => p.id === lastSelectedId)) {
        petIdToLoad = lastSelectedId;
      }
      // Priority 4: Use most recent pet (first in array)
      else if (this.cachedPets.length > 0) {
        petIdToLoad = this.cachedPets[0].id;
      }
    }

    // If no pets found AND no petIdToLoad, check if we should redirect
    if (!petIdToLoad) {
      // If we have customerId but no pets were fetched, this might be a timing issue
      // OR the pet was just created but state.petMetaobjectId wasn't set
      // In this case, redirect to name step
      if (!this.cachedPets || this.cachedPets.length === 0) {
        console.warn('No pets found for this customer. Redirecting to name step.');
        this.flow.router.navigate('name');
        return;
      }
    }

    // Load the selected pet
    if (petIdToLoad) {
      await this.loadProfileData(petIdToLoad);

      if (params && params.photo) {
        this.flow.state.updatePetData('profilePhotoUrl', params.photo);
      }
      
      // Update URL to include ?pet= if it wasn't there
      if (!params || !params.pet) {
        const currentPath = this.flow.router.getCurrentPath();
        const newUrl = `${currentPath}?pet=${encodeURIComponent(petIdToLoad)}`;
        window.history.replaceState({}, '', newUrl);
      }
    } else {
      // Safety net: if we somehow can't determine a pet ID, redirect to name step
      console.warn('No pet ID available to load profile. Redirecting to name step.');
      this.flow.router.navigate('name');
      return;
    }

    // Create pet selection tabs UI (only if multiple pets)
    if (this.cachedPets && this.cachedPets.length > 1) {
      this.createPetSelectionTabs();
    }

    // Populate and set up profile fields
    this.populateProfileFields();

    // Set up edit functionality
    this.setupProfileEditing();

    // Set up delete functionality
    this.setupProfileDeletion();

    // Set up footer save button
    this.setupProfileSaveButton();

    if (this.flow.state.flowName === 'wizard-2') {
      this.setupProfilePhotoUpload();
    }
    
    // Update footer buttons for profile step
    this.updateFooterForProfile();

    // Set up back button in header for profile step
    const headerBackButton = document.querySelector('.quiz-header [data-back-button]');
    if (headerBackButton) {
      // Remove existing listeners by cloning and replacing (clean way to remove all listeners)
      const newButton = headerBackButton.cloneNode(true);
      headerBackButton.parentNode.replaceChild(newButton, headerBackButton);
      
      // Add new listener that goes back to previous page
      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        // Use browser history to go back to previous page
        if (window.history.length > 1) {
          window.history.back();
        } else {
          // Fallback: go to flavors page if no history
          this.flow.router.navigate('flavors');
        }
      });
    }
  }

  /**
   * Update footer buttons for profile step (replace Back/Next with Save Profile)
   */
  updateFooterForProfile() {
    const footer = document.querySelector('.quiz-footer');
    const footerButtons = footer?.querySelector('.footer-buttons');
    
    if (!footer || !footerButtons) return;

    // Add profile-footer class
    footer.classList.add('profile-footer');

    // Get locale strings
    const w3Purchase = window.BzWizard && window.BzWizard.id() === 'w3' && !(this.isPortalProfileEdit && this.isPortalProfileEdit());
    const saveProfileText = w3Purchase
      ? (window.__wizard3Copy && window.__wizard3Copy.checkout) || 'Afrekenen'
      : this.getLocaleString('quiz.steps.profile.save_profile');

    // Replace buttons with single Save Profile button
    footerButtons.innerHTML = `
      <button class="profile-save-button" data-profile-save-button disabled>
        ${saveProfileText}
      </button>
    `;

    // Set up save button click handler
    const saveButton = footerButtons.querySelector('[data-profile-save-button]');
    if (saveButton) {
      saveButton.addEventListener('click', () => {
        this.saveProfile();
      });
    }
  }

  /**
   * Append email-step dog illustration to footer-buttons (used when restoring from profile)
   */
  appendEmailStepDog(footerButtons) {
    if (!footerButtons || footerButtons.querySelector('.email-step-dog')) return;
    const dog = document.createElement('img');
    dog.className = 'email-step-dog';
    dog.src = 'https://cdn.shopify.com/s/files/1/0919/3534/2856/files/BlueZone_-_Illustration_Border_Collie_1.webp?v=1773914888';
    dog.alt = '';
    dog.setAttribute('aria-hidden', 'true');
    dog.width = 56;
    footerButtons.appendChild(dog);
  }

  /**
   * Restore normal footer buttons (Next only - Back button removed, using header back arrow instead)
   */
  restoreFooterButtons() {
    const footer = document.querySelector('.quiz-footer');
    const footerButtons = footer?.querySelector('.footer-buttons');
    
    if (!footer || !footerButtons) return;

    // Remove profile-footer class
    footer.classList.remove('profile-footer');

    // Restore original button if we have it stored (preserves server-rendered translation)
    if (this.originalFooterButtons && this.originalFooterButtons.next) {
      // Clone the stored button to restore it
      const nextButton = this.originalFooterButtons.next.cloneNode(true);
      
      // Clear and restore
      footerButtons.innerHTML = '';
      footerButtons.appendChild(nextButton);
      this.appendEmailStepDog(footerButtons);
    } else {
      // Fallback: recreate button if original wasn't stored
      const nextText = this.getLocaleString('quiz.buttons.next');
      
      footerButtons.innerHTML = `
        <button class="next-button" disabled data-next-button>
          <span class="next-button-label">${nextText}</span>
          <span class="next-button-spinner" aria-hidden="true"></span>
        </button>
      `;
      this.appendEmailStepDog(footerButtons);
    }

    // Re-attach event listeners for Next button
    const nextButton = footerButtons.querySelector('[data-next-button]');
    if (nextButton) {
      nextButton.addEventListener('click', (e) => {
        const currentStep = this.flow.state.getCurrentStep();
        
        // Save weight when continue button is clicked on weight step
        if (currentStep === 'weight') {
          this.saveAllDogWeights();
        }
        
        // Don't navigate on email step - let setupCustomerCreation handle it
        if (currentStep === 'email') {
          return;
        }
        if (currentStep === 'extras' && window.WizardPlan) {
          if (window.BzWizard && window.BzWizard.id() === 'w3') {
            const picked = document.querySelector('input[name="w3-extra"]:checked');
            this.flow.state.extrasOn.w3 = picked ? picked.value : '';
          }
          this._runWizardCartAndCheckout({ mode: 'checkout' });
          return;
        }
        if (currentStep === 'profile' && window.BzWizard && window.BzWizard.id() === 'w3' && window.WizardPlan) {
          this._runWizardCartAndCheckout({ mode: 'checkout' });
          return;
        }
        this.flow.navigateNext();
      });
    }
  }

  /**
   * Fetch and cache all customer pets
   * @returns {Promise<void>}
   */
  async fetchAndCacheCustomerPets() {
    // Only fetch if not already cached
    if (this.cachedPets !== null) {
      return;
    }

    // Try to get customerId from flow state, fallback to window global
    let customerId = this.flow?.state?.customerId;
    if (!customerId && window.wizard1Flow?.state?.customerId) {
      customerId = window.wizard1Flow.state.customerId;
      // Also update flow state for consistency
      if (this.flow?.state) {
        this.flow.state.customerId = customerId;
      }
    }

    if (!customerId) {
      this.cachedPets = [];
      return;
    }

    try {
      const pets = await this.flow.shopifyAPI.getAllPetMetaobjects(customerId);
      this.cachedPets = pets || [];
    } catch (error) {
      console.error('Error fetching customer pets:', error);
      this.cachedPets = [];
    }
  }

  /**
   * Create pet selection tabs UI
   */
  createPetSelectionTabs() {
    if (!this.cachedPets || this.cachedPets.length <= 1) {
      return;
    }

    const profileStep = this.currentStepElement;
    if (!profileStep) {
      return;
    }

    // Check if tabs already exist
    let tabsContainer = profileStep.querySelector('[data-pet-selection-tabs]');
    if (tabsContainer) {
      // Update existing tabs
      this.updatePetSelectionTabs(tabsContainer);
      return;
    }

    // Create tabs container
    tabsContainer = document.createElement('div');
    tabsContainer.className = 'pet-selection-tabs';
    tabsContainer.setAttribute('data-pet-selection-tabs', '');

    // Insert before profile overview
    const profileOverview = profileStep.querySelector('[data-profile-overview]');
    if (profileOverview) {
      profileOverview.parentNode.insertBefore(tabsContainer, profileOverview);
    } else {
      // Fallback: insert after title
      const title = profileStep.querySelector('h2');
      if (title) {
        title.parentNode.insertBefore(tabsContainer, title.nextSibling);
      }
    }

    this.updatePetSelectionTabs(tabsContainer);
  }

  /**
   * Update pet selection tabs
   * @param {HTMLElement} tabsContainer - Container element for tabs
   */
  updatePetSelectionTabs(tabsContainer) {
    if (!this.cachedPets || this.cachedPets.length <= 1) {
      tabsContainer.style.display = 'none';
      return;
    }

    tabsContainer.style.display = 'block';
    tabsContainer.innerHTML = '';

    const currentPetId = this.flow.state.petMetaobjectId;

    this.cachedPets.forEach(pet => {
      const tab = document.createElement('button');
      tab.className = 'pet-selection-tab';
      tab.textContent = pet.petName || 'Unnamed Pet';
      tab.setAttribute('data-pet-id', pet.id);
      
      // Set active state if this is the current pet
      if (pet.id === currentPetId) {
        tab.classList.add('active');
      } else {
        tab.classList.add('inactive');
      }

      // Add click handler
      tab.addEventListener('click', async (e) => {
        e.preventDefault();
        await this.switchToPet(pet.id);
      });

      tabsContainer.appendChild(tab);
    });
  }

  /**
   * Switch to a different pet
   * @param {string} petId - Pet Metaobject ID
   */
  async switchToPet(petId) {
    // Save to localStorage
    localStorage.setItem('wizard-1-lastSelectedPetId', petId);

    // Update URL with query parameter
    const currentPath = this.flow.router.getCurrentPath();
    const newUrl = `${currentPath}?pet=${encodeURIComponent(petId)}`;
    window.history.pushState({}, '', newUrl);

    // Load the new pet
    await this.loadProfileData(petId);

    // Update tabs UI
    const tabsContainer = this.currentStepElement.querySelector('[data-pet-selection-tabs]');
    if (tabsContainer) {
      this.updatePetSelectionTabs(tabsContainer);
    }

    // Refresh profile display
    this.populateProfileFields();
    if (this.flow.state.flowName === 'wizard-2') {
      this.setupProfilePhotoUpload();
    }
  }

  /**
   * Load profile data from Shopify Metaobject
   * @param {string} metaobjectId - Pet Metaobject ID (GID format)
   */
  async loadProfileData(metaobjectId) {
    if (!metaobjectId) {
      throw new Error('Metaobject ID is required to load profile');
    }

    // Clear pet data and calculations (but keep customerId, selectedFlavors, selectedPlan)
    this.flow.state.petData = {
      name: '',
      gender: '',
      spayed: null,
      ageGroup: '',
      breed: '',
      breedUnknown: false,
      breedMixed: false,
      weight: null,
      composition: '',
      activity: '',
      isWorkingDog: false,
      allergies: [],
      snacks: '',
      ownerName: '',
      ownerEmail: '',
      emailConsent: true,
      profilePhotoUrl: '',
    };
    this.flow.state.calculations = {
      dailyKcal: null,
      dailyFoodG: null,
      daysPerBag: null,
      bagCount: null,
      matchDays: null,
      introBagCount: null,
      introMatchDays: null
    };

    // Load pet data from Shopify Metaobject
    let metaobject;
    try {
      metaobject = await this.flow.shopifyAPI.getPetMetaobjectById(metaobjectId, { bustCache: true });
    } catch (error) {
      throw error;
    }
    
    if (!metaobject) {
      throw new Error('Pet profile not found or access denied');
    }

    // Load data into state (this will update petMetaobjectId)
    // Don't update currentStep - keep it as 'profile'
    this.loadPetDataFromMetaobject(metaobject, false);

    if (this.flow.state.flowName === 'wizard-2') {
      const petData = this.flow.state.getPetData();
      if (!petData.profilePhotoUrl) {
        const profileField = (metaobject.fields || []).find((f) => f.key === 'profile_photo');
        const mediaGid = profileField?.value;
        if (
          mediaGid &&
          String(mediaGid).indexOf('gid://') === 0 &&
          this.flow.shopifyAPI &&
          typeof this.flow.shopifyAPI.getMediaImageUrl === 'function'
        ) {
          try {
            const mediaUrl = await this.flow.shopifyAPI.getMediaImageUrl(mediaGid);
            if (mediaUrl) this.flow.state.updatePetData('profilePhotoUrl', mediaUrl);
          } catch (e) {
            console.warn('Could not resolve profile photo URL:', e);
          }
        }
      }
    }
  }

  /**
   * Populate all profile fields with current values
   */
  populateProfileFields() {
    const petData = this.flow.state.getPetData();

    // Populate name input
    const nameInput = this.currentStepElement.querySelector('[data-profile-input="name"]');
    if (nameInput) {
      nameInput.value = petData.name || '';
    }

    this.renderProfilePhotoPreview();

    // Populate gender select
    const genderSelect = this.currentStepElement.querySelector('[data-profile-select="gender"]');
    if (genderSelect) {
      genderSelect.value = petData.gender || '';
    }

    // Populate ageGroup select
    const ageGroupSelect = this.currentStepElement.querySelector('[data-profile-select="ageGroup"]');
    if (ageGroupSelect) {
      ageGroupSelect.value = petData.ageGroup || '';
    }

    // Populate breed input with locale text (not value)
    const breedInput = this.currentStepElement.querySelector('[data-profile-input="breed"]');
    if (breedInput && petData.breed) {
      breedInput.value = (window.WizardBreedEntry && window.WizardBreedEntry.displayBreed)
        ? window.WizardBreedEntry.displayBreed(petData.breed)
        : petData.breed;
    }

    // Populate weight input
    const weightInput = this.currentStepElement.querySelector('[data-profile-input="weight"]');
    if (weightInput && petData.weight) {
      weightInput.value = petData.weight;
    }

    // Populate spayed select
    const spayedSelect = this.currentStepElement.querySelector('[data-profile-select="spayed"]');
    if (spayedSelect) {
      if (petData.spayed === true) {
        spayedSelect.value = 'true';
      } else if (petData.spayed === false) {
        spayedSelect.value = 'false';
      } else {
        spayedSelect.value = '';
      }
    }

    // Populate composition select
    const compositionSelect = this.currentStepElement.querySelector('[data-profile-select="composition"]');
    if (compositionSelect) {
      compositionSelect.value = petData.composition || '';
    }

    // Populate activity select (handle working dog)
    const activitySelect = this.currentStepElement.querySelector('[data-profile-select="activity"]');
    if (activitySelect) {
      // If activity is 'veryActive', show 'workingDog' in dropdown (since workingDog maps to veryActive)
      // Otherwise, show the activity value directly
      if (petData.activity === 'veryActive') {
        activitySelect.value = 'workingDog';
      } else {
        activitySelect.value = petData.activity || '';
      }
    }

    // Populate snacks select
    const snacksSelect = this.currentStepElement.querySelector('[data-profile-select="snacks"]');
    if (snacksSelect) {
      snacksSelect.value = petData.snacks || '';
    }

    // Populate allergies multi-select and tags
    const allergiesSelect = this.currentStepElement.querySelector('[data-profile-select="allergies"]');
    if (allergiesSelect) {
      const allergiesArray = this.normalizePetAllergies(
        Array.isArray(petData.allergies) ? petData.allergies : []
      );
      if (JSON.stringify(allergiesArray) !== JSON.stringify(petData.allergies || [])) {
        this.flow.updatePetData('allergies', allergiesArray);
      }
      Array.from(allergiesSelect.options).forEach(option => {
        option.selected = allergiesArray.includes(option.value);
      });
      this.updateAllergiesTags();
    }

    // Update spayed label based on gender
    this.updateSpayedLabel();

    // Set up allergies dropdown functionality
    this.setupAllergiesDropdown();
  }

  /**
   * Get age group display text
   */
  getAgeGroupDisplay(ageGroup) {
    const mappings = {
      'puppy': 'Puppy',
      'adult': 'Volwassen',
      'senior': 'Senior'
    };
    return mappings[ageGroup] || '';
  }

  /**
   * Get composition display text
   */
  getCompositionDisplay(composition) {
    const mappings = {
      'heavy': 'Er mag wat gewicht af',
      'normal': 'Precies goed',
      'slim': 'Er mag wat gewicht bij'
    };
    return mappings[composition] || '';
  }

  /**
   * Get activity display text
   */
  getActivityDisplay(activity) {
    const mappings = {
      'indoorInactive': '0-60 minuten',
      'low': '1-2 uur',
      'moderate': '2+ uur',
      'veryActive': 'Werkhond'
    };
    return mappings[activity] || '';
  }

  /**
   * Get allergies display text
   */
  getAllergiesDisplay(allergies) {
    if (!allergies || allergies.length === 0) {
      return 'Geen';
    }
    return allergies.join(', ');
  }

  /**
   * Get snacks display text
   */
  getSnacksDisplay(snacks) {
    const mappings = {
      'noTreats': 'Geen',
      'fewTreats': 'Een paar',
      'lotOfTreats': 'Veel'
    };
    return mappings[snacks] || '';
  }

  /**
   * Set up profile editing (always-editable fields)
   */
  setupProfileEditing() {
    // Set up text/number input handlers
    const inputs = this.currentStepElement.querySelectorAll('[data-profile-input]');
    inputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const field = input.getAttribute('data-field');
        let value = input.value;
        if (input.type === 'number') {
          value = value ? parseFloat(value) : null;
        }
        this.flow.updatePetData(field, value);
        this.updateSaveButtonState();
      });

      // Also trigger on input for real-time updates (except for weight which has buttons)
      if (input.getAttribute('data-field') !== 'weight') {
        input.addEventListener('input', (e) => {
          const field = input.getAttribute('data-field');
          let value = input.value;
          if (input.type === 'number') {
            value = value ? parseFloat(value) : null;
          }
          this.flow.updatePetData(field, value);
        });
      }

      if (input.getAttribute('data-field') === 'name') {
        input.addEventListener('blur', () => {
          const canonical = this.flow.state.petData?.name ?? '';
          if (input.value !== canonical) {
            input.value = canonical;
          }
        });
      }
    });

    // Set up select dropdown handlers
    const selects = this.currentStepElement.querySelectorAll('[data-profile-select]');
    selects.forEach(select => {
      const field = select.getAttribute('data-field');
      
      if (field === 'allergies') {
        // Multi-select for allergies
        select.addEventListener('change', (e) => {
          const selectedValues = Array.from(select.selectedOptions).map(opt => opt.value);
          this.flow.updatePetData('allergies', this.normalizePetAllergies(selectedValues));
          this.updateAllergiesTags();
          this.updateSaveButtonState();
        });
      } else {
        // Single select
        select.addEventListener('change', (e) => {
          let value = select.value;
          
          // Convert string booleans to actual booleans for spayed
          if (field === 'spayed') {
            if (value === 'true') value = true;
            else if (value === 'false') value = false;
            else value = null;
          }
          
          // Handle activity: if workingDog is selected, set activity to 'veryActive'
          if (field === 'activity') {
            if (value === 'workingDog') {
              this.flow.updatePetData('activity', 'veryActive');
            } else {
              this.flow.updatePetData('activity', value);
            }
          } else {
            this.flow.updatePetData(field, value);
          }
          
          // Update spayed label if gender changed
          if (field === 'gender') {
            this.updateSpayedLabel();
          }
          
          this.updateSaveButtonState();
        });
      }
    });

    // Set up weight +/- buttons
    const decreaseButton = this.currentStepElement.querySelector('[data-weight-decrease]');
    const increaseButton = this.currentStepElement.querySelector('[data-weight-increase]');
    const weightInput = this.currentStepElement.querySelector('[data-weight-input]');

    // Prevent +/- buttons from stealing focus (keeps input focused when repeatedly clicking)
    const preventButtonFocus = (e) => e.preventDefault();
    if (decreaseButton) decreaseButton.addEventListener('mousedown', preventButtonFocus);
    if (increaseButton) increaseButton.addEventListener('mousedown', preventButtonFocus);

    if (decreaseButton && weightInput) {
      decreaseButton.addEventListener('click', () => {
        const currentValue = parseFloat(weightInput.value) || 0.5;
        const newValue = Math.max(0.5, currentValue - 1);
        weightInput.value = newValue;
        if (document.activeElement !== weightInput) {
          weightInput.focus();
        }
        this.flow.updatePetData('weight', newValue);
        this.updateSaveButtonState();
      });
    }

    if (increaseButton && weightInput) {
      increaseButton.addEventListener('click', () => {
        const currentValue = parseFloat(weightInput.value) || 0;
        const newValue = Math.min(100, currentValue + 1);
        weightInput.value = newValue;
        if (document.activeElement !== weightInput) {
          weightInput.focus();
        }
        this.flow.updatePetData('weight', newValue);
        this.updateSaveButtonState();
      });
    }

    // Set up breed autocomplete (reuse existing breed input setup)
    const breedInput = this.currentStepElement.querySelector('[data-breed-input]');
    if (breedInput) {
      // Store original selectBreed method
      const originalSelectBreed = this.selectBreed.bind(this);
      
      // Override selectBreed for profile step to also update save button state
      const profileStepContext = this;
      this.selectBreed = function(breed, input, dropdown) {
        originalSelectBreed(breed, input, dropdown);
        // Update save button state when breed is selected on profile page
        if (profileStepContext.currentStepElement?.closest('[data-profile-step]')) {
          profileStepContext.updateSaveButtonState();
        }
      };
      
      // Reuse the existing setupBreedInput method
      this.setupBreedInput();
      
      // Listen for manual typing to update state and save button
      breedInput.addEventListener('input', (e) => {
        this.flow.updatePetData('breed', breedInput.value);
        this.updateSaveButtonState();
      });
      
      // Also listen for when value changes programmatically (when breed is selected from dropdown)
      // Use a MutationObserver on the value attribute or poll (less ideal but works)
      // Actually, selectBreed already updates state, so we just need to update save button
      // We can use a simple setTimeout to check after selectBreed runs, or better:
      // Override is already done above, so it should work
    }

    // Initial update of allergies tags and save button state
    this.updateAllergiesTags();
    this.updateSaveButtonState();
  }

  /**
   * Update spayed field label based on gender
   */
  updateSpayedLabel() {
    const spayedLabel = this.currentStepElement.querySelector('[data-spayed-label]');
    if (!spayedLabel) return;

    const petData = this.flow.state.getPetData();
    const gender = petData.gender;

    let labelKey;
    if (gender === 'male') {
      labelKey = 'quiz.steps.profile.field_spayed_male';
    } else if (gender === 'female') {
      labelKey = 'quiz.steps.profile.field_spayed_female';
    } else {
      labelKey = 'quiz.steps.profile.field_spayed';
    }

    const labelText = this.getLocaleString(labelKey);
    spayedLabel.textContent = labelText;
  }

  /**
   * Set up allergies dropdown functionality
   */
  setupAllergiesDropdown() {
    const trigger = this.currentStepElement.querySelector('[data-allergies-trigger]');
    const dropdown = this.currentStepElement.querySelector('[data-allergies-dropdown]');
    const allergiesSelect = this.currentStepElement.querySelector('[data-profile-select="allergies"]');
    
    if (!trigger || !dropdown || !allergiesSelect) return;

    // Build dropdown options from select
    const buildDropdown = () => {
      dropdown.innerHTML = '';
      Array.from(allergiesSelect.options).forEach(option => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'profile-allergies-dropdown-item';
        item.textContent = option.textContent;
        item.dataset.value = option.value;
        
        // Check if selected
        if (option.selected) {
          item.classList.add('profile-allergies-dropdown-item--selected');
        }
        
        item.addEventListener('click', () => {
          // Toggle selection
          option.selected = !option.selected;
          
          // Update visual state immediately
          if (option.selected) {
            item.classList.add('profile-allergies-dropdown-item--selected');
          } else {
            item.classList.remove('profile-allergies-dropdown-item--selected');
          }
          
          // Update state
          const selectedValues = Array.from(allergiesSelect.selectedOptions).map(opt => opt.value);
          this.flow.updatePetData('allergies', selectedValues);
          
          // Update UI
          this.updateAllergiesTags();
          this.updateSaveButtonState();
        });
        
        dropdown.appendChild(item);
      });
    };

    // Toggle dropdown
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = dropdown.style.display !== 'none';
      dropdown.style.display = isOpen ? 'none' : 'block';
      trigger.setAttribute('aria-expanded', !isOpen);
      buildDropdown();
    });

    // Close on outside click
    const closeHandler = (e) => {
      if (!trigger.contains(e.target) && !dropdown.contains(e.target)) {
        dropdown.style.display = 'none';
        trigger.setAttribute('aria-expanded', 'false');
      }
    };
    document.addEventListener('click', closeHandler);

    // Initial build
    buildDropdown();
  }

  /**
   * Update allergies dropdown display
   */
  updateAllergiesDropdown() {
    // This function is no longer needed - tags are shown inside the trigger
    // Placeholder visibility is handled in updateAllergiesTags()
  }

  /**
   * Update allergies tags display
   */
  updateAllergiesTags() {
    const allergiesSelect = this.currentStepElement.querySelector('[data-profile-select="allergies"]');
    const tagsContainer = this.currentStepElement.querySelector('[data-allergies-tags]');
    const placeholder = this.currentStepElement.querySelector('.profile-allergies-placeholder');
    
    if (!allergiesSelect || !tagsContainer) return;

    const selectedValues = Array.from(allergiesSelect.selectedOptions).map(opt => opt.value);
    const selectedTexts = Array.from(allergiesSelect.selectedOptions).map(opt => opt.text);

    tagsContainer.innerHTML = '';
    
    if (selectedValues.length === 0) {
      // Show placeholder when no allergies selected
      if (placeholder) {
        placeholder.textContent = this.getLocaleString('quiz.steps.profile.no_allergies');
        placeholder.style.display = 'block';
      }
    } else {
      // Hide placeholder and show tags
      if (placeholder) {
        placeholder.style.display = 'none';
      }
      
      selectedValues.forEach((value, index) => {
        const tag = document.createElement('span');
        tag.className = 'profile-allergy-tag';
        tag.textContent = selectedTexts[index];
        
        const removeButton = document.createElement('span');
        removeButton.className = 'profile-allergy-tag-remove';
        removeButton.textContent = '×';
        removeButton.setAttribute('aria-label', 'Remove');
        removeButton.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent trigger click
          // Deselect the option
          const option = Array.from(allergiesSelect.options).find(opt => opt.value === value);
          if (option) {
            option.selected = false;
            
            // Update dropdown item visual state if dropdown is open
            const dropdown = this.currentStepElement.querySelector('[data-allergies-dropdown]');
            if (dropdown && dropdown.style.display !== 'none') {
              const dropdownItem = dropdown.querySelector(`[data-value="${value}"]`);
              if (dropdownItem) {
                dropdownItem.classList.remove('profile-allergies-dropdown-item--selected');
              }
            }
            
            const selectedValues = Array.from(allergiesSelect.selectedOptions).map(opt => opt.value);
            this.flow.updatePetData('allergies', this.normalizePetAllergies(selectedValues));
            this.updateAllergiesTags();
            this.updateSaveButtonState();
          }
        });
        
        tag.appendChild(removeButton);
        tagsContainer.appendChild(tag);
      });
    }
  }

  /**
   * Update save button enabled/disabled state based on field validation
   */
  updateSaveButtonState() {
    const saveButton = document.querySelector('.profile-save-button');
    if (!saveButton) return;

    const isValid = this.validateProfileFields();
    saveButton.disabled = !isValid;
  }

  /**
   * Validate all profile fields
   * @returns {boolean} True if all required fields are valid
   */
  getProfilePhotoLabels() {
    const addPhotoEl = this.currentStepElement?.querySelector('[data-translate="quiz.steps.profile.add_photo"]');
    const savedEl = this.currentStepElement?.querySelector('[data-translate="quiz.steps.profile.photo_saved"]');
    const locale = window.__wizard2CurrentLocale || window.__wizard1CurrentLocale || 'nl';
    return {
      addPhoto: addPhotoEl?.textContent?.trim() || (locale === 'nl' ? 'Foto toevoegen' : 'Add photo'),
      photoSaved: savedEl?.textContent?.trim() || (locale === 'nl' ? 'Foto opgeslagen' : 'Photo saved'),
    };
  }

  parseProfilePhotoFromField(field) {
    if (!field) return '';
    let photoUrl = '';
    const ref = field.reference;
    if (ref) {
      if (ref.image && ref.image.url) photoUrl = ref.image.url;
      else if (ref.url) photoUrl = ref.url;
    }
    const value = field.value;
    if (!photoUrl && value && String(value).indexOf('http') === 0) {
      photoUrl = value;
    }
    return photoUrl;
  }

  renderProfilePhotoPreview() {
    if (!this.currentStepElement || this.flow.state.flowName !== 'wizard-2') return;
    const wrap = this.currentStepElement.querySelector('[data-bz-profile-photo]');
    if (!wrap || !window.bzPetPhotoUpload) return;
    const petData = this.flow.state.getPetData();
    const labels = this.getProfilePhotoLabels();
    window.bzPetPhotoUpload.renderWizardPhoto(
      wrap,
      petData.profilePhotoUrl || '',
      petData.name || '',
      labels.addPhoto
    );
  }

  setupProfilePhotoUpload() {
    if (!this.currentStepElement || this.flow.state.flowName !== 'wizard-2') return;
    if (!window.bzPetPhotoUpload || typeof window.bzPetPhotoUpload.bind !== 'function') return;

    if (this._profilePhotoUploadUnbind) {
      this._profilePhotoUploadUnbind();
      this._profilePhotoUploadUnbind = null;
    }

    const wrap = this.currentStepElement.querySelector('[data-bz-profile-photo]');
    const input = this.currentStepElement.querySelector('[data-bz-profile-photo-input]');
    if (!wrap || !input) return;

    const backendUrl = window.__wizard2BackendApiUrl || this.flow.shopifyAPI?.backendApiUrl || '';

    this.renderProfilePhotoPreview();

    this._profilePhotoUploadUnbind = window.bzPetPhotoUpload.bind({
      wrap,
      input,
      backendUrl,
      context: 'wizard',
      getPetMetaobjectId: () => this.flow.state.petMetaobjectId || '',
      getPetName: () => this.flow.state.getPetData().name || '',
      getCurrentPhotoUrl: () => this.flow.state.getPetData().profilePhotoUrl || '',
      onSuccess: (result) => {
        if (!result?.photoUrl) return;
        this.flow.state.updatePetData('profilePhotoUrl', result.photoUrl);
        this.renderProfilePhotoPreview();
      },
    });
  }

  validateProfileFields() {
    const petData = this.flow.state.getPetData();
    
    // All fields are required
    if (!petData.name || petData.name.trim() === '') return false;
    if (!petData.gender) return false;
    if (!petData.ageGroup) return false;
    if (!petData.breed || petData.breed.trim() === '') return false;
    if (!petData.weight || petData.weight < 0.5 || petData.weight > 200) return false;
    if (petData.spayed === null || petData.spayed === undefined) return false;
    if (!petData.composition) return false;
    if (!petData.activity) return false;
    if (!petData.snacks) return false;
    // Allergies can be empty (array can be empty)
    
    return true;
  }

  /**
   * Set up profile save button in footer
   */
  setupProfileSaveButton() {
    // This will be called from renderStep or setupStepListeners to update footer
    // The actual button setup is handled in the footer button setup
  }

  /**
   * Step to open after a successful profile save (wizard-1: flavors).
   * Wizard-2 overrides to return plan_overview.
   */
  getProfileSaveRedirectStep() {
    return 'flavors';
  }

  /**
   * Where to send the customer after a successful profile save.
   * Wizard-2 overrides to use browser history (portal / plan overview).
   */
  redirectAfterProfileSave() {
    const redirectStep =
      typeof this.getProfileSaveRedirectStep === 'function' ? this.getProfileSaveRedirectStep() : 'flavors';
    if (redirectStep === 'plan_overview' && this.flow.state.petMetaobjectId) {
      this.flow.router.navigate('plan_overview', {}, false, { pet: this.flow.state.petMetaobjectId });
    } else {
      this.flow.router.navigate(redirectStep);
    }
  }

  /**
   * Where to send the customer after deleting a profile from the profile step.
   */
  redirectAfterProfileDelete() {
    this.flow.router.navigate('name');
  }

  /**
   * Save profile and redirect (flavors for wizard-1; wizard-2 overrides redirect).
   */
  async saveProfile() {
    // Validate fields
    if (!this.validateProfileFields()) {
      alert('Please fill in all required fields.');
      return;
    }

    const saveButton = document.querySelector('.profile-save-button');
    let originalText = '';
    if (saveButton) {
      saveButton.disabled = true;
      originalText = saveButton.textContent;
      saveButton.textContent = 'Bezig...';
    }

    try {
      // Recalculate with current data
      this.flow.runCalculations();

      // Get current pet data and calculations
      const petData = this.flow.state.getPetData();
      const calculations = this.flow.state.getCalculations();
      const state = this.flow.state;
      const ownerEmail =
        (petData.ownerEmail && String(petData.ownerEmail).trim()) ||
        (state.sessionOwnerEmail && String(state.sessionOwnerEmail).trim()) ||
        '';
      const ownerName =
        (petData.ownerName && String(petData.ownerName).trim()) ||
        (state.sessionOwnerName && String(state.sessionOwnerName).trim()) ||
        '';
      const emailConsent =
        petData.emailConsent !== undefined && petData.emailConsent !== null
          ? !!petData.emailConsent
          : state.sessionEmailConsent !== undefined && state.sessionEmailConsent !== null
            ? !!state.sessionEmailConsent
            : false;
      const customerData = {
        ownerEmail,
        ownerName,
        emailConsent
      };

      // Save to Shopify via backend API
      if (this.flow.state.petMetaobjectId) {
        const customerIdForBackend =
          this.flow.state.sessionCustomerId ||
          this.flow.state.customerId ||
          null;
        await this.flow.shopifyAPI.updatePetMetaobject(
          this.flow.state.petMetaobjectId,
          petData,
          calculations,
          customerIdForBackend
        );
      } else {
        // Shouldn't happen on profile page, but handle it anyway
        console.warn('No petMetaobjectId on profile page');
      }

      // Save to localStorage
      this.flow.storage.saveToLocalStorage(this.flow.state.getState());

      // Send "Pet updated" event to Klaviyo (if initial event was already sent)
      if (this.flow.klaviyoManager && ownerEmail) {
        // Check if there's no pending event (meaning initial event was already sent)
        if (!this.flow.klaviyoManager.hasPendingEvent(ownerEmail)) {
          try {
            await this.flow.klaviyoManager.sendPetUpdatedEvent(
              petData,
              customerData,
              this.flow.state.petMetaobjectId
            );
          } catch (e) {
            console.warn('Failed to send Klaviyo "Pet updated" event (non-blocking):', e);
          }
        }
      }

      this.redirectAfterProfileSave();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Er is een fout opgetreden bij het opslaan van het profiel. Probeer het opnieuw.');
      
      if (saveButton) {
        saveButton.disabled = false;
        saveButton.textContent = originalText;
      }
    }
  }

  /**
   * Set up profile deletion
   */
  setupProfileDeletion() {
    const deleteButton = this.currentStepElement.querySelector('[data-delete-profile]');
    const modal = this.currentStepElement.querySelector('[data-delete-modal]');
    const confirmButton = this.currentStepElement.querySelector('[data-confirm-delete]');
    const cancelButton = this.currentStepElement.querySelector('[data-cancel-delete]');

    if (deleteButton && modal) {
      deleteButton.addEventListener('click', () => {
        modal.style.display = 'flex';
      });
    }

    if (confirmButton) {
      confirmButton.addEventListener('click', async () => {
        await this.deleteProfile();
      });
    }

    if (cancelButton && modal) {
      cancelButton.addEventListener('click', () => {
        modal.style.display = 'none';
      });
    }
  }

  /**
   * Delete profile
   */
  async deleteProfile() {
    try {
      // Delete from Shopify Pet Metaobject if exists
      if (this.flow.state.petMetaobjectId) {
        try {
          await this.flow.shopifyAPI.deletePetMetaobject(this.flow.state.petMetaobjectId);
        } catch (error) {
          console.error('Error deleting Pet Metaobject:', error);
          // Continue anyway - will clear locally
        }
      }

      // Clear state
      this.flow.state.reset();

      // Clear localStorage
      this.flow.storage.clearLocalStorage();

      // Hide modal
      const modal = this.currentStepElement.querySelector('[data-delete-modal]');
      if (modal) {
        modal.style.display = 'none';
      }

      // Redirect to name step
      this.redirectAfterProfileDelete();
    } catch (error) {
      console.error('Error deleting profile:', error);
      alert('Er is een fout opgetreden bij het verwijderen van het profiel.');
    }
  }

  /**
   * Setup name step specific logic (gender selection, name field enable/disable)
   */
  setupNameStepLogic() {
    if (window.WizardHousehold && this.currentStepElement.querySelector('[data-wizard-name]')) {
      window.WizardHousehold.mountName(this);
      return;
    }

    const genderInputs = this.currentStepElement.querySelectorAll('[name="gender"]');
    const nameInput = this.currentStepElement.querySelector('[name="dogName"]');
    
    if (!nameInput) return;
    
    // Check if gender is already selected (from state restoration)
    const currentGender = this.flow.state.petData?.gender;
    if (currentGender) {
      // Enable name field and update placeholder if gender is already selected
      nameInput.disabled = false;
      const activePlaceholder = nameInput.getAttribute('data-placeholder-active');
      if (activePlaceholder) {
        nameInput.placeholder = activePlaceholder;
      }
    }
    
    // Listen for gender selection changes
    genderInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Enable name field when gender is selected
          nameInput.disabled = false;
          const activePlaceholder = nameInput.getAttribute('data-placeholder-active');
          if (activePlaceholder) {
            nameInput.placeholder = activePlaceholder;
          }
          nameInput.focus();
        }
      });
    });
    
    // Listen for name input changes to check for duplicates
    nameInput.addEventListener('input', () => {
      this.updateInlineMessages('name');
    });

    nameInput.addEventListener('blur', () => {
      const canonical = this.flow.state.petData?.name ?? '';
      if (nameInput.value !== canonical) {
        nameInput.value = canonical;
      }
    });
  }

  /**
   * Update inline messages
   * @param {string} step - Step identifier
   */
  async updateInlineMessages(step) {
    if (step === 'name') {
      // Check for duplicate name
      const nameInput = this.currentStepElement.querySelector('[name="dogName"]');
      if (nameInput && nameInput.value) {
        const petName = nameInput.value.trim();
        
        // Check if name exists in localStorage or Shopify
        const existingPet = await this.flow.storage.petNameExists(petName, { flow: this.flow });
        const displayName = existingPet?.name?.trim() || petName;
        
        const message = this.currentStepElement.querySelector('[data-conditional="duplicate-name"]');
        if (message) {
          if (existingPet) {
            // Pet exists - show message with appropriate link
            message.style.display = 'flex';
            
            // Get message content element
            const messageContent = message.querySelector('[data-duplicate-message-content]');
            if (messageContent) {
              // Get translations - we'll need to replace placeholders
              const lang = document.documentElement.lang || 'en';
              const isDutch = lang.startsWith('nl');
              
              let messageText;
              const linkText = isDutch ? 'Klik hier' : 'Click here';
              
              if (existingPet.source === 'shopify') {
                // Link to profile page - use SPA router navigation
                messageContent.innerHTML = isDutch 
                  ? `Je hebt al een hond genaamd <strong>${displayName}</strong> toegevoegd. <a href="#" class="inline-link" data-navigate="profile" data-metaobject-id="${existingPet.metaobjectId}">${linkText}</a> om door te gaan met haar profiel.`
                  : `You have already added a dog named <strong>${displayName}</strong>. <a href="#" class="inline-link" data-navigate="profile" data-metaobject-id="${existingPet.metaobjectId}">${linkText}</a> to continue with her profile.`;
                
                // Set up click handler for profile navigation
                const link = messageContent.querySelector('.inline-link');
                if (link) {
                  link.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.flow.router.navigate('profile', { pet: existingPet.metaobjectId });
                  });
                }
              } else {
                // Link to furthest step in quiz - use SPA router navigation
                const furthestStep = existingPet.step || 'name';
                messageContent.innerHTML = isDutch
                  ? `Je hebt al een hond genaamd <strong>${displayName}</strong> toegevoegd. <a href="#" class="inline-link" data-navigate="quiz" data-step="${furthestStep}">${linkText}</a> om door te gaan met haar profiel.`
                  : `You have already added a dog named <strong>${displayName}</strong>. <a href="#" class="inline-link" data-navigate="quiz" data-step="${furthestStep}">${linkText}</a> to continue with her profile.`;
                
                // Set up click handler for quiz navigation
                const link = messageContent.querySelector('.inline-link');
                if (link) {
                  link.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.flow.router.navigate(furthestStep, {});
                  });
                }
              }
            }
          } else {
            // Pet doesn't exist - hide message
            message.style.display = 'none';
          }
        }
      } else {
        // No name entered - hide duplicate message
        const message = this.currentStepElement.querySelector('[data-conditional="duplicate-name"]');
        if (message) {
          message.style.display = 'none';
        }
      }
    }
  }
}

// Export for global access
window.StepHandler = StepHandler;

